import { VDataTable } from 'vuetify/components'
import { ref, onMounted, computed } from 'vue'
import Draggable from 'vuedraggable'
import SidebarControls from '@/components/taxonomy-editor/SidebarControls.vue'
import TaxonomyTree from '@/components/taxonomy-editor/TaxonomyTree.vue'
import AttributeEditorTable from '@/components/taxonomy-editor/AttributeEditorTable.vue'
import GPCMapper from '@/components/taxonomy-editor/GPCMapper.vue'
import RuleBuilderDialog from '@/components/taxonomy-editor/RuleBuilderDialog.vue'
import SizeMapper from '@/components/taxonomy-editor/SizeMapper.vue';
import { generateFrontendTree, getBackendCategoryPaths } from './frontendDerivationEngine.js';
import RuleInspectorDialog from '@/components/taxonomy-editor/RuleInspectorDialog.vue';
import StyleRulesManager from '@/components/taxonomy-editor/StyleRulesManager.vue';
import BrandsEditor from '@/components/taxonomy-editor/BrandsEditor.vue';
import { getSizeMapperData, publishSizeMapperData, saveSizeMapperData } from '@/components/taxonomy-editor/SizeMapper.api'
import { getStyleGuide, saveStyleGuideRules, publishStyleGuide } from '@/components/taxonomy-editor/StyleRulesManager.api'

// Needs to be a function otherwise all new attributes will be the same
// object reference and cannot change state independantly
const DEFAULT_ATTRIBUTE_METADATA = () => ({ is_required: false });

export default {
  name: 'TaxonomyEditor',
  components: {
    VDataTable,
    Draggable,
    SidebarControls,
    TaxonomyTree,
    AttributeEditorTable,
    GPCMapper,
    RuleBuilderDialog,
    RuleInspectorDialog,
    StyleRulesManager,
    SizeMapper,
    BrandsEditor
  },
  data() {
    return {
      displayedFrontendTree: null,
      treeRenderKey: 0,
      backendTaxonomyData: {},
      gpcLastPublishInfo: null,
      globalExclusionsDialog: {
        show: false,
        newExclusionPath: '',

      },
      _isInternalLoad: false,
      _saveInProgress: false,  // Prevents rapid double-clicks from triggering multiple saves
      
      // ============================================================================
      // V2 TAXONOMY SUPPORT - New data properties
      // ============================================================================
      taxonomyFamilies: [],
      sharedAttributesInfo: null,
      orphanReport: null,
      systemHealth: null,
      v2EndpointsEnabled: false,  // Will be set by healthCheck
      
      // Granular change tracking
      unsavedChanges: {
        taxonomy: false,
        attributes: false,
        gpcMappings: false,
        frontend: false
      },
      
      // ============================================================================
      // MULTI-USER EDITING SUPPORT
      // ============================================================================
      // Session tracking
      sessionId: null,  // Unique ID for this browser session
      
      // Loaded timestamps for conflict detection (set when data is loaded)
      loadedTimestamps: {
        taxonomy: null,      // When backend taxonomy was loaded
        attributes: null,    // When attributes were loaded
        frontend: null,      // When frontend rules were loaded
        gpcMappings: null    // When GPC mappings were loaded
      },
      
      // Active sessions (other users editing same taxonomy)
      activeSessions: [],
      
      // Conflict dialog state
      conflictDialog: {
        show: false,
        dataType: '',        // 'taxonomy', 'attributes', 'frontend', 'gpcMappings'
        serverUpdatedAt: null,
        loadedAt: null
      },
      
      // V2 UI state
      sharedAttributesWarningDismissed: false,
      showCreateV2Dialog: false,
      showOrphanReportDialog: false,
      newV2Filename: '',
      createV2Options: {
        copyFrontend: true,
        copyGpcMappings: false
      },
      creatingV2: false,
      showSharedAttributesWarning: false,
      // ============================================================================
      ruleInspectorDialog: {
        show: false,
        rule: null,
      },
      ruleBuilderDialog: {
        show: false,
        nodeId: null, // The parent node for the new derived category
      },
      attributeSearchDialog: {
        show: false,
        search: '',
        filteredAttributes: [],
        nodeId: null,
      },
  
      loadVersion: null,
      showBackupVersions: false,
      hasUnsavedChanges: false,
      trackChanges: false,
      saveVersion: '',
      _versionDisplayMap: {}, // Maps formatted versions back to actual version names
      versionTypes: {
        PRODUCTION: 'production',
        TEST: 'test',
        WORKING: 'Working Version',
        QA: 'QA Version'
      },
      versionDescription: '',
      versionTags: [],
      isVersionSelectorExpanded: false,
      showWorkingVersions: true,
      showEnvironmentVersions: true,
      currentVersionDescription: '',
      taxonomies: [], // List of available taxonomy filenames (e.g., 'main.json', 'frontend.json')
      currentView: 'backendTaxonomyEditor',
      selectedTaxonomy: '', // The filename of the currently selected taxonomy (e.g., 'main.json' or 'frontend.json')
      selectedTopLevelNode: '',
      taxonomyData: {}, // The active taxonomy data (either backend or frontend categories)
      attributeData: {}, // The active attribute data (should primarily be backend attrs)
      categoryNotesDialog: {
        show: false,
        nodeId: null,
        nodeName: '',
        notes: '',
        tempNotes: ''
      },
  
      selectedBackendTaxonomy: '', // Stores the filename of the last loaded backend taxonomy
      loadedBackendVersion: '',    // Stores the version of the last loaded backend taxonomy (used by loadedVersion watcher)

      sizeMapperData: {},

      _preventViewChangeDialog: false,
      selectorsCollapsed: true,
      webCategoryCodesForSelectedNode: [],
      placeholderWebCategoryCodes: [],
      // --------------------------------------------------------
      frontendRules: {
        backend_source_taxonomy: 'simplified_taxonomy_deduplicated.json', // Default backend source
        backend_source_version: 'Working Version', // Default backend version
        categories: [ // Array of top-level frontend category definitions
          // Example of a conditional node rule
          // {
          //   id: 'fe_men',
          //   name: 'Men',
          //   type: 'conditional', // 'conditional' or 'manual' or 'linked_root'
          //   conditions: {
          //     operator: 'AND',
          //     groups: [
          //       { operator: 'OR', conditions: [{ attribute_id: '60001002', operator: 'in', value: ['Male', 'Unisex'] }] },
          //       { operator: 'OR', conditions: [{ attribute_id: '60001003', operator: 'equals', value: 'Adults' }] }
          //     ]
          //   },
          //   children: [ // These are rules for children *under* this derived node
          //     { type: 'linked_category', backend_path: 'Fashion|Clothing', exclusions: ['Dresses'] },
          //     { type: 'manual_node', name: 'Men\'s Shoes', attribute_ids: [], subcategories: [] } // manual nodes will be like existing nodes
          //   ]
          // },
          // Example of a linked root node
          // {
          //   id: 'fe_apparel',
          //   name: 'Apparel',
          //   type: 'linked_root',
          //   backend_path: 'Fashion|Apparel',
          //   exclusions: ['Kids Apparel', 'Pet Apparel']
          // }
          // Example of a manually created top-level node
          // {
          //   id: 'fe_holiday',
          //   name: 'Holiday Specials',
          //   type: 'manual_node',
          //   subcategories: [],
          //   attribute_ids: []
          // }
        ],
        global_exclusions: [] // Paths to exclude globally from any derivation: "Fashion|Clothing|Dresses"
      },
      editingFrontendRule: null,

      // The dynamically generated frontend tree, presented to vue3-tree-org in frontendPreview
      generatedFrontendTree: {},
      topLevelNodes: [],
      expandedNodes: new Set(), // Tracks expanded node IDs
      treeItems: [], // Your tree data
      nodeIdMap: {}, // Assuming this maps node IDs to their data
      keysToSkip: ['name', 'url', 'total_items', 'attribute_ids'],
      nameDialog: false, // Controls dialog visibility
      newAttributeName: '', // Stores the user-entered name
      publishDialog: false,
      publishType: 'current', // Track what to publish
      unsavedChangesDialog: false,
      pendingPublishAction: null,
      publishTarget: null,     // Track where to publish
  
      // Global search used in some other views
      searchQuery: '',
  
      // Language Variant Editor search:
      langVariantSearch: '',
  
      selectedNode: null,
      selectedNodeId: null,
      expandedFilters: [],
      expandedCategories: {},
      leftPaneWidth: 900, // Moderate increase for better tree view without being too large
      isDragging: false,
      startX: 0,
      startWidth: 0,
  
      horizontal: true,
      collapsable: true,
      isDragging: false,
      autoScrollInterval: null,
      scrollSpeed: 12, // Pixels per scroll update
      scrollThreshold: 60, // Distance from edge to trigger scrolling
      mouseX: null,
      mouseY: null,
      lastScrollTime: 0,
      scrollAnimationFrame: null,
      scrollSpeed: 20, // Increased for more noticeable scrolling
      scrollThreshold: 100, // Larger threshold for easier edge detection
      draggedNodeId: null, // Track which node is being dragged
      highlightedNodeId: null,
  
      gpcMappings: [],
      gpcBricksData: [],
      backendCategoriesData: [],
      gpcDataLoading: false,
      gpcBricksLoaded: false,
      gpcError: null,
  
      nodeIdMap: {},
      nodeIdsByType: {},
      editableFilters: {},
      editableFilterNames: {},
      newFilterName: '',
      newFilterValues: {}, // Keys will be attrId
      editingFilterName: null,
      editingValue: { filter: null, index: null },
      valueContextMenu: {
        show: false,
        x: 0,
        y: 0,
        attrId: null,
        value: null,
        valueIndex: null
      },
      nodeClipboard: null,
  
      snackbar: { show: false, message: '', color: 'success' },
  
      FUNCTION_URL: 'https://data-reviewer-mongo-998838505944.europe-west6.run.app',
      cachedTaxonomies: {}, // Used for general caching, not specific frontend/backend data copies
  
      // Holds the ids of expanded nodes so we can restore them
      expandedNodes: new Set(),
  
      editNodeDialog: false,
      editNodeId: null,
      editNodeName: '',
  
      allFiltersMap: {},
      allFilterNames: [],
      addExistingFilterName: '',
  
      sourceColors: {
        base_source: '#F59E0B',
        about_you: '#3B82F6',
        zalando: '#EF4444',
        galaxus_fashion: '#10B981',
        ochsner: "#00CED1",
        other_source: '#9D4EDD'
      },
  
      attributeSort: 'display_name',
      newAttributeValue: {},
  
      // Data table headers for the main attribute editor
      attributeTableHeaders: [
        { title: 'ID', key: 'attribute_id' },
        { title: 'Name', key: 'name' },
        { title: 'Display Name', key: 'display_name' },
        { title: 'Description', key: 'description', width: '300px' }, 
        { title: 'Values', key: 'values' },
        { title: 'Attribute Group', key: 'attribute_group' },
        { title: 'Data Type', key: 'data_type' },
        { title: 'Booleans (1)', key: 'booleans_group_1' },
        { title: 'Booleans (2)', key: 'booleans_group_2' },
        { title: 'Booleans (3)', key: 'booleans_group_3' },
        { title: 'Sort Order', key: 'sort_order' },
        { title: 'Attribute Level', key: 'attribute_level' },
        { title: 'Attribute Unit', key: 'attribute_unit' },
        { title: 'Notes', key: 'notes', width: '300px' },
        { title: 'Attribute Sources', key: 'filter_sources' },
        { title: 'Categories', key: 'categories' },
        { title: 'Actions', key: 'actions', width: 60 },
      ],
  
      // Data table headers for the Language Variant Editor
      langVariantHeaders: [
        { text: 'ID', value: 'attribute_id' },
        { text: 'English Name', value: 'display_name' },
        { text: '# Values', value: 'value_count' },
        { text: 'Actions', value: 'actions', width: 100 }
      ],
  
      textEditDialog: {
        show: false,
        attributeObj: null,
        fieldName: '',
        tempValue: ''
      },
  
      // notes dialog for individual values
      valueNotesDialog: {
        show: false,
        attrId: null,
        value: null,
        tempNote: ''
      },
  
      // Single attribute translations dialog
      translationsDialog: {
        show: false,
        item: null
      },
  
      // List of daily versions
      versions: [],
      // Currently selected version in the dropdown
      selectedVersion: '',
  
      isAttributeEditorFullscreen: false,
      expandedAttributeValues: {},
      resetDialog: false,
      backendNodeClipboardPath: null,

      // Style Rules Manager data
      styleRules: [],
      styleRulesId: null,
      styleRulesLoading: false,
      styleRulesLastPublishInfo: null,
      styleRulesEnvironment: 'test', // Default environment
      gpcList: [],
      segmentBricks: [],
      brandsList: [],
      brandsLoading: false,
      brandsSearchQuery: '',
      brandsSearchTimeout: null,
      BRANDS_API_URL: 'https://brands-mini-api-844472556697.europe-west6.run.app',
      debugMode: false, // Controls visibility of debug buttons
      categoryDetailsDialog: {
        show: false,
        categoryName: '',
        brickId: null
      }
    }
  },
  
  computed: {
    allWebCategoryCodes() {
      // 1. Get codes already in use within the current frontendRules
      const inUseCodes = new Set();
      if (this.frontendRules && this.frontendRules.categories) {
        const collectCodes = (rules) => {
          if (!rules) return;
          for (const rule of rules) {
            // Handle new multiple codes format
            if (rule.web_category_codes && Array.isArray(rule.web_category_codes)) {
              rule.web_category_codes.forEach(code => {
                if (code && typeof code === 'string') {
                  inUseCodes.add(code);
                }
              });
            }
            // Handle legacy single code format
            if (rule.web_category_code && typeof rule.web_category_code === 'string') {
              inUseCodes.add(rule.web_category_code);
            }
            if (rule.children) {
              collectCodes(rule.children);
            }
          }
        };
        collectCodes(this.frontendRules.categories);
      }
    
      // 2. Combine with the static placeholder list using a Set to handle duplicates automatically
      const combinedCodes = new Set([
        ...inUseCodes,
        ...this.placeholderWebCategoryCodes
      ]);
    
      // 3. Return a sorted array for the UI
      return Array.from(combinedCodes).sort();},

    generatedFrontendTree() {
      // CHECKPOINT 2: Is this computed property even re-running after the action?
      console.log('%c[2. COMPUTED] `generatedFrontendTree` is re-evaluating...', 'color: #1976D2; font-weight: bold;');
    
      // This is the guard clause from your code.
      if (!this.frontendRules || Object.keys(this.backendTaxonomyData).length === 0 || Object.keys(this.attributeData).length === 0) {
        console.warn("[COMPUTED] Halting: source data (rules, backend taxonomy, or attributes) is missing.");
        return {};
      }
      
      // Call the external derivation engine.
      const result = generateFrontendTree(this.frontendRules, this.backendTaxonomyData, this.attributeData);
      
      // Let's see what the engine produced.
      console.log('[COMPUTED] The result from the derivation engine is:', JSON.parse(JSON.stringify(result)));
      return result;
    },
    allBackendCategoryPaths() {
      // This will generate a flattened list of all paths from your `backendTaxonomyData`
      return getBackendCategoryPaths(this.backendTaxonomyData);
    },
    activeEditorType() {
      if (this.currentView === 'frontendTaxonomyEditor') {
        return 'frontend';
      }
      // All other views are considered part of the 'backend' context.
      return 'backend';
    },
    customMenus() {
      // --- Actions for the BACKEND editor ---
      if (this.currentView === 'backendTaxonomyEditor') {
        return [
          { name: 'Copy Text', command: 'copy' },
          { isDivider: true },
          { name: 'Add Node', command: 'add' },
          { name: 'Edit Node', command: 'edit' },
          { name: 'Delete Node', command: 'delete' },
          { isDivider: true },
          // ADD THIS NEW MENU ITEM FOR BACKEND ONLY
          { 
            name: 'View/Edit Notes', 
            command: 'edit_notes',
            icon: 'mdi-note-text'
          },
          { isDivider: true },
          { name: 'Copy Backend Path', command: 'copy_backend_path' }
        ];
      }
    
      // --- Actions for the FRONTEND editor (NO NOTES HERE) ---
      if (this.currentView === 'frontendTaxonomyEditor') {
        return [
          { name: 'View / Edit Rule', command: 'edit_rule' },
          { name: 'Delete', command: 'delete_rule', class: 'text-danger' },
          { isDivider: true },
          { name: 'Add Child Rule', command: 'add_child_rule' },
          { name: 'Add Nodes From Attribute', command: 'add_from_attribute' }
        ];
      }
    
      return [];
    },

    availableViews() {
      return [
        { key: 'backendTaxonomyEditor', label: 'Backend Taxonomy Editor' },
        { key: 'frontendRuleBuilder', label: 'Frontend Rule Builder' }, // New View
        { key: 'frontendPreview', label: 'Frontend Preview' }, // New View
        { key: 'attributeOverview', label: 'Attribute Overview' },
        { key: 'attributeEditor', label: 'Attribute Editor' },
        { key: 'languageVariants', label: 'Language Variants' },
        { key: 'gpcMapping', label: 'GPC Mapping' },
        { key: 'styleRules', label: 'Style Guide Rules' }
      ]
    },

    formattedTaxonomies() {
      // turn the raw filenames you fetched in this.taxonomies
      // into simple objects the tab row expects
      return (this.taxonomies || []).map(f => ({
        filename: f,                       // original file name
        // prettier label for the tab – drop “.json”
        displayName: f.replace(/\.json$/, '')
      }));
    },
    
    /**
     * V2 SUPPORT: Formatted taxonomies with V1/V2 grouping for enhanced selector
     * Only shows backend taxonomies - frontend is automatically linked
     */
    formattedTaxonomiesGrouped() {
      const items = [];
      
      // If we have family info from the API, use it for grouping
      if (this.taxonomyFamilies && this.taxonomyFamilies.length > 0) {
        this.taxonomyFamilies.forEach(family => {
          // Only include backend taxonomies (not frontend, not other non-taxonomy families)
          // Skip entries that are frontend rules or don't have simplified_taxonomy pattern
          if (family.baseName === 'frontend') return;
          if (family.baseName.startsWith('frontend')) return;
          
          if (family.versions?.v1) {
            items.push({
              filename: family.versions.v1.filename + '.json',
              displayName: `${family.displayName}${family.versions?.v2 ? ' (V1)' : ''}`,
              isV2: false,
              hasProduction: family.versions.v1.hasProduction || false,
              linkedFrontend: 'frontend',
              linkedGpc: family.versions.v1.filename
            });
          }
          if (family.versions?.v2) {
            items.push({
              filename: family.versions.v2.filename + '.json',
              displayName: `${family.displayName} (V2)`,
              isV2: true,
              hasProduction: family.versions.v2.hasProduction || false,
              linkedFrontend: 'frontend_v2',
              linkedGpc: family.versions.v2.filename
            });
          }
        });
      } else {
        // Fall back to simple list with V2 detection - exclude frontend files
        items.push(...(this.formattedTaxonomies || [])
          .filter(t => !t.filename.startsWith('frontend'))
          .map(t => ({
            ...t,
            isV2: t.filename.includes('_v2'),
            hasProduction: false
          })));
      }
      
      return items;
    },
    
    formattedVersions() {
      // Start with our 4 main versions if they exist
      const specialVersions = [];
    
      // Add Production and Test (official published versions)
      const prod = this.versions.find(v => v.toLowerCase() === 'production');
      const test = this.versions.find(v => v.toLowerCase() === 'test');
      if (prod) specialVersions.push('Production');
      if (test) specialVersions.push('Test');
    
      // Add Working Version and QA Version (main working versions)
      const working = this.versions.find(v => v === 'Working Version');
      const qa = this.versions.find(v => v === 'QA Version');
      if (working) specialVersions.push('Working Version');
      if (qa) specialVersions.push('QA Version');
    
      // Filter custom versions (everything that's not our main 4 versions)
      let customVersions = this.versions.filter(v =>
        !['production', 'test', 'Working Version', 'QA Version'].includes(v)
      );
    
      // Clear and rebuild version display map
      this._versionDisplayMap = {};
    
      // Process custom versions (including backups)
      const formattedCustomVersions = [];
    
      customVersions.forEach(version => {
        // Check for backup versions with timestamps
        const workingBackupMatch = version.match(/^Working Version Backup (.+)$/);
        const testBackupMatch = version.match(/^test[_ ]backup[_ ](.+)$/i);
        const prodBackupMatch = version.match(/^production[_ ]backup[_ ](.+)$/i);
    
        if (workingBackupMatch || testBackupMatch || prodBackupMatch) {
          // This is a backup version - format it nicely
          let backupType, timestampStr;
    
          if (workingBackupMatch) {
            backupType = "Working Version";
            timestampStr = workingBackupMatch[1];
          } else if (testBackupMatch) {
            backupType = "Test";
            timestampStr = testBackupMatch[1];
          } else {
            backupType = "Production";
            timestampStr = prodBackupMatch[1];
          }
    
          // Parse and format the timestamp
          const formattedTime = this.formatBackupTimestamp(timestampStr);
          const displayVersion = `${backupType} Backup (${formattedTime})`;
    
          // Store the mapping
          this._versionDisplayMap[displayVersion] = version;
          formattedCustomVersions.push(displayVersion);
        } else {
          // Regular custom version - use as-is
          this._versionDisplayMap[version] = version;
          formattedCustomVersions.push(version);
        }
      });
    
      // Apply the backup versions filter
      let displayCustomVersions = formattedCustomVersions;
      if (!this.showBackupVersions) {
        displayCustomVersions = displayCustomVersions.filter(v =>
          !(v.includes('Working Version Backup (') ||
            v.includes('Test Backup (') ||
            v.includes('Production Backup ('))
        );
      }
    
      // Combine all versions with special ones first
      const allVersions = [...specialVersions, ...displayCustomVersions];
    
      return allVersions;
    },
    currentVersionLabel() {
      if (!this.loadedVersion) return 'Unsaved';
      if (this.loadedVersion.toLowerCase() === 'production') return 'Production';
      if (this.loadedVersion.toLowerCase() === 'test') return 'Test';
      if (this.loadedVersion === 'Working Version') return 'Working Version';
      if (this.loadedVersion === 'QA Version') return 'QA Version';
      return this.loadedVersion;
    },
    orgData() {
      // For the frontend view, we now simply return the data we manually built.
      if (this.currentView === 'frontendTaxonomyEditor') {
          return this.displayedFrontendTree || { id: 'no-data-root', label: 'Loading...', children: [] };
      }
      
      // Backend editor logic remains the same.
      if (this.currentView === 'backendTaxonomyEditor' && this.selectedTopLevelNode && this.taxonomyData[this.selectedTopLevelNode]) {
          return this.applyExpansionState(this.convertToTreeItems(this.taxonomyData[this.selectedTopLevelNode], this.selectedTopLevelNode)[0]);
      }
      
      // Default fallback
      return { id: 'no-data-root', label: 'No Data', children: [] };
    },
  
    showFilters() {
      return (
        this.selectedNode &&
        this.selectedNode.filters &&
        Object.keys(this.selectedNode.filters).length > 0
      );
    },
    sortedAttributes() {
      const arr = Object.entries(this.attributeData).map(([key, rawObj]) => {
        if (!rawObj.attribute_id) {
          // FIX: Assign the key (which is already a string) directly.
          rawObj.attribute_id = key;
        } else {
          // Ensure any existing id is also a string.
          rawObj.attribute_id = String(rawObj.attribute_id);
        }
        rawObj.valuesArray = this.getValuesArray(rawObj);
        return rawObj;
      });
      const created = arr.filter(a => a.justCreated);
      const rest = arr.filter(a => !a.justCreated);
      if (this.attributeSort === 'display_name') {
        rest.sort((a, b) => (this.getDisplayName(a) || '').localeCompare(this.getDisplayName(b) || ''));
      } else if (this.attributeSort === 'attribute_id') {
        // FIX: Parse the strings to numbers locally just for the sort comparison.
        rest.sort((a, b) => parseInt(a.attribute_id, 10) - parseInt(b.attribute_id, 10));
      }
      return [...created, ...rest];
    },
    filteredAttributes() {
      const q = (this.searchQuery || '').trim().toLowerCase();
      let arr = this.sortedAttributes;
      if (q) {
        arr = arr.filter((item) => {
          const nameMatch = (item.name || '').toLowerCase().includes(q);
          // Add display name matching using the helper function
          const displayNameMatch = this.getDisplayName(item).toLowerCase().includes(q);
          const idMatch = String(item.attribute_id).includes(q);
          return nameMatch || displayNameMatch || idMatch;
        });
      }
      return arr;
    },
    attributeOverviewStats() {
      const stats = {
        totalAttributes: 0,
        nameCounts: {},
        categoryCounts: {},
        avgAttrsPerCategory: 0,
        avgCatsPerAttribute: 0,
        avgValuesPerFilter: 0
      };
      const allAttrIds = Object.keys(this.attributeData);
      stats.totalAttributes = allAttrIds.length;
      let totalCategoryRefs = 0;
      let totalValues = 0;
      for (const attrId of allAttrIds) {
        const att = this.attributeData[attrId];
        const attName = att.name || '(unnamed)';
        if (!stats.nameCounts[attName]) {
          stats.nameCounts[attName] = 0;
        }
        stats.nameCounts[attName]++;
        if (Array.isArray(att.categories)) {
          for (const cat of att.categories) {
            if (!stats.categoryCounts[cat]) {
              stats.categoryCounts[cat] = 0;
            }
            stats.categoryCounts[cat]++;
            totalCategoryRefs++;
          }
        }
        if (Array.isArray(att.values)) {
          totalValues += att.values.length;
        }
      }
      const distinctCategoryKeys = Object.keys(stats.categoryCounts);
      const numCategories = distinctCategoryKeys.length;
      if (numCategories > 0) {
        stats.avgAttrsPerCategory = (totalCategoryRefs / numCategories).toFixed(2);
      }
      if (stats.totalAttributes > 0) {
        stats.avgCatsPerAttribute = (totalCategoryRefs / stats.totalAttributes).toFixed(2);
        stats.avgValuesPerFilter = (totalValues / stats.totalAttributes).toFixed(2);
      }
      return stats;
    },
    sortedNameEntries() {
      const entries = Object.entries(this.attributeOverviewStats.nameCounts);
      entries.sort((a, b) => a[0].localeCompare(b[0]));
      return entries;
    },
    languageVariantAttributes() {
      return this.sortedAttributes.map((attr) => {
        const enLength = this.getValuesArray(attr).length || 0;
        if (!attr.translations.value_de) attr.translations.value_de = [];
        if (!attr.translations.value_fr) attr.translations.value_fr = [];
        if (!attr.translations.value_it) attr.translations.value_it = [];

        const syncArray = (arr) => {
          if (arr.length < enLength) {
            for (let i = arr.length; i < enLength; i++) {
              arr.push('');
            }
          } else if (arr.length > enLength) {
            arr.splice(enLength);
          }
        };

        syncArray(attr.translations.value_de);
        syncArray(attr.translations.value_fr);
        syncArray(attr.translations.value_it);

        return attr;
      });
    },
    filteredLanguageVariantAttributes() {
      const q = this.langVariantSearch.toLowerCase().trim();
      if (!q) return this.languageVariantAttributes;

      return this.languageVariantAttributes.filter(item => {
        const idStr = String(item.attribute_id);
        const displayName = (this.getDisplayName(item) || '').toLowerCase();
        return idStr.includes(q) || displayName.includes(q);
      });
    },
    // NEW: Compute the table height based on full screen state
    attributeEditorTableHeight() {
      return this.isAttributeEditorFullscreen
        ? 'calc(100vh - 230px)' // Additional space for footer in fullscreen
        : 'calc(100vh - 390px)'; // Additional space for footer in normal mode
    },

    // NEW: Enhanced topLevelNodes with brick IDs
    topLevelNodesWithBrickIds() {
      return this.topLevelNodes.map(nodeName => {
        const brickId = this.getBrickIdForCategory(nodeName);
        const nodeWithBrick = {
          name: nodeName,
          brickId: brickId
        };
        console.log(`📋 Node: ${nodeName} -> Brick ID: ${brickId || 'None'}`);
        return nodeWithBrick;
      });
    },

    // GPC system status indicator
    gpcSystemStatus() {
      if (this.gpcDataLoading) {
        return { text: 'Loading...', color: 'info' };
      }
      
      if (this.gpcBricksData.length === 0) {
        return { text: 'Click "Initialize GPC System"', color: 'error' };
      }
      
      // Count top-level categories that are mapped
      const topLevelMapped = this.topLevelNodesWithBrickIds.filter(node => 
        node.brickId && node.brickId !== 'None' && node.name !== '_metadata'
      ).length;
      
      const totalTopLevel = this.topLevelNodes.filter(name => name !== '_metadata').length;
      
      // Count total mapped categories (including subcategories) from various sources
      const totalMappedFromGpcMappings = this.gpcMappings.length;
      
      if (topLevelMapped === 0 && totalMappedFromGpcMappings === 0) {
        return { text: `${this.gpcBricksData.length} bricks loaded, 0 categories mapped`, color: 'warning' };
      }
      
      return {
        text: `${topLevelMapped}/${totalTopLevel} top-level (${totalMappedFromGpcMappings} total) mapped`,
        color: topLevelMapped === totalTopLevel ? 'success' : topLevelMapped > 0 ? 'info' : 'warning'
      };
    },
  },
  methods: {
    // ============================================================================
    // STYLE RULES MANAGER METHODS
    // ============================================================================
    
    getStyleRulesSystemStatus() {
      if (this.styleRulesLoading) {
        return { type: 'info', message: 'Loading style rules...' };
      }
      return { type: 'success', message: '' };
    },

    async loadStyleRules() {
      this.styleRulesLoading = true;
      try {
        const taxonomyFile = this.selectedTaxonomy || 'simplified_taxonomy_deduplicated';
        const resp = await getStyleGuide(this.loadVersion, taxonomyFile.replace(".json", ""));
        // Transform MongoDB data to component format
        this.styleRules = resp.rules.map((rule, i) => this.transformMongoToComponent(rule, i));
        this.styleRulesId = resp._id;

        console.log(this.styleRules, this.styleRulesId);
        
        console.log(`✅ Loaded ${this.styleRules.length} style rules for taxonomy: ${taxonomyFile}`);
        this.currentView === 'styleRules' && this.showSnackbar(`Loaded ${this.styleRules.length} style rules`, 'success');
      } catch (error) {
        console.error('❌ Error loading style rules:', error);
        this.currentView === 'styleRules' && this.showSnackbar(`Failed to load style rules: ${error.message}`, 'error');
      } finally {
        this.styleRulesLoading = false;
      }
    },

    async loadGPCBricksAndBrands() {
      try {
        console.log('🔍 loadGPCBricksAndBrands - gpcBricksData length:', this.gpcBricksData?.length);
        
        // If gpcBricksData hasn't been loaded yet, load it first
        if (!this.gpcBricksData || this.gpcBricksData.length === 0) {
          console.log('⏳ gpcBricksData not loaded yet, loading now...');
          await this.loadGpcBricksData();
          console.log(`✅ Loaded gpcBricksData: ${this.gpcBricksData.length} bricks`);
        }
        
        // Load GPC bricks from the existing gpcBricksData
        if (this.gpcBricksData && this.gpcBricksData.length > 0) {
          this.gpcList = this.gpcBricksData.map(brick => {
            // Remove "gpc-" prefix from brick.code to match transformed rule data
            const cleanCode = String(brick.code).replace(/^gpc-/, '');
            const brickTitle = brick.title || 'Unknown Brick';
            return {
              code: cleanCode,
              display: `${cleanCode} - ${brickTitle}`
            };
          });
          console.log(`✅ Loaded ${this.gpcList.length} GPC bricks for dropdown`);
          console.log('📋 First 3 GPC items:', this.gpcList.slice(0, 3));
        } else {
          console.warn('⚠️ No gpcBricksData available, gpcList will be empty');
          this.gpcList = [];
        }

        // Don't load brands initially - they'll be loaded when user starts searching
        this.brandsList = [];
        console.log('ℹ️ Brands will be loaded on demand when user searches');

        console.log('✅ Loaded GPC bricks and brands');
      } catch (error) {
        console.error('❌ Error loading GPC/brands:', error);
      }
    },

    async searchBrands(searchQuery) {
      const isNumeric = /^\d+$/.test(searchQuery);
      if (this.brandsSearchTimeout && !isNumeric) {
        // If it's a numeric search it's likely the system initiating the search and not the user
        // so we don't want to disturb any in flight searches. Only clear if it's a new text search
        clearTimeout(this.brandsSearchTimeout);
      }

      this.brandsSearchTimeout = setTimeout(async () => {
        if (!searchQuery || searchQuery.length < 2) {
          // If search is cleared or too short, clear the list
          this.brandsLoading = false;
          return;
        }

        this.brandsLoading = true;
        try {
          console.log('🔍 Searching brands for:', searchQuery);
          
          const url = new URL(this.BRANDS_API_URL);
          
          if (isNumeric) {
            // Search by brandId if numeric
            console.log('  🔢 Searching by brandId');
            url.searchParams.set('brandId', searchQuery);
          } else {
            // Search by name if text
            console.log('  📝 Searching by name');
            url.searchParams.set('search', searchQuery);
          }
          
          url.searchParams.set('limit', '100');
          
          console.log('  📡 API URL:', url.toString());
          const response = await fetch(url);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const result = await response.json();
          console.log('  📦 API Response:', result);
          
          // Handle different response formats
          let brandsData = [];
          
          if (result.data && Array.isArray(result.data)) {
            // Standard paginated response
            brandsData = result.data;
          } else if (Array.isArray(result)) {
            // Direct array response
            brandsData = result;
          } else if (result.brandId && result.name) {
            // Single brand object returned (when searching by brandId)
            brandsData = [result];
          } else {
            console.warn('⚠️ Unexpected API response format:', result);
            brandsData = [];
          }
          
          const brandsSeen = new Set();
          const newBrands = brandsData.map(brand => ({
            brandId: brand.brandId,
            name: brand.name
          }));
          this.brandsList = [...this.brandsList, ...newBrands].filter((brand) => {
            if (brandsSeen.has(brand.brandId)) { // This is already in the brands list
              return false;
            } else {
              brandsSeen.add(brand.brandId);
              return true;
            }
          });
          
          console.log(`✅ Found ${this.brandsList.length} brands matching "${searchQuery}"`);
        } catch (error) {
          console.error('❌ Error searching brands:', error);
          this.showSnackbar('Failed to search brands. Please try again.', 'error');
        } finally {
          this.brandsLoading = false;
        }
      }, 300); // 300ms debounce
    },

    transformMongoToComponent(mongoRule, rule_index) {
      // Transform MongoDB field names to component field names
      console.log('🔄 Transforming rule:', mongoRule);
      console.log('📋 Raw bricks:', mongoRule.bricks);
      
      // Format GPC codes - MongoDB stores them as "gpc-10000365", component expects just the code
      let formattedGpcBricks = [];
      if (mongoRule.bricks && mongoRule.bricks.length > 0) {
        formattedGpcBricks = mongoRule.bricks.map(gpcCode => {
          const codeStr = String(gpcCode);
          // Remove "gpc-" prefix if present
          const cleaned = codeStr.replace(/^gpc-/, '');
          console.log(`  🔢 GPC: "${gpcCode}" → "${cleaned}"`);
          return cleaned;
        });
      }
      console.log('✅ Formatted GPC array:', formattedGpcBricks);
      
      const transformed = {
        _id: mongoRule._id,
        brands: (mongoRule.brands && mongoRule.brands.length > 0) 
          ? mongoRule.brands
          : ['All'],
        rule_index: rule_index,
        
        // New stuff down here!!!
        segments: mongoRule.segments,
        bricks: mongoRule.bricks,
        areas: mongoRule.areas,
        attribute_ids: mongoRule.attribute_ids,
        rule_description: mongoRule.rule_description,
        rule_name: mongoRule.rule_name,
        instruction: mongoRule.instruction,
        rule_type: mongoRule.rule_type,
      };
      
      console.log('✅ Transformed to:', transformed);
      return transformed;
    },

    onStyleRuleChanged(updatedRules) {
      console.log('📝 Style rules changed:', updatedRules);
      this.styleRules = updatedRules;
      this.hasUnsavedChanges = true;
    },

    onStyleRulesEnvironmentChanged(newEnvironment) {
      console.log(`🔄 Style rules environment changed to: ${newEnvironment}`);
      this.styleRulesEnvironment = newEnvironment;
      // Reload rules from the new environment
      this.loadStyleRules();
    },

    onStyleRuleUpdated({ environment }) {
      console.log(`✅ Rule updated in ${environment}`);
      this.showSnackbar('Style rule updated successfully', 'success');
      // Reload rules to get latest data
      this.loadStyleRules();
    },

    onStyleRuleCreated({ environment }) {
      console.log(`✅ Rule created in ${environment}`);
      this.showSnackbar('Style rule created successfully', 'success');
      // Reload rules to get latest data
      this.loadStyleRules();
    },

    async deleteStyleRule({ environment }) {
      console.log(`✅ Rule deleted in ${environment}`);
      this.showSnackbar('Style rule deleted successfully', 'success');
      // Reload rules to get latest data
      this.loadStyleRules();
    },

    exportStyleRules() {
      // Export rules as CSV
      console.log('📊 Exporting style rules as CSV');
      
      if (this.styleRules.length === 0) {
        this.showSnackbar('No rules to export', 'warning');
        return;
      }

      const csvHeaders = ['Rule Name', 'Brands', 'GPC Bricks', 'Rule Type', 'Instruction'];
      const csvRows = this.styleRules.map(rule => [
        rule.rule_name || rule.name,
        rule.brands,
        (rule.bricks || []).join('; '),
        rule.rule_type,
        rule.instruction || ''
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `style-rules-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      this.showSnackbar('Style rules exported', 'success');
    },

    importStyleRules(file) {
      console.log('📥 Importing style rules from file:', file.name);
      this.showSnackbar('CSV import not yet implemented', 'info');
      // TODO: Implement CSV parsing and import
    },

    // ============================================================================
    // END STYLE RULES MANAGER METHODS
    // ============================================================================

    // ============================================================================
    // V2 TAXONOMY SUPPORT METHODS
    // ============================================================================
    
    /**
     * Check system health and feature flags - call on mount
     */
    async checkSystemHealth() {
      try {
        const response = await fetch(`${this.FUNCTION_URL}?action=healthCheck`);
        if (!response.ok) {
          throw new Error(`Health check failed: ${response.status}`);
        }
        const data = await response.json();
        this.systemHealth = data;
        this.v2EndpointsEnabled = data.checks?.feature_flags?.ENABLE_V2_ENDPOINTS || false;
        console.log('[V2] System health:', data.status, '| V2 endpoints:', this.v2EndpointsEnabled);
        return data;
      } catch (error) {
        console.warn('[V2] Health check failed:', error.message);
        this.systemHealth = { status: 'error', error: error.message };
        this.v2EndpointsEnabled = false;
        return null;
      }
    },
    
    /**
     * Load taxonomy families (V1/V2 groupings)
     */
    async loadTaxonomyFamilies() {
      if (!this.v2EndpointsEnabled) {
        // Fall back to simple taxonomy list
        return;
      }
      
      try {
        const response = await fetch(`${this.FUNCTION_URL}?action=listTaxonomyFamilies`);
        if (!response.ok) {
          throw new Error(`Failed to load taxonomy families: ${response.status}`);
        }
        const data = await response.json();
        this.taxonomyFamilies = data.families || [];
        this.sharedAttributesInfo = data.sharedAttributes || null;
        console.log('[V2] Loaded', this.taxonomyFamilies.length, 'taxonomy families');
      } catch (error) {
        console.warn('[V2] Failed to load taxonomy families:', error.message);
      }
    },
    
    /**
     * Get orphan report for frontend rules
     */
    async loadOrphanReport(frontendFilename, backendFilename, version = 'Working Version') {
      if (!this.v2EndpointsEnabled) return null;
      
      try {
        const params = new URLSearchParams({
          action: 'getOrphanReport',
          frontendFilename,
          backendFilename,
          version
        });
        const response = await fetch(`${this.FUNCTION_URL}?${params}`);
        if (!response.ok) {
          throw new Error(`Failed to load orphan report: ${response.status}`);
        }
        const data = await response.json();
        this.orphanReport = data;
        console.log('[V2] Orphan report:', data.summary?.orphaned, 'orphaned of', data.summary?.total);
        return data;
      } catch (error) {
        console.warn('[V2] Failed to load orphan report:', error.message);
        return null;
      }
    },
    
    /**
     * Create a new taxonomy version (e.g., V2)
     */
    async createTaxonomyVersion(sourceFilename, targetFilename, options = {}) {
      if (!this.v2EndpointsEnabled) {
        this.showSnackbar('V2 features not enabled', 'error');
        return null;
      }
      
      try {
        const response = await fetch(`${this.FUNCTION_URL}?action=createTaxonomyVersion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceFilename,
            targetFilename,
            options
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          this.showSnackbar('New taxonomy version created!', 'success');
          await this.loadTaxonomyList();
          await this.loadTaxonomyFamilies();
          
          if (data.warnings?.length) {
            data.warnings.forEach(w => this.showSnackbar(w, 'warning'));
          }
        } else {
          this.showSnackbar(data.error || 'Failed to create version', 'error');
        }
        
        return data;
      } catch (error) {
        console.error('[V2] Failed to create taxonomy version:', error);
        this.showSnackbar('Failed to create taxonomy version', 'error');
        return null;
      }
    },
    
    /**
     * Get shared attributes info
     */
    async loadSharedAttributesInfo() {
      try {
        const response = await fetch(`${this.FUNCTION_URL}?action=getSharedAttributesInfo`);
        if (response.ok) {
          this.sharedAttributesInfo = await response.json();
          console.log('[V2] Shared attributes info loaded');
        }
      } catch (error) {
        console.warn('[V2] Failed to load shared attributes info:', error.message);
      }
    },
    
    // ============================================================================
    // GRANULAR CHANGE TRACKING METHODS
    // ============================================================================
    
    /**
     * Mark taxonomy as changed
     */
    markTaxonomyChanged() {
      this.unsavedChanges.taxonomy = true;
      this.hasUnsavedChanges = true;
    },
    
    /**
     * Mark attributes as changed
     */
    markAttributesChanged() {
      this.unsavedChanges.attributes = true;
      this.hasUnsavedChanges = true;
    },
    
    /**
     * Mark GPC mappings as changed
     */
    markGpcMappingsChanged() {
      this.unsavedChanges.gpcMappings = true;
      this.hasUnsavedChanges = true;
    },
    
    /**
     * Mark frontend rules as changed
     */
    markFrontendChanged() {
      this.unsavedChanges.frontend = true;
      this.hasUnsavedChanges = true;
    },
    
    /**
     * Clear unsaved changes (all or specific type)
     */
    async clearUnsavedChanges(type = 'all') {
      // Temporarily disable tracking to prevent watchers from re-marking as unsaved
      const wasTracking = this.trackChanges;
      this.trackChanges = false;
      
      if (type === 'all') {
        this.unsavedChanges = {
          taxonomy: false,
          attributes: false,
          gpcMappings: false,
          frontend: false
        };
        this.hasUnsavedChanges = false;
      } else {
        this.unsavedChanges[type] = false;
        this.hasUnsavedChanges = Object.values(this.unsavedChanges).some(v => v);
      }
      
      // Wait for Vue to process before restoring tracking
      await this.$nextTick();
      this.trackChanges = wasTracking;
    },
    
    // ============================================================================
    // MULTI-USER SESSION & CONFLICT DETECTION
    // ============================================================================
    
    /**
     * Handle page unload - clean up session
     */
    handleBeforeUnload() {
      // Use sendBeacon for reliable delivery during page unload
      if (this.sessionId && navigator.sendBeacon) {
        const data = JSON.stringify({ sessionId: this.sessionId });
        navigator.sendBeacon(
          `${this.FUNCTION_URL}?action=removeSession`,
          new Blob([data], { type: 'application/json' })
        );
      }
    },
    
    /**
     * Register or update current session
     */
    async registerSession() {
      if (!this.sessionId || !this.selectedTaxonomy || !this.loadedVersion) return;
      
      try {
        await fetch(`${this.FUNCTION_URL}?action=registerSession`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: this.sessionId,
            filename: this.selectedTaxonomy.replace('.json', ''),
            version: this.loadedVersion,
            activeSection: this.currentView,
            hasUnsavedChanges: this.hasUnsavedChanges
          })
        });
      } catch (error) {
        console.warn('[Session] Failed to register session:', error.message);
      }
    },
    
    /**
     * Get active sessions for current taxonomy
     */
    async fetchActiveSessions() {
      if (!this.selectedTaxonomy || !this.loadedVersion) {
        this.activeSessions = [];
        return;
      }
      
      try {
        const params = new URLSearchParams({
          action: 'getActiveSessions',
          filename: this.selectedTaxonomy.replace('.json', ''),
          version: this.loadedVersion,
          excludeSessionId: this.sessionId
        });
        
        const response = await fetch(`${this.FUNCTION_URL}?${params}`);
        if (response.ok) {
          const data = await response.json();
          this.activeSessions = data.sessions || [];
          if (this.activeSessions.length > 0) {
            console.log('[Session] Active sessions on this taxonomy:', this.activeSessions.length);
          }
        }
      } catch (error) {
        console.warn('[Session] Failed to fetch active sessions:', error.message);
        this.activeSessions = [];
      }
    },
    
    /**
     * Remove current session
     */
    async removeSession() {
      if (!this.sessionId) return;
      
      try {
        await fetch(`${this.FUNCTION_URL}?action=removeSession`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId })
        });
      } catch (error) {
        console.warn('[Session] Failed to remove session:', error.message);
      }
    },
    
    /**
     * Check for conflicts before saving a specific data type
     * Returns true if there's a conflict, false if safe to save
     */
    async checkForConflict(dataType) {
      const collectionMap = {
        'taxonomy': 'taxonomy',
        'attributes': 'attribute_taxonomy',
        'frontend': 'frontend_taxonomy',
        'gpcMappings': 'gpc_mappings'
      };
      
      const collection = collectionMap[dataType];
      if (!collection) return false;
      
      const loadedAt = this.loadedTimestamps[dataType];
      if (!loadedAt) {
        // No timestamp means we haven't loaded this data yet - no conflict possible
        return false;
      }
      
      try {
        const filename = dataType === 'frontend' 
          ? this.getLinkedFrontendFilename()
          : this.selectedTaxonomy.replace('.json', '');
          
        const params = new URLSearchParams({
          action: 'getUpdatedAt',
          collection,
          filename,
          version: this.loadedVersion
        });
        
        const response = await fetch(`${this.FUNCTION_URL}?${params}`);
        if (!response.ok) return false;
        
        const data = await response.json();
        const serverUpdatedAt = data.updatedAt ? new Date(data.updatedAt) : null;
        const loadedAtDate = new Date(loadedAt);
        
        if (serverUpdatedAt && serverUpdatedAt > loadedAtDate) {
          // There's a conflict - someone saved after we loaded
          console.warn(`[Conflict] ${dataType} was modified on server after we loaded`);
          this.conflictDialog = {
            show: true,
            dataType,
            serverUpdatedAt: serverUpdatedAt.toISOString(),
            loadedAt: loadedAtDate.toISOString()
          };
          return true;
        }
        
        return false;
      } catch (error) {
        console.warn(`[Conflict] Failed to check for conflicts on ${dataType}:`, error.message);
        return false; // Assume no conflict if check fails
      }
    },
    
    /**
     * Handle conflict resolution - reload latest data
     */
    async resolveConflictReload() {
      const dataType = this.conflictDialog.dataType;
      this.conflictDialog.show = false;
      
      // Reload the specific data type
      if (dataType === 'taxonomy' || dataType === 'attributes') {
        await this.loadTaxonomyFromCloud(this.loadedVersion);
      } else if (dataType === 'frontend') {
        await this.loadFrontendRulesFromCloud(this.loadedVersion);
      } else if (dataType === 'gpcMappings') {
        await this.loadGpcMappings(
          this.selectedTaxonomy.replace('.json', ''),
          this.loadedVersion
        );
      }
      
      this.showSnackbar(`Reloaded latest ${dataType} from server`, 'info');
    },
    
    /**
     * Reload data for a specific view group when switching views.
     * This ensures we have the latest data and fresh timestamps for conflict detection.
     * @param {string} viewGroup - 'taxonomy', 'attributes', 'frontend', 'gpcMappings'
     * @param {string} version - The version to load (e.g., 'Working Version')
     */
    async reloadDataForViewGroup(viewGroup, version) {
      if (!this.selectedTaxonomy || !version) {
        console.log(`[reloadDataForViewGroup] Skipping - no taxonomy or version`);
        return;
      }
      
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      console.log(`[reloadDataForViewGroup] Reloading ${viewGroup} for ${baseFilename} @ ${version}`);
      
      try {
        if (viewGroup === 'taxonomy') {
          // Reload taxonomy data (also reloads attributes since they're loaded together)
          await this.loadTaxonomyFromCloud(version);
        } else if (viewGroup === 'attributes') {
          // For attributes, we need to reload just the attributes
          // Since loadTaxonomyFromCloud loads both, we use that but could optimize later
          await this.reloadAttributesOnly(baseFilename, version);
        } else if (viewGroup === 'frontend') {
          // Reload frontend rules
          await this.loadFrontendRulesFromCloud(version);
        } else if (viewGroup === 'gpcMappings') {
          // Reload GPC mappings
          await this.loadGpcMappings(baseFilename, version);
        }
        
        console.log(`[reloadDataForViewGroup] Successfully reloaded ${viewGroup}`);
      } catch (error) {
        console.error(`[reloadDataForViewGroup] Failed to reload ${viewGroup}:`, error);
        // Don't show error to user - this is a background refresh
      }
    },
    
    /**
     * Helper to determine view group for a view name
     */
    getViewGroup(view) {
      if (view === 'backendTaxonomyEditor') return 'taxonomy';
      if (['attributeEditor', 'attributeOverview', 'languageVariants'].includes(view)) return 'attributes';
      if (view === 'frontendTaxonomyEditor') return 'frontend';
      if (view === 'gpcMapping') return 'gpcMappings';
      return 'other';
    },
    
    /**
     * Handle view change request from sidebar - checks for unsaved changes BEFORE switching
     * This prevents the view from changing until user confirms
     */
    handleViewChangeRequest(newView) {
      const oldView = this.currentView;
      if (newView === oldView) return;
      
      const oldGroup = this.getViewGroup(oldView);
      const newGroup = this.getViewGroup(newView);
      const isSwitchingGroups = oldGroup !== newGroup;
      
      console.log(`[handleViewChangeRequest] ${oldView} (${oldGroup}) → ${newView} (${newGroup}), switching groups: ${isSwitchingGroups}`);
      
      // Check for unsaved changes when LEAVING a view group
      if (isSwitchingGroups) {
        const unsavedInOldGroup = this.unsavedChanges?.[oldGroup] || false;
        
        if (unsavedInOldGroup) {
          const groupNames = {
            taxonomy: 'Backend Taxonomy',
            attributes: 'Attributes',
            frontend: 'Frontend Rules',
            gpcMappings: 'GPC Mappings'
          };
          const groupName = groupNames[oldGroup] || oldGroup;
          
          const confirmed = confirm(
            `You have unsaved changes in ${groupName} that will be lost. Continue?`
          );
          
          if (!confirmed) {
            console.log(`[handleViewChangeRequest] User cancelled view switch`);
            return; // Don't change the view
          }
          
          // User confirmed - clear the unsaved changes for that group
          if (this.unsavedChanges) {
            this.unsavedChanges[oldGroup] = false;
          }
        }
      }
      
      // Now actually change the view - the watcher will handle the rest
      this.currentView = newView;
    },
    
    /**
     * Reload only frontend rules (without reloading backend taxonomy)
     * Updates loadedTimestamps.frontend with fresh server value
     */
    async loadFrontendRulesFromCloud(version) {
      const frontendFilename = this.getLinkedFrontendFilename();
      const editorType = 'frontend';
      const modPath = `${frontendFilename}_modified_${version}.json`;
      const modUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${modPath}&editorType=${editorType}`;
      
      // Temporarily disable change tracking to prevent watcher from marking as unsaved
      const wasTracking = this.trackChanges;
      this.trackChanges = false;
      
      try {
        const response = await fetch(modUrl);
        if (!response.ok) {
          throw new Error(`Failed to load frontend rules: ${response.status}`);
        }
        
        const modJson = await response.json();
        this.frontendRules = modJson.data || { categories: [] };
        
        // Update timestamp for conflict detection
        if (modJson.updatedAt) {
          this.loadedTimestamps.frontend = modJson.updatedAt;
        }
        
        console.log(`[loadFrontendRulesFromCloud] Loaded frontend rules, timestamp: ${this.loadedTimestamps.frontend}`);
      } catch (error) {
        console.error('[loadFrontendRulesFromCloud] Failed:', error);
        throw error;
      } finally {
        // Wait for Vue to flush watchers before restoring trackChanges
        await this.$nextTick();
        this.trackChanges = wasTracking;
      }
    },
    
    /**
     * Reload only attributes data (without reloading taxonomy)
     * Updates loadedTimestamps.attributes with fresh server value
     */
    async reloadAttributesOnly(baseFilename, version) {
      const editorType = 'backend';
      const attPath = `${baseFilename}_attributes_${version}.json`;
      const attUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${attPath}&editorType=${editorType}`;
      
      // Temporarily disable change tracking to prevent watcher from marking as unsaved
      const wasTracking = this.trackChanges;
      this.trackChanges = false;
      
      try {
        const response = await fetch(attUrl);
        if (!response.ok) {
          throw new Error(`Failed to load attributes: ${response.status}`);
        }
        
        const attJson = await response.json();
        this.attributeData = attJson.data || {};
        
        // Update timestamp for conflict detection
        if (attJson.updatedAt) {
          this.loadedTimestamps.attributes = attJson.updatedAt;
        }
        
        console.log(`[reloadAttributesOnly] Loaded ${Object.keys(this.attributeData).length} attributes, timestamp: ${this.loadedTimestamps.attributes}`);
      } catch (error) {
        console.error('[reloadAttributesOnly] Failed:', error);
        throw error;
      } finally {
        // Wait for Vue to flush watchers before restoring trackChanges
        await this.$nextTick();
        this.trackChanges = wasTracking;
      }
    },
    
    /**
     * Handle conflict resolution - overwrite anyway
     */
    async resolveConflictOverwrite() {
      const dataType = this.conflictDialog.dataType;
      console.log(`[resolveConflictOverwrite] dataType=${dataType}, selectedTaxonomy=${this.selectedTaxonomy}, loadedVersion=${this.loadedVersion}`);
      this.conflictDialog.show = false;
      
      // Proceed with save, bypassing conflict check
      const result = await this.saveDataType(dataType, true);
      console.log(`[resolveConflictOverwrite] saveDataType returned: ${result}`);
    },
    
    /**
     * Save a specific data type (with optional force flag to bypass conflict check)
     */
    async saveDataType(dataType, force = false) {
      console.log(`[saveDataType] dataType=${dataType}, force=${force}`);
      
      // Check for conflict first (unless forced)
      if (!force) {
        const hasConflict = await this.checkForConflict(dataType);
        if (hasConflict) return false; // Dialog will be shown
      }
      
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      const version = this.loadedVersion;
      
      console.log(`[saveDataType] baseFilename=${baseFilename}, version=${version}`);
      
      try {
        // Pass force as skipConflictCheck to bypass server-side check when overwriting
        if (dataType === 'taxonomy') {
          console.log('[saveDataType] calling saveTaxonomyOnly');
          await this.saveTaxonomyOnly(baseFilename, version, force);
        } else if (dataType === 'attributes') {
          console.log('[saveDataType] calling saveAttributesOnly');
          await this.saveAttributesOnly(baseFilename, version, force);
        } else if (dataType === 'frontend') {
          console.log('[saveDataType] calling saveFrontendOnly');
          await this.saveFrontendOnly(version, force);
        } else if (dataType === 'gpcMappings') {
          console.log('[saveDataType] calling saveGpcMappingsOnly');
          await this.saveGpcMappingsOnly(baseFilename, version, force);
        }
        
        console.log(`[saveDataType] save completed successfully for ${dataType}`);
        await this.clearUnsavedChanges(dataType);
        this.showSnackbar(`${dataType} saved successfully`, 'success');
        
        // Update session to reflect no unsaved changes for this type
        this.registerSession();
        
        return true;
      } catch (error) {
        console.error(`[saveDataType] Failed to save ${dataType}:`, error);
        this.showSnackbar(`Failed to save ${dataType}: ${error.message}`, 'error');
        return false;
      }
    },
    
    /**
     * Save only backend taxonomy data (with server-side conflict detection)
     * @param {boolean} skipConflictCheck - If true, don't send expectedUpdatedAt (for force overwrite)
     */
    async saveTaxonomyOnly(baseFilename, version, skipConflictCheck = false) {
      const pathModified = `allium-ai-manor/taxonomies/${baseFilename}_modified_${version}.json`;
      const editorType = 'backend';
      
      const payload = {
        path: pathModified,
        data: this.taxonomyData,
        metadata: {
          version,
          description: `Taxonomy saved on ${new Date().toLocaleString()}`,
          createdAt: new Date().toISOString()
        },
        editorType,
        // Pass expected timestamp for server-side optimistic locking (unless forcing)
        ...(!skipConflictCheck && { expectedUpdatedAt: this.loadedTimestamps.taxonomy })
      };
      
      const response = await fetch(`${this.FUNCTION_URL}?action=save&editorType=${editorType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.status === 409) {
        // Server detected conflict - throw specific error
        const conflictData = await response.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        throw error;
      }
      
      if (!response.ok) {
        throw new Error(`Save failed with status ${response.status}`);
      }
      
      const result = await response.json();
      // Use server's timestamp instead of client time
      this.loadedTimestamps.taxonomy = result.updatedAt || new Date().toISOString();
      return result;
    },
    
    /**
     * Save only attributes data (with server-side conflict detection)
     * @param {boolean} skipConflictCheck - If true, don't send expectedUpdatedAt (for force overwrite)
     */
    async saveAttributesOnly(baseFilename, version, skipConflictCheck = false) {
      const pathAttributes = `allium-ai-manor/taxonomies/${baseFilename}_attributes_${version}.json`;
      const editorType = 'backend';
      
      // Debug logging
      console.log(`[saveAttributesOnly] skipConflictCheck=${skipConflictCheck}, loadedTimestamps.attributes=${this.loadedTimestamps.attributes}`);
      
      const payload = {
        path: pathAttributes,
        data: this.attributeData,
        metadata: {
          version,
          description: `Attributes saved on ${new Date().toLocaleString()}`,
          createdAt: new Date().toISOString()
        },
        editorType,
        ...(!skipConflictCheck && { expectedUpdatedAt: this.loadedTimestamps.attributes })
      };
      
      console.log(`[saveAttributesOnly] payload.expectedUpdatedAt=${payload.expectedUpdatedAt || 'NOT SET (overwrite mode)'}`);
      
      const response = await fetch(`${this.FUNCTION_URL}?action=save&editorType=${editorType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.status === 409) {
        const conflictData = await response.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        throw error;
      }
      
      if (!response.ok) {
        throw new Error(`Save failed with status ${response.status}`);
      }
      
      const result = await response.json();
      this.loadedTimestamps.attributes = result.updatedAt || new Date().toISOString();
      return result;
    },
    
    /**
     * Save only frontend rules (with server-side conflict detection)
     * @param {boolean} skipConflictCheck - If true, don't send expectedUpdatedAt (for force overwrite)
     */
    async saveFrontendOnly(version, skipConflictCheck = false) {
      const frontendFilename = this.getLinkedFrontendFilename();
      const rulesPath = `allium-ai-manor/taxonomies/${frontendFilename}_modified_${version}.json`;
      const editorType = 'frontend';
      
      const payload = {
        path: rulesPath,
        data: this.frontendRules,
        metadata: {
          version,
          description: `Frontend rules saved on ${new Date().toLocaleString()}`,
          createdAt: new Date().toISOString()
        },
        editorType,
        ...(!skipConflictCheck && { expectedUpdatedAt: this.loadedTimestamps.frontend })
      };
      
      const response = await fetch(`${this.FUNCTION_URL}?action=save&editorType=${editorType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.status === 409) {
        const conflictData = await response.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        throw error;
      }
      
      if (!response.ok) {
        throw new Error(`Save failed with status ${response.status}`);
      }
      
      const result = await response.json();
      this.loadedTimestamps.frontend = result.updatedAt || new Date().toISOString();
      return result;
    },
    
    /**
     * Save only GPC mappings (with server-side conflict detection)
     * @param {boolean} skipConflictCheck - If true, don't send expectedUpdatedAt (for force overwrite)
     */
    async saveGpcMappingsOnly(baseFilename, version, skipConflictCheck = false) {
      const payload = {
        filename: baseFilename,
        version,
        mappings: this.gpcMappings,
        metadata: {
          description: `GPC mappings for ${baseFilename}`,
          lastUpdated: new Date().toISOString(),
          taxonomyVersion: version
        },
        ...(!skipConflictCheck && { expectedUpdatedAt: this.loadedTimestamps.gpcMappings })
      };
      
      const response = await fetch(`${this.FUNCTION_URL}?action=saveGpcMappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.status === 409) {
        const conflictData = await response.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        throw error;
      }
      
      if (!response.ok) {
        throw new Error(`Save failed with status ${response.status}`);
      }
      
      const result = await response.json();
      this.loadedTimestamps.gpcMappings = result.updatedAt || new Date().toISOString();
      return result;
    },
    
    // ============================================================================
    // V2 HELPER METHODS
    // ============================================================================
    
    /**
     * Determine current taxonomy version (V1 or V2)
     */
    getCurrentTaxonomyVersion() {
      if (!this.selectedTaxonomy) return null;
      return this.selectedTaxonomy.includes('_v2') ? 'v2' : 'v1';
    },
    
    /**
     * Get linked frontend filename for current taxonomy
     */
    getLinkedFrontendFilename() {
      if (!this.selectedTaxonomy) return 'frontend';
      if (this.selectedTaxonomy.includes('_v2')) {
        return 'frontend_v2';
      }
      return 'frontend';
    },
    
    /**
     * Get backend taxonomy display name (always shows backend, even when viewing frontend)
     * Used for sidebar status display
     */
    getBackendTaxonomyDisplayName() {
      const backendTaxonomy = this.selectedBackendTaxonomy || this.selectedTaxonomy;
      if (!backendTaxonomy || backendTaxonomy.startsWith('frontend')) {
        return 'simplified taxonomy deduplicated'; // Default backend taxonomy
      }
      return backendTaxonomy.replace('.json', '').replace(/_/g, ' ');
    },
    
    /**
     * Check if current view is editing shared attributes
     */
    isEditingSharedAttributes() {
      return ['attributeEditor', 'attributeOverview', 'languageVariants'].includes(this.currentView);
    },
    
    /**
     * Check if V2 taxonomy exists for current family
     */
    hasV2Taxonomy() {
      return this.taxonomies.some(t => t.includes('_v2'));
    },
    
    /**
     * Get suggested V2 filename based on current taxonomy
     */
    getSuggestedV2Filename() {
      if (!this.selectedTaxonomy) return '';
      return this.selectedTaxonomy.replace('.json', '') + '_v2';
    },
    
    /**
     * Get taxonomy version class for styling
     */
    getTaxonomyVersionClass() {
      return {
        'taxonomy-v1': this.getCurrentTaxonomyVersion() === 'v1',
        'taxonomy-v2': this.getCurrentTaxonomyVersion() === 'v2',
        'taxonomy-frontend': this.selectedTaxonomy?.startsWith('frontend')
      };
    },
    
    /**
     * Handle V2 creation
     */
    async handleCreateV2() {
      this.creatingV2 = true;
      try {
        const sourceFilename = this.selectedTaxonomy.replace('.json', '');
        const targetFilename = this.newV2Filename || this.getSuggestedV2Filename();
        
        const result = await this.createTaxonomyVersion(
          sourceFilename,
          targetFilename,
          this.createV2Options
        );
        
        if (result?.success) {
          this.showCreateV2Dialog = false;
          this.newV2Filename = '';
        }
      } finally {
        this.creatingV2 = false;
      }
    },
    
    // ============================================================================
    // END V2 TAXONOMY SUPPORT METHODS
    // ============================================================================

    // Get brick ID for a category by checking taxonomy data
    getBrickIdForCategory(categoryName) {
      if (!categoryName) {
        return null;
      }
      
      // The ID from taxonomy data IS the brick ID
      if (this.taxonomyData && this.taxonomyData[categoryName] && this.taxonomyData[categoryName].id) {
        const brickId = this.taxonomyData[categoryName].id;
        return brickId;
      }
      
      return null;
    },

    async loadPlaceholderWebCategoryCodes() {
      try {
        const response = await fetch(`${this.FUNCTION_URL}?action=loadWebCategoryPlaceholders`);
        if (!response.ok) {
          throw new Error(`Failed to fetch placeholders with status: ${response.status}`);
        }
        const data = await response.json();
        // Ensure data is an array before assigning
        this.placeholderWebCategoryCodes = Array.isArray(data) ? data : [];
        console.log(`Loaded ${this.placeholderWebCategoryCodes.length} placeholder web category codes.`);
      } catch (error) {
        console.error('Could not load placeholder web category codes:', error);
        this.showSnackbar('Could not load placeholder web category codes.', 'error');
        this.placeholderWebCategoryCodes = []; // Default to empty on error
      }
    },

    // Helper method to find category ID by name
    findCategoryIdByName(categoryName) {
      const findInNode = (node) => {
        if (node.name === categoryName) {
          return node.id;
        }
        if (node.subcategories) {
          for (const child of node.subcategories) {
            const found = findInNode(child);
            if (found) return found;
          }
        }
        return null;
      };
      
      // Search through taxonomy data
      if (this.taxonomyData && typeof this.taxonomyData === 'object') {
        for (const topLevelKey in this.taxonomyData) {
          const topLevelNode = this.taxonomyData[topLevelKey];
          const found = findInNode(topLevelNode);
          if (found) return found;
        }
      }
      
      return null;
    },

    // Debug method to toggle debug mode (accessible from browser console)
    toggleDebugMode() {
      this.debugMode = !this.debugMode;
      console.log(`Debug mode is now ${this.debugMode ? 'enabled' : 'disabled'}`);
      if (this.debugMode) {
        console.log('Debug buttons are now visible in the Categories section');
      } else {
        console.log('Debug buttons are now hidden');
      }
    },

    // Show category details dialog
    showCategoryDetails(categoryName, brickId) {
      this.categoryDetailsDialog = {
        show: true,
        categoryName: categoryName,
        brickId: brickId
      };
    },

    // Select category from dialog
    selectCategoryFromDialog() {
      this.selectedTopLevelNode = this.categoryDetailsDialog.categoryName;
      this.categoryDetailsDialog.show = false;
    },

    // Copy text to clipboard
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.showSnackbar('Copied to clipboard!', 'success');
      } catch (err) {
        console.error('Failed to copy: ', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showSnackbar('Copied to clipboard!', 'success');
      }
    },

    // Standalone method to load GPC bricks if not already loaded
    async ensureGpcBricksLoaded() {
      console.log('🔍 ensureGpcBricksLoaded called - current state:', {
        gpcBricksDataLength: this.gpcBricksData.length,
        gpcDataLoading: this.gpcDataLoading,
        gpcBricksLoaded: this.gpcBricksLoaded
      });
      
      if (this.gpcBricksData.length === 0 && !this.gpcDataLoading) {
        console.log('🔄 Loading GPC bricks data on demand...');
        this.gpcDataLoading = true;
        try {
          const url = `${this.FUNCTION_URL}?action=loadGpcBricks`;
          console.log('🌐 Fetching from:', url);
          
          // Add timeout and AbortController for better error handling
          const controller = new AbortController();
          const timeoutId = setTimeout(() => {
            controller.abort();
            console.log('⏰ Fetch request timed out after 10 seconds');
          }, 10000);
          
          const response = await fetch(url, {
            signal: controller.signal,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          
          clearTimeout(timeoutId);
          console.log('📥 Response received - status:', response.status, 'ok:', response.ok);
          console.log('📋 Response headers:', Object.fromEntries(response.headers.entries()));
          
          if (!response.ok) {
            const errorText = await response.text();
            console.log('❌ Error response body:', errorText);
            throw new Error(`Failed to load GPC bricks: ${response.status} - ${errorText}`);
          }
          
          const data = await response.json();
          console.log('📊 Raw API response type:', typeof data, 'isArray:', Array.isArray(data));
          console.log('📊 Raw API response keys:', Object.keys(data || {}));
          
          // Handle the API response structure: {data: [...]} 
          let bricksArray = [];
          if (data && Array.isArray(data.data)) {
            bricksArray = data.data;
          } else if (Array.isArray(data)) {
            bricksArray = data;
          }
          
          this.gpcBricksData = bricksArray;
          this.gpcBricksLoaded = true;
          console.log(`✅ Loaded ${this.gpcBricksData.length} GPC bricks on demand`);
          
          // Log a few sample bricks to verify the data structure
          if (this.gpcBricksData.length > 0) {
            console.log('📋 Sample GPC brick:', this.gpcBricksData[0]);
            console.log('📋 Sample brick keys:', Object.keys(this.gpcBricksData[0] || {}));
          }
          
          // Also load GPC mappings if we have a selected taxonomy
          if (this.selectedTaxonomy && this.currentVersionLabel) {
            console.log('🔗 Loading GPC mappings for current taxonomy...');
            const baseFilename = this.selectedTaxonomy.replace('.json', '');
            await this.loadGpcMappings(baseFilename, this.currentVersionLabel);
            
            // If no mappings exist, offer to create initial ones
            if (this.gpcMappings.length === 0) {
              console.log('🎯 No existing mappings found, skipping auto-suggestions for now...');
              // TODO: Implement generateAutoMappingSuggestions function
              // await this.generateAutoMappingSuggestions();
            }
          }
        } catch (error) {
          console.error('❌ Failed to load GPC bricks on demand:', error);
          if (error.name === 'AbortError') {
            this.gpcError = 'GPC bricks request timed out';
          } else {
            this.gpcError = `Failed to load GPC bricks: ${error.message}`;
          }
          this.gpcBricksData = [];
        } finally {
          this.gpcDataLoading = false;
        }
      }
    },

    // Helper method to find brick ID by searching through GPC bricks data


    // Manual test method for GPC API debugging
    async testGpcApiManually() {
      console.log('🧪 Manual GPC API test starting...');
      try {
        const url = `${this.FUNCTION_URL}?action=loadGpcBricks`;
        console.log('🌐 Testing URL:', url);
        
        // Simple fetch without timeout first
        const response = await fetch(url);
        console.log('✅ Basic fetch succeeded:', response.status, response.ok);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ JSON parsing succeeded');
          console.log('📋 Response structure:', {
            type: typeof data,
            isArray: Array.isArray(data),
            keys: Object.keys(data || {}),
            dataProperty: data.data ? 'has data property' : 'no data property',
            dataLength: data.data ? data.data.length : 'N/A'
          });
          
          if (data.data && data.data.length > 0) {
            console.log('🎯 Sample brick from manual test:', data.data[0]);
            
            // Load the data into our component manually
            this.gpcBricksData = data.data;
            this.gpcBricksLoaded = true;
            console.log(`🎉 Manual load successful: ${this.gpcBricksData.length} bricks loaded`);
            
            return true;
          }
        }
      } catch (error) {
        console.error('❌ Manual test failed:', error);
      }
      return false;
    },

    async loadFrontendRulesInBackground() {
      try {
        // Use linked frontend filename based on current backend taxonomy
        const frontendFilename = this.getLinkedFrontendFilename();
        const version = 'Working Version'; // Or match current backend version
        
        console.log(`[loadFrontendRulesInBackground] Loading ${frontendFilename} for backend ${this.selectedTaxonomy}`);
        const url = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${frontendFilename}_modified_${version}.json&editorType=frontend`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          if (data.data && Array.isArray(data.data.categories)) {
            this.frontendRules = data.data;
            console.log('Frontend rules loaded in background:', this.frontendRules.categories.length, 'categories');
          }
        }
      } catch (error) {
        console.warn('Could not load frontend rules in background:', error);
      }
    },
    updateWebCategoryCode(newValue) {
      if (!this.selectedNodeId || this.currentView !== 'frontendTaxonomyEditor') {
          return;
      }
      const rule = this.findRuleById(this.frontendRules.categories, this.selectedNodeId);
      if (rule) {
          // In Vue, direct assignment to a nested property is reactive if the parent object is reactive.
          rule.web_category_code = newValue;
          this.hasUnsavedChanges = true;
      }
  },
    async saveFrontendChanges() {
      console.log('saveFrontendChanges called.');
      
      // Basic validation - allow any frontend taxonomy (frontend.json, frontend_v2.json, etc.)
      const isFrontendTaxonomy = this.selectedTaxonomy?.startsWith('frontend');
      if (!this.selectedTaxonomy || !isFrontendTaxonomy) {
        this.showSnackbar('This action is only available for frontend taxonomies', 'warning');
        return;
      }
  
      if (!this.frontendRules) {
        this.showSnackbar('No frontend rules to save', 'warning');
        return;
      }
  
      // Clean up the rules before saving
      this.cleanupFrontendRules();
  
      // Determine target version
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      let versionToSave = this.saveVersion.trim();
      let description = this.versionDescription || `Frontend changes saved on ${new Date().toLocaleString()}`;
  
      // Special handling for Working Version and QA Version
      if (this.loadedVersion === 'Working Version' || this.loadedVersion === 'QA Version') {
        try {
          // Create timestamped backup only for Working Version
          // Note: Backups skip conflict check (skipConflictCheck=true) since they're new documents
          if (this.loadedVersion === 'Working Version') {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const backupVersionName = `Working Version Backup ${timestamp}`;
            await this.saveFrontendVersionToCloud(backupVersionName, `Automatic backup of Working Version frontend rules created on ${new Date().toLocaleString()}`, true);
            console.log(`Created automatic frontend backup: ${backupVersionName}`);
          }
  
          // Then update the version itself (either Working or QA)
          // This call DOES do conflict checking (skipConflictCheck=false, the default)
          const versionType = this.loadedVersion;
          await this.saveFrontendVersionToCloud(versionType, this.versionDescription || `${versionType} frontend rules updated on ${new Date().toLocaleString()}`);
  
          // Clear all unsaved changes flags
          await this.clearUnsavedChanges('all');
          
          // Update session after successful save
          this.registerSession();
          
          this.showSnackbar(`${versionType} frontend rules updated successfully` +
            (this.loadedVersion === 'Working Version' ? ' with backup' : ''), 'success');
  
          await this.loadTaxonomyVersions();
          this.loadVersion = versionType;
          return;
        } catch (error) {
          // Handle server-side conflict detection (409 response)
          if (error.isConflict) {
            console.log('Server detected conflict during frontend save:', error.conflictData);
            this.conflictDialog.dataType = error.dataType || 'frontend';
            this.conflictDialog.serverUpdatedAt = error.conflictData?.currentUpdatedAt || null;
            this.conflictDialog.loadedAt = error.conflictData?.expectedUpdatedAt || null;
            this.conflictDialog.show = true;
            return;
          }
          console.error(`Failed to update ${this.loadedVersion} frontend rules:`, error);
          this.showSnackbar(`Failed to update ${this.loadedVersion} frontend rules: ${error.message}`, 'error');
          return;
        }
      }
    
      // Handle custom version save if specified (or default to date)
      if (versionToSave) {
        // Check if trying to save as one of our main version names
        if (['production', 'test', 'Working Version', 'QA Version'].includes(versionToSave)) {
          if (versionToSave.toLowerCase() === 'production' || versionToSave.toLowerCase() === 'test') {
            this.showSnackbar('Cannot save directly to "production" or "test". Use "Publish Taxonomy" instead.', 'error');
            return;
          } else if (versionToSave === 'Working Version') {
            this.showSnackbar('To update Working Version, load it first then use Save Frontend Changes', 'info');
            return;
          } else if (versionToSave === 'QA Version') {
            this.showSnackbar('To update QA Version, use Set Working Version or load it first', 'info');
            return;
          }
        }
        const validation = this.validateVersionName(versionToSave);
        if (!validation.valid) {
          this.showSnackbar(validation.message, 'error');
          return;
        }
      } else {
        // If no custom version name, use current date
        versionToSave = new Date().toISOString().split('T')[0];
      }
    
      try {
        // Save frontend rules with proper editorType
        await this.saveFrontendVersionToCloud(versionToSave, description);
        
        // Clear all unsaved changes flags
        await this.clearUnsavedChanges('all');
        this.showSnackbar(`Frontend rules saved as version "${versionToSave}"`, 'success');
        
        // Update UI state
        this.loadedVersion = versionToSave;
        this.saveVersion = '';
        this.versionDescription = '';
        
        // Refresh versions list
        await this.loadTaxonomyVersions();
        
      } catch (error) {
        console.error('Failed to save frontend changes:', error);
        this.showSnackbar(`Failed to save frontend changes: ${error.message}`, 'error');
      }
    },
    // skipConflictCheck: true for backup versions, false for main saves
    async saveFrontendVersionToCloud(versionName, description, skipConflictCheck = false) {
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      const rulesPath = `allium-ai-manor/taxonomies/${baseFilename}_modified_${versionName}.json`;
      
      // Determine if this is the currently loaded version (needs conflict check)
      const isCurrentVersion = versionName === this.loadedVersion;
      const shouldCheckConflict = isCurrentVersion && !skipConflictCheck;
  
      const metadata = {
        version: versionName,
        description: description || `Frontend rules version ${versionName} created on ${new Date().toLocaleString()}`,
        createdAt: new Date().toISOString(),
        type: 'frontend-rules'
      };
  
      const payload = {
        path: rulesPath,
        data: this.frontendRules,
        metadata: metadata,
        editorType: 'frontend',
        // Include expectedUpdatedAt for conflict checking on current version saves
        ...(shouldCheckConflict && { expectedUpdatedAt: this.loadedTimestamps.frontend })
      };
  
      const response = await fetch(`${this.FUNCTION_URL}?action=save&editorType=frontend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      // Handle 409 Conflict from server
      if (response.status === 409) {
        const conflictData = await response.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        error.dataType = 'frontend';
        throw error;
      }
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Save failed for frontend rules: ${errorText}`);
      }
  
      const result = await response.json();
      
      // Update loaded timestamp with server's value
      if (shouldCheckConflict && result.updatedAt) {
        this.loadedTimestamps.frontend = result.updatedAt;
      }
      return true;
    },
    cleanupFrontendRules() {
      if (!this.frontendRules || !Array.isArray(this.frontendRules.categories)) return;
    
      const cleanupRule = (rule) => {
        // Remove empty arrays to keep the data structure clean
        if (Array.isArray(rule.backend_categories) && rule.backend_categories.length === 0) {
          delete rule.backend_categories;
        }
        if (Array.isArray(rule.children) && rule.children.length === 0) {
          delete rule.children;
        }
    
        // Clean up conditions if they're empty
        if (rule.conditions) {
          if (!rule.conditions.groups || rule.conditions.groups.length === 0) {
            delete rule.conditions;
          } else {
            // Remove any empty condition groups
            rule.conditions.groups = rule.conditions.groups.filter(group => 
              group.conditions && group.conditions.length > 0
            );
            if (rule.conditions.groups.length === 0) {
              delete rule.conditions;
            }
          }
        }
    
        // Recursively clean up children
        if (rule.children && Array.isArray(rule.children)) {
          rule.children.forEach(child => cleanupRule(child));
        }
    
        // Remove deprecated properties if they exist
        delete rule.linked_categories;
        delete rule.exclusions;
        delete rule.backend_path; // This should now be in backend_categories array
        delete rule.attribute_ids; // We no longer use manual attribute_ids
      };
    
      this.frontendRules.categories.forEach(rule => cleanupRule(rule));
    
      // Clean up global exclusions if empty
      if (Array.isArray(this.frontendRules.global_exclusions) && this.frontendRules.global_exclusions.length === 0) {
        delete this.frontendRules.global_exclusions;
      }
    },
    addMissingIds(node, existingIds = new Set()) {
      if (!node || typeof node !== 'object') return;
  
      // If node is missing an ID, generate a new one.
      if (!node.id || existingIds.has(node.id)) {
          node.id = `be_id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      existingIds.add(node.id);
  
      // Recurse into subcategories if they exist.
      if (Array.isArray(node.subcategories)) {
          node.subcategories.forEach(child => this.addMissingIds(child, existingIds));
      }
    },
    // ADD: Also add this method to open the rule builder dialog
openRuleBuilder() {
  console.log('Opening rule builder with editingRule:', this.editingRule);
  this.showRuleBuilderDialog = true; // or whatever your dialog show property is called
},
addNodesFromAttributeRule(attribute, parentNodeId) {
  // 1. Validate the selected attribute
  if (!attribute || !attribute.is_closed_list) {
    this.showSnackbar('Please select an attribute with a closed list of values.', 'error');
    return;
  }
  const values = this.getValuesArray(attribute);
  if (!values || values.length === 0) {
    this.showSnackbar('The selected attribute has no enumerable values to create nodes from.', 'error');
    return;
  }

  // 2. Find the parent rule in the `frontendRules` object.
  const parentRule = parentNodeId ? this.findRuleById(this.frontendRules.categories, parentNodeId) : null;

  // 3. Create a new "manual_node" rule for each attribute value using simplified structure
  const newChildRules = values.map(valueObject => ({
    id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    name: valueObject.value_en,
    type: 'manual_node',
    // Add a condition that filters for this specific value
    conditions: {
      operator: 'AND',
      groups: [{
        id: `group_${Date.now()}`,
        operator: 'AND',
        conditions: [{
          id: `cond_${Date.now()}`,
          attribute_id: attribute.attribute_id,
          operator: 'equals',
          value: valueObject.value_en
        }]
      }]
    },
    backend_categories: [], // No backend categories by default
    children: []
  }));

  // 4. Add the new rules to the correct location in the rules tree.
  if (parentRule) {
    if (!Array.isArray(parentRule.children)) {
      parentRule.children = [];
    }
    parentRule.children.push(...newChildRules);
  } else {
    this.frontendRules.categories.push(...newChildRules);
  }

  // 5. Mark changes and rebuild the entire frontend tree.
  this.hasUnsavedChanges = true;
  this.showSnackbar(`Added ${newChildRules.length} new category rules from attribute "${this.getDisplayName(attribute)}".`, 'success');
  this.$nextTick(() => {
    this.rebuildFrontendTreeState();
  });

  // 6. Close the search dialog
  this.attributeSearchDialog.show = false;
},

    findParentOfNode(currentNode, targetId, parentNode = null) {
      if (!currentNode) return null;
      if (currentNode.id === targetId) return parentNode;
      if (Array.isArray(currentNode.children)) {
        for (const child of currentNode.children) {
          const found = this.findParentOfNode(child, targetId, currentNode);
          if (found) return found;
        }
      }
      return null;
    },

    findRuleById(rules, id) {
      for (const rule of rules) {
        if (rule.id === id) return rule;
        if (rule.children) {
          const found = this.findRuleById(rule.children, id);
          if (found) return found;
        }
        // Also check inside linked_categories, as they can contain nested rule definitions in advanced scenarios
        if (rule.linked_categories) {
            for(const linked of rule.linked_categories) {
                if(linked.children) {
                     const found = this.findRuleById(linked.children, id);
                     if (found) return found;
                }
            }
        }
      }
      return null;
    },
    rebuildFrontendTreeState() {
      console.log('[REBUILD] Updating frontend tree data without full rebuild...');
      
      // Generate the new tree data
      const derivedTreeData = generateFrontendTree(this.frontendRules, this.backendTaxonomyData, this.attributeData);
    
      // Update the node ID map
      this.nodeIdMap = {};
      Object.values(derivedTreeData).forEach(rootNode => {
        if (rootNode && rootNode.id) {
            this.buildIdToNodeMap(rootNode);
        }
      });
      
      // Create the virtual root
      const virtualRoot = {
        id: 'frontend_virtual_root',
        label: 'Frontend Taxonomy (Derived)',
        children: [],
        expand: true,
        className: 'node-derived-composite',
        rule_details: { name: 'Virtual Root', message: 'This is the container for all frontend rules.' },
        type: 'root'
      };
    
      if (derivedTreeData && Object.keys(derivedTreeData).length > 0) {
        virtualRoot.children = Object.values(derivedTreeData).map(nodeData => {
          return this.createItemFromData(nodeData, nodeData.id);
        });
      } else {
        virtualRoot.children = [{
          id: 'placeholder-child',
          label: 'Right-click here to add a category rule',
          children: [],
          className: 'node-placeholder'
        }];
      }
    
      // UPDATE: Don't increment treeRenderKey, just update the data
      this.displayedFrontendTree = this.applyExpansionState(virtualRoot);
      
      // Try to trigger a reactive update without forcing a complete re-render
      this.$forceUpdate();
      
      console.log('[REBUILD] Updated tree data without re-render');
    },
    openGlobalExclusionsDialog() {
      this.globalExclusionsDialog.show = true;
      this.globalExclusionsDialog.newExclusionPath = ''; // Clear input field
    },

    addGlobalExclusion() {
      const path = this.globalExclusionsDialog.newExclusionPath.trim();
      if (path && !this.frontendRules.global_exclusions.includes(path)) {
        this.frontendRules.global_exclusions.push(path);
        this.hasUnsavedChanges = true;
        this.globalExclusionsDialog.newExclusionPath = ''; // Clear input
        this.showSnackbar(`Added "${path}" to global exclusions.`, 'success');
        // CRITICAL: Trigger the update.
        this.$nextTick(() => this.rebuildFrontendTreeState());
      }
    },
    
    removeGlobalExclusion(index) {
      const removed = this.frontendRules.global_exclusions.splice(index, 1);
      this.hasUnsavedChanges = true;
      this.showSnackbar(`Global exclusion "${removed[0]}" removed.`, 'success');
      // CRITICAL: Trigger the update.
      this.$nextTick(() => this.rebuildFrontendTreeState());
    },

    // New data property for the dialog state
    globalExclusionsDialog: {
        show: false,
        newExclusionPath: '',
    },


    // In `initializeEmptyTaxonomy` method:
    initializeEmptyTaxonomy(baseName, versionSlug) {
      this.trackChanges = false;
      this.hasUnsavedChanges = false;
  
      if (baseName === 'frontend') {
          this.frontendRules = {
              backend_source_taxonomy: 'simplified_taxonomy_deduplicated.json',
              backend_source_version: 'Working Version',
              categories: [],
              global_exclusions: []
          };
          this.taxonomyData = this.frontendRulesAsTree();
          
          // ******************************************************************
          // **** REMOVE THE LINE BELOW. IT IS THE SOURCE OF THE BUG. ****
          // this.attributeData = {}; 
          // ******************************************************************
  
          // This function will now correctly load the attributes into a non-empty object.
          this.loadBackendSourceForFrontend(this.frontendRules.backend_source_taxonomy, this.frontendRules.backend_source_version);
      } else {
          // Existing backend initialization
          this.taxonomyData = {
              'root': {
                  name: 'root',
                  subcategories: [],
                  filters: [],
                  attribute_ids: [],
                  sources: { category: [] }
              }
          };
          this.attributeData = {}; // Clearing here is OK for a new backend.
      }
  
      this.loadedVersion = versionSlug || 'Working Version';
      this.currentVersionDescription = 'Newly created taxonomy. Save changes to persist.';
      this.saveVersion = this.loadedVersion;
  
      this.topLevelNodes = this.getTopLevelNodes(this.taxonomyData).filter(key => 
        !['_metadata', 'name', 'url', 'total_items', 'attribute_ids'].includes(key)
      );
      if (this.topLevelNodes.length > 0) {
          this.selectedTopLevelNode = this.topLevelNodes[0];
          this.loadTopLevelNode();
      } else {
          this.treeItems = [];
      }
      this.gatherAllFilters();
  
      this.showSnackbar('Created new empty taxonomy. Please save your changes.', 'info');
  
      setTimeout(() => {
          this.trackChanges = true;
          this.hasUnsavedChanges = true;
      }, 500);
    },

    // Refine `convertToTreeItems` and `createItemFromData` to handle new node types
    convertToTreeItems(data, parentId = '') {
      if (!data || typeof data !== 'object') {
        const label = (data && data.name) || parentId || 'New Node';
        return [{ id: parentId, label: label, children: [], filters: [], isDerived: false, className: '', sources: {} }];
      }

      // `createItemFromData` is now the single source of truth for creating visual nodes.
      const root = this.createItemFromData(data, parentId || 'root');

      // The method expects an array to be returned.
      return [root];
    },
    createItemFromData(data, itemId) {
      if (!data || typeof data !== 'object') return null;

      const filters = data.attribute_ids || data.filters || [];
      
      let className = '';
      // --- FIX for Node Styling ---
      // This logic now handles all views correctly.
      if (this.currentView === 'frontendTaxonomyEditor') {
        // Frontend-specific styling based on rule type
        if (data.type === 'conditional') {
            className = 'node-derived-composite';
        } else if (data.backend_source_path || data.type === 'linked_root' || data.type === 'linked_category') {
            className = 'node-derived';
        } else if (data.type === 'manual_node' && filters.length > 0) {
            className = 'node-with-attributes';
        }
      } 
      // For the backend editor, the logic is simple: style if it has attributes.
      else if (this.currentView === 'backendTaxonomyEditor') {
        if (filters.length > 0) {
            className = 'node-with-attributes'; // This provides the blue style
        }
      }

      const item = {
        id: data.id || itemId,
        label: data.name || itemId || 'Unnamed Node',
        children: [],
        filters: filters,
        className: className,
        isDerived: data.isDerived,
        rule_details: data.derivation_rule || null,
        type: data.type,
        backend_source_path: data.backend_source_path,
        // --- FIX for Node Sources ---
        // Pass the 'sources' object directly to the visual node.
        // This makes the lookup for the source chips direct and reliable.
        sources: data.sources || {},
        notes: data.notes || '' 
      };

      // Recursively build children
      const childrenSource = data.subcategories || data.children;
      if (Array.isArray(childrenSource)) {
        childrenSource.forEach((child, idx) => {
          const childId = child.id || `${item.id}.[${idx}]`;
          const childItem = this.createItemFromData(child, childId);
          if (childItem) item.children.push(childItem);
        });
      }
      return item;
    },
    
    // The `buildIdToNodeMap` should also be adjusted to map rule IDs when in rule builder mode,
    // and generated node IDs when in preview mode.
    buildIdToNodeMap(data, parentId = '') {
      if (!data || typeof data !== 'object') return;
  
      // CRITICAL FIX: The ID for the map key MUST come from the node's own 'id' property.
      // The parentId is only a fallback for legacy data that might not have an id.
      const itemId = data.id || parentId;
      if (!itemId) {
          console.error("Cannot map a node without an ID:", data);
          return;
      }
      this.nodeIdMap[itemId] = data;
  
      const childrenSource = data.subcategories || data.children;
      if (Array.isArray(childrenSource)) {
          childrenSource.forEach((child) => {
              // The recursive call does not need to construct a path-based ID.
              // It will pick up the child's own `id` property.
              this.buildIdToNodeMap(child); 
          });
      }
    },

    // In TaxonomyEditor.js -> methods
    frontendRulesAsTree() {
      const rootNode = {
          id: 'frontend_root_rule',
          name: 'Frontend Rules',
          label: 'Frontend Rules',
          children: [],
          className: 'node-derived-composite', // Style for the root of the rules
          isRuleNode: true,
          type: 'root'
      };

      if (this.frontendRules && Array.isArray(this.frontendRules.categories)) {
          // Helper function to build nodes recursively, ensuring children are also processed
          const buildRuleNodes = (rules) => {
              return rules.map(rule => {
                  const ruleNode = {
                      // --- FIX: Use the rule's actual ID for stability ---
                      id: rule.id,
                      name: rule.name || `Unnamed Rule (${rule.type})`,
                      label: rule.name || `Unnamed Rule (${rule.type})`,
                      children: [],
                      rule_details: rule, // Pass the full rule for the inspector/editor
                      isRuleNode: true,
                      type: rule.type, // Pass the type for styling and context menus
                      className: rule.type === 'conditional' ? 'node-derived-composite' :
                                (rule.type === 'linked_root' || rule.type === 'linked_category') ? 'node-derived' :
                                (rule.type === 'manual_node' ? 'node-with-attributes' : '')
                  };

                  // Recursively process children of the current rule
                  if (rule.children && Array.isArray(rule.children)) {
                      ruleNode.children = buildRuleNodes(rule.children);
                  }

                  return ruleNode;
              });
          };

          rootNode.children = buildRuleNodes(this.frontendRules.categories);
      }

      // The method must return an object with a single top-level key
      // for the tree component to render it correctly.
      return { 'Frontend Rules': rootNode };
    },
    attributeItemsForRuleBuilder() { // This will be passed to RuleBuilderDialog
      if (!this.attributeData) return [];
      return Object.values(this.attributeData)
        .map(attr => ({
          id: attr.attribute_id,
          name: attr.display_name?.en || attr.name || `Unnamed ${attr.attribute_id}`,
          // Also useful for backend category selection later: backend category paths.
          // This would require pre-processing the backendTaxonomyData.
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    copyNodeForFrontend(nodeId) {
      // Safety check: This action should only be available in the Backend Taxonomy Editor view.
      if (this.currentView !== 'backendTaxonomyEditor') return;

      const nodeToCopy = this.nodeIdMap[nodeId];

      if (nodeToCopy) {
        // Create a deep copy of the node object to prevent any reference issues.
        // This captures the node, its children, and all its attribute_ids.
        this.nodeClipboard = JSON.parse(JSON.stringify(nodeToCopy));
        this.showSnackbar(`Node "${nodeToCopy.name}" copied.`, 'info');
      } else {
        this.showSnackbar('Error: Could not find node data to copy.', 'error');
      }
    },
    
    pasteNodeToFrontend(targetNodeId) {
      // Safety checks: Must be in the Frontend Taxonomy Editor view and have something on the clipboard.
      if (this.currentView !== 'frontendTaxonomyEditor' || !this.nodeClipboard) {
        return;
      }

      const targetNode = this.nodeIdMap[targetNodeId];

      if (targetNode) {
        // Ensure the target node has a subcategories array to push to.
        if (!Array.isArray(targetNode.subcategories)) {
          targetNode.subcategories = [];
        }

        // Push the copied node data into the target's children.
        const pastedNodeData = JSON.parse(JSON.stringify(this.nodeClipboard));
        targetNode.subcategories.push(pastedNodeData);

        // Rebuild the tree view to show the newly added node.
        this.loadTopLevelNode();

        // Mark that there are changes to be saved.
        this.hasUnsavedChanges = true;

        // If the node we pasted into is the currently selected node,
        // we need to refresh the attribute view on the right.
        if (this.selectedNodeId === targetNodeId) {
          this.refreshSelectedNodeFilters();
        }

        this.showSnackbar(`Pasted node "${pastedNodeData.name}" under "${targetNode.name}".`, 'success');
      } else {
        this.showSnackbar('Error: Could not find the target node for pasting.', 'error');
      }
    },

    selectDerivedAttribute(attribute) {
      // This method now calls our new rule-based creation logic
      const parentNodeId = this.attributeSearchDialog.nodeId;
      this.addNodesFromAttributeRule(attribute, parentNodeId);
    },
    openAttributeSearchDialog(nodeId) {
      // We now store the parent node's ID, which could be null for the root
      this.attributeSearchDialog.nodeId = nodeId; 
      this.attributeSearchDialog.search = '';
      this.filterDerivedAttributes(); // This remains the same
      this.attributeSearchDialog.show = true;
    },
    
    filterDerivedAttributes() {
      const allAttributes = Object.values(this.attributeData);
      // --- DEBUGGING LINE ADDED ---
      console.log(`[DEBUG] Total attributes found in attributeData: ${allAttributes.length}`);

      const closedListAttributes = allAttributes.filter(attr => attr.is_closed_list === true);
      // --- DEBUGGING LINE ADDED ---
      console.log(`%c[DEBUG] Found ${closedListAttributes.length} attributes with "is_closed_list: true"`, 'color: blue; font-weight: bold;');
      
      if(closedListAttributes.length === 0) {
          console.warn(`[DEBUG] The selection list will be empty because no attributes are configured with a closed list of values.`);
      }

      if (this.attributeSearchDialog.search) {
          this.attributeSearchDialog.filteredAttributes = closedListAttributes.filter(attr =>
              attr.name.toLowerCase().includes(this.attributeSearchDialog.search.toLowerCase())
          );
      } else {
          this.attributeSearchDialog.filteredAttributes = closedListAttributes;
      }
    },
    openResetDialog() {
      this.resetDialog = true;
    },
    resetTaxonomy() {
      this.resetDialog = false; // Close the dialog
      this.trackChanges = false; // Prevent watchers from firing prematurely
  
      // 1. Reset the category tree data structure
      this.taxonomyData = {
        'root': {
          name: 'root',
          subcategories: [],
          filters: [],
          attribute_ids: [],
          sources: { category: [] }
        }
      };
  
      // 2. Reset the state of the editor UI
      this.selectedNode = null;
      this.selectedNodeId = null;
      this.editableFilters = {};
      this.editableFilterNames = {};
      this.expandedFilters = [];
      this.expandedNodes = new Set();
        
      // Clear all attribute-related helpers
      this.allFiltersMap = {};
      this.allFilterNames = [];
      this.addExistingFilterName = '';
  
      // 3. Re-initialize the tree view from the new data
      this.topLevelNodes = this.getTopLevelNodes(this.taxonomyData);
      if (this.topLevelNodes.length > 0) {
        this.selectedTopLevelNode = this.topLevelNodes[0];
        this.loadTopLevelNode(); // This will rebuild treeItems and nodeIdMap
      } else {
        this.treeItems = [];
      }
      this.gatherAllFilters(); // Rebuild the attribute dropdowns from the (preserved) attributeData
  
      // 4. Show feedback and enable saving
      this.showSnackbar('Category taxonomy has been reset. Attributes are preserved.', 'info');
        
      this.$nextTick(() => {
        this.trackChanges = true;
        this.hasUnsavedChanges = true;
      });
    },

    // NEW METHOD: Creates a blank slate for a new frontend taxonomy
    initializeEmptyTaxonomy(baseName, versionSlug) {
        this.trackChanges = false;
        this.hasUnsavedChanges = false;

        this.taxonomyData = {
          "Frontend Root": {
            "name": "Frontend Root",
            "subcategories": [],
            "filters": [],
            "attribute_ids": [],
            "sources": { "category": [] }
          }
        };
        // this.attributeData = {};
        this.loadedVersion = versionSlug || 'Working Version';
        this.currentVersionDescription = 'Newly created frontend taxonomy. Save changes to persist.';
        this.saveVersion = this.loadedVersion;
        
        this.topLevelNodes = this.getTopLevelNodes(this.taxonomyData);
        if (this.topLevelNodes.length > 0) {
          this.selectedTopLevelNode = this.topLevelNodes[0];
          this.loadTopLevelNode();
        } else {
          this.treeItems = [];
        }
        this.gatherAllFilters();

        this.showSnackbar('Created new empty taxonomy. Please save your changes.', 'info');
        
        // Enable tracking and mark as dirty so the user is prompted to save
        setTimeout(() => {
          this.trackChanges = true;
          this.hasUnsavedChanges = true;
        }, 500);
    },

// Initialize GPC data when component mounts

parseBackupTimestamp(timestampStr) {
  try {
    console.log('Parsing timestamp:', timestampStr); // DEBUG
    
    // Check if it's our standard ISO format with dashes
    const match = timestampStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z$/);

    if (match) {
      const [_, year, month, day, hour, minute, second, ms] = match;
      // Build date string in standard format for parsing
      const dateStr = `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}Z`;
      console.log('Converted to standard ISO:', dateStr); // DEBUG
      const date = new Date(dateStr);
      
      // Verify the date is valid
      if (!isNaN(date.getTime())) {
        console.log('Successfully parsed date:', date); // DEBUG
        return date;
      }
    }

    // Try parsing other common timestamp formats
    const alternativeFormats = [
      // ISO with colons (if someone manually created one)
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})Z$/,
      // ISO without milliseconds
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})Z$/,
    ];

    for (const format of alternativeFormats) {
      const match = timestampStr.match(format);
      if (match) {
        console.log('Matched alternative format:', format.source); // DEBUG
        // Convert to standard ISO format and parse
        let isoString;
        if (format.source.includes(':')) {
          // Already has colons
          isoString = timestampStr;
        } else {
          // Convert dashes to colons
          isoString = timestampStr.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
        }
        
        console.log('Trying to parse:', isoString); // DEBUG
        const date = new Date(isoString);
        if (!isNaN(date.getTime())) {
          console.log('Successfully parsed with alternative format:', date); // DEBUG
          return date;
        }
      }
    }

    // Last resort: try direct parsing
    console.log('Trying direct Date parsing'); // DEBUG
    const date = new Date(timestampStr);
    if (!isNaN(date.getTime())) {
      console.log('Direct parsing worked:', date); // DEBUG
      return date;
    }

    console.log('All parsing attempts failed'); // DEBUG
    return null;
  } catch (e) {
    console.error('Error parsing backup timestamp:', e);
    return null;
  }
},
    formatBackupTimestamp(timestampStr) {
      try {
        // Parse ISO timestamp format: 2025-06-11T14-14-29-634Z
        const match = timestampStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z$/);
        
        if (match) {
          const [_, year, month, day, hour, minute, second, ms] = match;
          
          // Convert to standard ISO format for Date parsing
          const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}Z`;
          const date = new Date(isoString);
          
          if (!isNaN(date.getTime())) {
            // Format as "Jun 11, 2025 at 2:14 PM"
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                              "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            
            const monthName = monthNames[date.getMonth()];
            const dayNum = date.getDate();
            const yearNum = date.getFullYear();
            
            // Convert to 12-hour format
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // 0 should be 12
            
            return `${monthName} ${dayNum}, ${yearNum} at ${hours}:${minutes} ${ampm}`;
          }
        }
        
        // Fallback: try to extract just the date
        const dateMatch = timestampStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const [_, year, month, day] = dateMatch;
          return `${year}-${month}-${day}`;
        }
        
        // Last resort: return the original timestamp
        return timestampStr;
        
      } catch (error) {
        console.error('Error formatting backup timestamp:', error);
        return timestampStr;
      }
    },

    async initializeGpcMapping() {
      console.log('🔄 Initializing GPC mapping system...');
      this.gpcDataLoading = true;
      this.gpcError = null;

      // Add guard clauses for critical dependencies
      if (!this.selectedTaxonomy || !this.loadedVersion) {
        console.warn('GPC mapping init skipped: Taxonomy not fully loaded (missing selectedTaxonomy or loadedVersion).');
        this.gpcDataLoading = false;
        return;
      }

      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      const version = this.loadedVersion; // Use loadedVersion directly

      try {
        console.log('📋 Current state for GPC init:', {
          selectedTaxonomy: baseFilename,
          loadedVersion: version,
          taxonomyDataKeys: Object.keys(this.taxonomyData).length
        });

        // Step 1: Load GPC bricks data
        console.log('📦 Loading GPC bricks...');
        await this.loadGpcBricksData(); // This does not depend on selectedTaxonomy/loadedVersion
        console.log(`✅ GPC bricks loaded: ${this.gpcBricksData.length} bricks`);

        // Step 2: Load backend categories (will auto-generate if none exist)
        console.log('📁 Loading backend categories...');
        // Pass parameters explicitly for robustness
        await this.loadBackendCategories(baseFilename, version);
        console.log(`✅ Backend categories loaded: ${this.backendCategoriesData.length} categories`);

        // Step 3: If no backend categories exist and we have taxonomy data, generate them
        if (this.backendCategoriesData.length === 0 && Object.keys(this.taxonomyData).length > 0) {
          console.log('🔄 No backend categories found, generating from taxonomy...');
          // Pass parameters explicitly
          await this.generateBackendCategoriesFromTaxonomy(baseFilename, version);
          console.log(`✅ Generated ${this.backendCategoriesData.length} backend categories`);
        }

        // Step 4: Load existing GPC mappings
        console.log('🔗 Loading GPC mappings...');
        // Pass parameters explicitly
        await this.loadGpcMappings(baseFilename, version);
        console.log(`✅ GPC mappings loaded: ${this.gpcMappings.length} mappings`);

        // Step 5: Update brick mapped categories
        console.log('🔄 Updating brick mapped categories...');
        this.updateBrickMappedCategories();
        console.log('✅ Brick mapped categories updated');

        console.log('🎉 GPC mapping system initialized successfully!');

      } catch (error) {
        console.error('❌ Failed to initialize GPC mapping:', error);
        this.gpcError = error.message;
        this.showSnackbar(`Failed to initialize GPC system: ${error.message}`, 'error');
      } finally {
        this.gpcDataLoading = false;
      }
    },

    // Initialize GPC mapping with auto-suggestions
    async initializeGpcMappingWithAutoSuggestions() {
      await this.initializeGpcMapping();
      
      // If no mappings exist after initialization, generate auto-suggestions
      if (this.gpcMappings.length === 0 && this.gpcBricksData.length > 0) {
        console.log('🎯 No mappings found, skipping auto-suggestions for now...');
        // TODO: Implement generateAutoMappingSuggestions function
        // await this.generateAutoMappingSuggestions();
      }
    },

  // Load GPC bricks data - ABSOLUTELY NO DEFAULTS
  async loadGpcBricksData() {
    try {
      const response = await fetch(`${this.FUNCTION_URL}?action=loadGpcBricks`);
      const data = await response.json();
      
      if (response.ok) {
        this.gpcBricksData = data.data || [];
        this.gpcBricksLoaded = true;
        
        if (this.gpcBricksData.length === 0) {
          console.warn('No GPC bricks data available');
        }
      } else if (response.status === 404) {
        // No GPC data uploaded yet - this is expected on first run
        this.gpcBricksData = []; // Empty array, no defaults!
        this.gpcBricksLoaded = true;
        console.info('No GPC bricks data found - please upload GPC data first');
      } else {
        throw new Error(data.error || `Failed to load GPC bricks: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to load GPC bricks data:', error);
      this.gpcBricksData = []; // Empty array, no defaults!
      this.gpcBricksLoaded = false;
      throw error;
    }
  },

  // Load existing GPC mappings
  async loadGpcMappings(baseFilename, version) { // <-- UPDATED SIGNATURE
    // Temporarily disable change tracking to prevent watcher from marking as unsaved
    const wasTracking = this.trackChanges;
    this.trackChanges = false;
    
    if (!baseFilename || !version) { // <-- UPDATED GUARD
      this.gpcMappings = [];
      // Wait for Vue to flush watchers before restoring trackChanges
      await this.$nextTick();
      this.trackChanges = wasTracking;
      return;
    }
    
    try {
      const response = await fetch(
        `${this.FUNCTION_URL}?action=loadGpcMappings&filename=${baseFilename}&version=${version}`
      );
      
      if (response.ok) {
        const data = await response.json();
        this.gpcMappings = data.data || [];
        // Store loaded timestamp for conflict detection
        if (data.updatedAt) {
          this.loadedTimestamps.gpcMappings = data.updatedAt;
        }
        this.updateBrickMappedCategories();
      } else {
        throw new Error(`Failed to load mappings: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to load GPC mappings:', error);
      this.gpcMappings = [];
      throw error;
    } finally {
      // Wait for Vue to flush watchers before restoring trackChanges
      // This is critical because Vue batches watcher execution asynchronously
      await this.$nextTick();
      this.trackChanges = wasTracking;
    }
  },
  
  async loadBackendCategories(baseFilename, version) { // <-- UPDATED SIGNATURE
    if (!baseFilename || !version) { // <-- UPDATED GUARD
      console.log('⚠️ No taxonomy selected or version specified, clearing backend categories');
      this.backendCategoriesData = [];
      return;
    }
  
    console.log(`📁 Loading backend categories for ${baseFilename}, version: ${version}`);
  
    try {
      const response = await fetch(
        `${this.FUNCTION_URL}?action=loadBackendCategories&filename=${baseFilename}&version=${version}`
      );
      
      if (response.ok) {
        const data = await response.json();
        this.backendCategoriesData = data.data || [];
        
        console.log(`✅ Backend categories response:`, {
          count: this.backendCategoriesData.length,
          source: data.metadata?.source,
          sampleCategories: this.backendCategoriesData.slice(0, 3)
        });
        
        // Force brick ID mapping recalculation
        this.$nextTick(() => {
          console.log('🔄 Manually forcing topLevelNodesWithBrickIds recalculation...');
          const nodes = this.topLevelNodesWithBrickIds;
          console.log('📊 Manual recalc result:', nodes.length, 'nodes processed');
        });
        
        if (data.metadata?.source === 'auto-generated') {
          this.showSnackbar('Backend categories auto-generated from taxonomy', 'info');
        }
        
      } else {
        console.error('❌ Failed to load backend categories:', response.status, response.statusText);
        this.backendCategoriesData = [];
      }
    } catch (error) {
      console.error('❌ Error loading backend categories:', error);
      this.backendCategoriesData = [];
      throw error;
    }
  },
  

  // Enhanced generateBackendCategoriesFromTaxonomy with better logging
  // In methods: { ... }
  async generateBackendCategoriesFromTaxonomy(baseFilename, version) { // <-- UPDATED SIGNATURE
    if (!baseFilename || !version) { // <-- UPDATED GUARD
      console.warn('Skipping category generation: No filename or version provided.');
      return;
    }
    if (Object.keys(this.taxonomyData || {}).length === 0) {
      console.warn('Skipping category generation: Taxonomy data is empty.');
      return;
    }
  
    console.log(`🔄 Generating backend categories from taxonomy:`, {
      filename: baseFilename,
      version: version,
    });
  
    try {
      const response = await fetch(
        `${this.FUNCTION_URL}?action=generateBackendCategories`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: baseFilename,
            version: version
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        this.backendCategoriesData = data.data || [];
        
        console.log(`✅ Generated backend categories:`, {
          count: this.backendCategoriesData.length,
        });
        
        this.showSnackbar(`Synced ${this.backendCategoriesData.length} backend categories from taxonomy`, 'success');
      } else {
        const errorPayload = await response.json();
        const errorText = errorPayload.error || `Request failed with status ${response.status}`;
        console.error('❌ Generate categories failed:', response.status, errorText);
        throw new Error(`Failed to generate categories: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Failed to generate backend categories:', error);
      this.showSnackbar(`Failed to sync backend categories: ${error.message}`, 'error');
    }
  },
  // Save GPC mappings to cloud storage
  async saveGpcMappings() {
    if (!this.selectedTaxonomy) {
      this.showSnackbar('No taxonomy selected', 'error')
      return
    }

    const baseFilename = this.selectedTaxonomy.replace('.json', '')
    const version = this.loadedVersion || 'Working Version'

    const payload = {
      filename: baseFilename,
      version,
      mappings: this.gpcMappings,
      metadata: {
        description: `GPC mappings for ${baseFilename}`,
        lastUpdated: new Date().toISOString(),
        taxonomyVersion: this.loadedVersion
      }
    }

    try {
      const response = await fetch(`${this.FUNCTION_URL}?action=saveGpcMappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        this.showSnackbar('GPC mappings saved successfully', 'success')
        this.hasUnsavedChanges = false
      } else {
        throw new Error(`Save failed with status ${response.status}`)
      }
    } catch (error) {
      console.error('Failed to save GPC mappings:', error)
      this.showSnackbar('Failed to save GPC mappings', 'error')
    }
  },

  // Export mappings to CSV
  exportGpcMappings() {
    if (!this.gpcMappings.length) {
      this.showSnackbar('No mappings to export', 'warning')
      return
    }

    const headers = ['Backend Category', 'GPC Bricks', 'Brick Codes', 'Mapping ID']
    const rows = this.gpcMappings.map((mapping, index) => [
      mapping.backendCategory || '', // FIXED: was webCategory
      mapping.gpcBricks.join('; '),
      mapping.gpcBricks.map(brick => {
        const match = brick.match(/^(\d+)/)
        return match ? match[1] : ''
      }).join('; '),
      mapping.id || index + 1
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `gpc-mappings-${this.selectedTaxonomy.replace('.json', '')}-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)

    this.showSnackbar('GPC mappings exported successfully', 'success')
  },

// Import mappings from CSV
async importGpcMappings(file) {
  try {
    const text = await file.text()
    const lines = text.split('\n').filter(line => line.trim())
    
    if (lines.length < 2) {
      throw new Error('CSV file appears to be empty or invalid')
    }

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim())
    const mappings = []

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim())
      
      if (values.length >= 2 && values[0] && values[1]) {
        // Process brick codes/strings
        const rawBricks = values[1].split(';').map(b => b.trim()).filter(b => b)
        const processedBricks = rawBricks.map(brick => {
          // Check if it's already a full string (contains " - ")
          if (brick.includes(' - ')) {
            return brick
          }
          
          // Otherwise, treat as numeric code and look up the title
          const brickCode = brick.replace(/[^0-9]/g, '') // Extract only numbers
          if (brickCode) {
            const brickData = this.gpcBricksData.find(b => b.code === brickCode)
            if (brickData) {
              return `${brickCode} - ${brickData.title}`
            } else {
              // Brick code not found in data, use placeholder
              return `${brickCode} - Unknown Brick`
            }
          }
          return brick // Fallback to original if no code found
        })

        mappings.push({
          id: mappings.length + 1,
          backendCategory: values[0],
          gpcBricks: processedBricks
        })
      }
    }

    if (mappings.length === 0) {
      throw new Error('No valid mappings found in CSV file')
    }

    this.gpcMappings = mappings
    this.updateBrickMappedCategories()
    this.hasUnsavedChanges = true
    this.showSnackbar(`Imported ${mappings.length} GPC mappings`, 'success')
  } catch (error) {
    console.error('Failed to import CSV:', error)
    this.showSnackbar(`Failed to import CSV: ${error.message}`, 'error')
  }
},

  // Update brick mapped categories when mappings change
  updateBrickMappedCategories() {
    // Clear existing mapped categories
    this.gpcBricksData.forEach(brick => {
      brick.mappedCategories = []
    })

    // Update with current mappings
    this.gpcMappings.forEach(mapping => {
      if (mapping.backendCategory && mapping.gpcBricks.length > 0) { // FIXED: was webCategory
        mapping.gpcBricks.forEach(brickString => {
          const brickCode = brickString.match(/^(\d+)/)
          if (brickCode) {
            const brick = this.gpcBricksData.find(b => b.code === brickCode[1])
            if (brick && !brick.mappedCategories.includes(mapping.backendCategory)) { // FIXED: was webCategory
              brick.mappedCategories.push(mapping.backendCategory) // FIXED: was webCategory
            }
          }
        })
      }
    })
  },

  // Handle GPC mapping changes from child component
  onGpcMappingChanged(mappings) {
    this.gpcMappings = mappings
    this.updateBrickMappedCategories()
    this.hasUnsavedChanges = true
  },

  // Check if GPC system is ready to use
  isGpcSystemReady() {
    return this.gpcBricksLoaded && this.gpcBricksData.length > 0;
  },

// Enhanced getGpcSystemStatus with more detailed status info
getGpcSystemStatus() {
    if (this.gpcDataLoading) {
      return { type: 'loading', message: 'Loading GPC data...' };
    }
    
    if (this.gpcError) {
      return { type: 'error', message: `Error loading GPC data: ${this.gpcError}` };
    }
    
    if (!this.gpcBricksLoaded) {
      return { type: 'error', message: 'Failed to load GPC bricks data' };
    }
    
    if (this.gpcBricksData.length === 0) {
      return { 
        type: 'warning', 
        message: 'No GPC bricks data available. Please upload GPC data to use mapping features.',
        showUpload: true
      };
    }
  
    if (this.backendCategoriesData.length === 0) {
      return {
        type: 'warning',
        message: 'No backend categories available. Click "Sync Categories from Taxonomy" to generate them.',
        showGenerate: true
      };
    }
    
    return { 
      type: 'success', 
      message: `System ready: ${this.gpcBricksData.length} GPC bricks, ${this.backendCategoriesData.length} backend categories, ${this.gpcMappings.length} mappings` 
    };
  },

  // Upload GPC bricks data (for admin)
  async uploadGpcBricksData(file) {
    try {
      const text = await file.text();
      const bricksData = JSON.parse(text);
      
      if (!Array.isArray(bricksData)) {
        throw new Error('GPC data must be an array of brick objects');
      }
      
      // Validate structure
      const requiredFields = ['code', 'title', 'segment', 'family', 'class'];
      for (const brick of bricksData.slice(0, 5)) { // Check first 5 items
        for (const field of requiredFields) {
          if (!brick[field]) {
            throw new Error(`Invalid GPC data: missing '${field}' field`);
          }
        }
      }

      const response = await fetch(`${this.FUNCTION_URL}?action=saveGpcBricks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bricks: bricksData,
          metadata: {
            uploadedAt: new Date().toISOString(),
            totalBricks: bricksData.length,
            source: 'admin-upload'
          }
        })
      });

      if (response.ok) {
        this.showSnackbar(`Uploaded ${bricksData.length} GPC bricks successfully`, 'success');
        await this.loadGpcBricksData(); // Reload data
      } else {
        throw new Error(`Upload failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to upload GPC bricks:', error);
      this.showSnackbar(`Failed to upload GPC bricks: ${error.message}`, 'error');
    }
  },
  
  // Helper to collect category paths recursively
  collectCategoryPaths(node, currentPath, categories) {
    if (!node || typeof node !== 'object') return
    
    const nodeName = node.name || 'Unnamed'
    const fullPath = [...currentPath, nodeName]
    const pathString = fullPath.join(' > ')
    
    categories.push(pathString)
    
    // Process subcategories
    if (Array.isArray(node.subcategories)) {
      node.subcategories.forEach(sub => {
        this.collectCategoryPaths(sub, fullPath, categories)
      })
    }
    
    // Process other object children
    Object.keys(node).forEach(key => {
      if (!this.keysToSkip.includes(key) && 
          key !== 'subcategories' && 
          key !== 'filters' && 
          key !== 'sources' && 
          key !== 'name') {
        const child = node[key]
        if (child && typeof child === 'object') {
          this.collectCategoryPaths(child, fullPath, categories)
        }
      }
    })
  },
  normalizeFiltersRecursively(node, attributeData) { // <-- Pass in the data
    if (!node || typeof node !== 'object') return;
    
    // Sync filters and attribute_ids
    if (Array.isArray(node.attribute_ids)) {
      node.filters = [...node.attribute_ids];
    } else if (Array.isArray(node.filters)) {
      node.attribute_ids = [...node.filters];
    }

    // Clean up any references to attributes that no longer exist
    if (Array.isArray(node.filters)) {
      node.filters = node.filters.filter(id => attributeData[id]); // <-- Use the passed-in data
    }
    if (Array.isArray(node.attribute_ids)) {
      node.attribute_ids = node.attribute_ids.filter(id => attributeData[id]); // <-- Use the passed-in data
    }
    
    // Recurse through subcategories
    if (Array.isArray(node.subcategories)) {
      node.subcategories.forEach(sub => this.normalizeFiltersRecursively(sub, attributeData)); // <-- Pass it down
    }

    // Recurse through other object children (classic schema)
    Object.entries(node).forEach(([k, v]) => {
      if (
        ['name', 'url', 'total_items', 'filters', 'attribute_ids', 'subcategories', 'sources'].includes(k)
      ) return;
      if (v && typeof v === 'object') this.normalizeFiltersRecursively(v, attributeData); // <-- Pass it down
    });
  },
  openCategoryNotesDialog(nodeId) {
    const nodeData = this.nodeIdMap[nodeId];
    if (!nodeData) {
      this.showSnackbar('Node data not found', 'error');
      return;
    }

    // Initialize notes property if it doesn't exist
    if (!nodeData.notes) {
      nodeData.notes = '';
    }

    this.categoryNotesDialog.nodeId = nodeId;
    this.categoryNotesDialog.nodeName = nodeData.name || 'Unnamed Node';
    this.categoryNotesDialog.notes = nodeData.notes;
    this.categoryNotesDialog.tempNotes = nodeData.notes;
    this.categoryNotesDialog.show = true;
  },

  saveCategoryNotes() {
    const nodeData = this.nodeIdMap[this.categoryNotesDialog.nodeId];
    if (!nodeData) {
      this.showSnackbar('Error saving notes: Node not found', 'error');
      return;
    }

    // Save the notes to the node data
    nodeData.notes = this.categoryNotesDialog.tempNotes;
    
    this.hasUnsavedChanges = true;
    this.categoryNotesDialog.show = false;
    
    // Show feedback
    const preview = this.categoryNotesDialog.tempNotes 
      ? (this.categoryNotesDialog.tempNotes.substring(0, 50) + 
         (this.categoryNotesDialog.tempNotes.length > 50 ? '...' : ''))
      : 'Notes cleared';
    this.showSnackbar(`Notes saved: ${preview}`, 'success');

    // Update tree to show note indicator
    this.$nextTick(() => {
      this.loadTopLevelNode();
    });
  },

  closeCategoryNotesDialog() {
    this.categoryNotesDialog.show = false;
    this.categoryNotesDialog.tempNotes = '';
  },

  // Check if a node has notes (for visual indicators)
  nodeHasNotes(nodeId) {
    const nodeData = this.nodeIdMap[nodeId];
    return nodeData && nodeData.notes && nodeData.notes.trim().length > 0;
  },
    onExpandChange(event, nodeBefore, nodeAfter) {
      const node = nodeAfter; // Use the node after the expansion change
      if (node.expand) {
        this.expandedNodes.add(node.id); // Add node ID to the set if expanded
      } else {
        this.expandedNodes.delete(node.id); // Remove node ID if collapsed
      }
    },
    applyExpansionState(node) {
      if (this.expandedNodes.has(node.id)) {
        node.expand = true; // Adjust this property based on your tree component
      } else {
        node.expand = false;
      }
      if (node.children) {
        node.children.forEach(child => this.applyExpansionState(child));
      }
    },
    preserveTreePosition(callback) {
      // Save current scroll position
      const container = this.$el.querySelector('.tree-scroll-container');
      const scrollLeft = container ? container.scrollLeft : 0;
      const scrollTop = container ? container.scrollTop : 0;

      // Save current expansion state
      const savedExpansions = new Set(this.expandedNodes);

      // Execute the callback that modifies the tree
      callback();

      // Restore expansion state
      this.expandedNodes = savedExpansions;
      if (this.treeItems && this.treeItems.length > 0) {
        this.applyExpansionState(this.treeItems[0]);
      }

      // Restore scroll position after DOM update
      this.$nextTick(() => {
        if (container) {
          container.scrollLeft = scrollLeft;
          container.scrollTop = scrollTop;
        }
      });
    },
    removeNodeByPath(nodeId) {
      console.log('REMOVING NODE:', nodeId);

      // Parse the node path
      const segments = nodeId.split('.');
      if (segments.length < 1) {
        console.error('Invalid node path:', nodeId);
        return false;
      }

      // Get the top level node
      const topLevelKey = segments[0];
      const topLevelNode = this.taxonomyData[topLevelKey];
      if (!topLevelNode) {
        console.error('Top level node not found:', topLevelKey);
        return false;
      }

      // If it's a direct child of the top level
      if (segments.length === 2) {
        const segment = segments[1];
        if (segment.startsWith('[') && segment.endsWith(']')) {
          const index = parseInt(segment.slice(1, -1), 10);
          if (Array.isArray(topLevelNode.subcategories) &&
            index >= 0 &&
            index < topLevelNode.subcategories.length) {
            console.log('Removing direct child of top level at index:', index);
            console.log('Before:', topLevelNode.subcategories.length, 'children');
            topLevelNode.subcategories.splice(index, 1);
            console.log('After:', topLevelNode.subcategories.length, 'children');
            return true;
          }
        }
        return false;
      }

      // For deeper levels, we need to navigate to the parent
      let currentNode = topLevelNode;
      let path = [topLevelKey];

      // Navigate all but the last segment
      for (let i = 1; i < segments.length - 1; i++) {
        const segment = segments[i];
        path.push(segment);

        if (segment.startsWith('[') && segment.endsWith(']')) {
          const index = parseInt(segment.slice(1, -1), 10);
          if (Array.isArray(currentNode.subcategories) &&
            index >= 0 &&
            index < currentNode.subcategories.length) {
            currentNode = currentNode.subcategories[index];
          } else {
            console.error('Invalid path segment at index', i, segment, path.join('.'));
            return false;
          }
        } else {
          // For non-array indices (direct properties)
          if (typeof currentNode[segment] === 'object') {
            currentNode = currentNode[segment];
          } else {
            console.error('Invalid path segment at index', i, segment, path.join('.'));
            return false;
          }
        }
      }

      // Now we're at the parent node, remove the child specified by the last segment
      const lastSegment = segments[segments.length - 1];
      if (lastSegment.startsWith('[') && lastSegment.endsWith(']')) {
        const index = parseInt(lastSegment.slice(1, -1), 10);
        console.log('Removing from parent:', path.join('.'), 'child at index:', index);

        if (Array.isArray(currentNode.subcategories) &&
          index >= 0 &&
          index < currentNode.subcategories.length) {

          // Add extra debug info to see what's happening
          console.log('Parent before removal:', {
            hasSubcategories: !!currentNode.subcategories,
            subcategoriesLength: currentNode.subcategories.length,
            childToRemove: JSON.stringify(currentNode.subcategories[index].name)
          });

          // Actually remove the node
          currentNode.subcategories.splice(index, 1);

          console.log('Parent after removal:', {
            subcategoriesLength: currentNode.subcategories.length
          });

          return true;
        } else {
          console.error('Invalid last segment index:', index,
            'subcategories:', currentNode.subcategories ?
            currentNode.subcategories.length : 'none');
          return false;
        }
      } else {
        // For direct property access
        if (typeof currentNode[lastSegment] === 'object') {
          console.log('Deleting property:', lastSegment);
          delete currentNode[lastSegment];
          return true;
        } else {
          console.error('Invalid last segment (not an object):', lastSegment);
          return false;
        }
      }
    },
    onNodeDragStart(event, nodeInfo) {
      console.log('Drag start params:', event, nodeInfo);

      // Safely check what we actually received
      let nodeId = null;

      // Option 1: nodeInfo is the node object itself
      if (nodeInfo && typeof nodeInfo === 'object' && nodeInfo.id) {
        nodeId = nodeInfo.id;
      }
      // Option 2: nodeInfo contains a node property
      else if (nodeInfo && typeof nodeInfo === 'object' && nodeInfo.node && nodeInfo.node.id) {
        nodeId = nodeInfo.node.id;
      }
      // Option 3: event is actually a custom object with node info
      else if (event && typeof event === 'object' && event.id) {
        nodeId = event.id;
      }
      else if (event && typeof event === 'object' && event.node && event.node.id) {
        nodeId = event.node.id;
      }

      // If we couldn't find a node ID, we can't proceed
      if (!nodeId) {
        console.error('Could not determine node ID for dragging');
        return;
      }

      // Store the node being dragged
      this.draggedNodeId = nodeId;

      // Set dragging state and enable auto-scrolling
      this.isDragging = true;

      // Skip the drag image setup since event.target is undefined
      // We'll use simpler drag handling

      console.log(`Dragging node with ID: ${nodeId}`);
    },
    onNodeDragEnd(event, targetInfo) {
      console.log("DRAG END EVENT:", event, targetInfo);


      // Check if we have a valid target
      if (!targetInfo) {
        console.log("Drop occurred outside of a valid target node");
        this.draggedNodeId = null;
        return; // Exit early - nothing to do
      }

      // event is the source node, targetInfo is the target node
      const sourceNode = event;
      const targetNode = targetInfo;

      // Get IDs
      const sourceNodeId = sourceNode.id;
      const targetNodeId = targetNode.id;

      console.log("MOVE OPERATION:", sourceNodeId, "→", targetNodeId);

      if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) {
        console.log("Invalid move operation - same node or missing IDs");
        this.draggedNodeId = null;
        return;
      }

      // Prevent moving a node into itself or its descendants
      if (targetNodeId.startsWith(sourceNodeId)) {
        this.showSnackbar('Cannot move a node into itself or its children', 'error');
        this.draggedNodeId = null;
        return;
      }

      try {
        // Get node data objects from the nodeIdMap
        const sourceNodeData = this.nodeIdMap[sourceNodeId];
        const targetNodeData = this.nodeIdMap[targetNodeId];

        console.log("Source node data:", sourceNodeData);
        console.log("Target node data:", targetNodeData);

        if (!sourceNodeData || !targetNodeData) {
          throw new Error("Source or target node data not found");
        }

        // Create a copy of the source node
        const nodeCopy = JSON.parse(JSON.stringify(sourceNodeData));
        console.log("Created node copy:", nodeCopy);

        // Ensure target has subcategories array
        if (!targetNodeData.subcategories) {
          targetNodeData.subcategories = [];
        }

        // Add copy to target
        targetNodeData.subcategories.push(nodeCopy);
        console.log("Added to target, new subcategories length:", targetNodeData.subcategories.length);

        // Find parent of source node
        const segments = sourceNodeId.split('.');
        console.log("Path segments:", segments);

        // Handle different path structures
        let parentNode = null;
        let childIndex = -1;

        if (segments.length == 2) {
          // Direct child of top level node (e.g., "0.[1]")
          const topLevelKey = segments[0];
          parentNode = this.taxonomyData[topLevelKey];

          // Extract index from the second segment
          const lastSegment = segments[1];
          if (lastSegment.startsWith('[') && lastSegment.endsWith(']')) {
            childIndex = parseInt(lastSegment.slice(1, -1), 10);
          }
        } else if (segments.length >= 3) {
          // Deeper nested node (e.g., "0.[1].[2]")
          const parentPath = segments.slice(0, -1).join('.');
          parentNode = this.nodeIdMap[parentPath];

          // Extract index from the last segment
          const lastSegment = segments[segments.length - 1];
          if (lastSegment.startsWith('[') && lastSegment.endsWith(']')) {
            childIndex = parseInt(lastSegment.slice(1, -1), 10);
          }
        }

        console.log("Parent node:", parentNode);
        console.log("Child index:", childIndex);

        // Remove from parent if found
        if (parentNode && childIndex !== -1) {
          console.log("Before removal, subcategories length:",
            parentNode.subcategories ? parentNode.subcategories.length : 'none');

          if (Array.isArray(parentNode.subcategories) &&
            childIndex >= 0 &&
            childIndex < parentNode.subcategories.length) {
            // Remove the node at the specified index
            parentNode.subcategories.splice(childIndex, 1);
            console.log("After removal, subcategories length:", parentNode.subcategories.length);
          }
        }

        // Rebuild tree to reflect changes
        this.loadTopLevelNode();

        this.showSnackbar("Node moved successfully", "success");
      } catch (error) {
        console.error("Error moving node:", error);
        this.showSnackbar("Error: " + error.message, "error");
      }

      // Reset state
      this.draggedNodeId = null;
    },

    onNodeDragOver(event, nodeInfo) {
      // Extract target node ID safely
      let targetNodeId = null;

      if (nodeInfo && nodeInfo.id) {
        targetNodeId = nodeInfo.id;
      } else if (nodeInfo && nodeInfo.node && nodeInfo.node.id) {
        targetNodeId = nodeInfo.node.id;
      } else if (event && event.id) {
        targetNodeId = event.id;
      } else if (event && event.node && event.node.id) {
        targetNodeId = event.node.id;
      }

      // If we have both source and target IDs, and they're different
      if (this.draggedNodeId && targetNodeId && this.draggedNodeId !== targetNodeId) {
        // We can't use DOM methods if event.target is undefined
        // Instead, set a class in our component data and use it in the template
        this.highlightedNodeId = targetNodeId;
      }
    },

    onNodeDragLeave(event) {
      // Clear highlighted node
      this.highlightedNodeId = null;
    },
    async setWorkingVersion() {
      // Check if we have a selected taxonomy
      if (!this.selectedTaxonomy) {
        this.showSnackbar('No taxonomy selected', 'error');
        return;
      }

      // Normalize data objects before saving
      this.normalizeDataBeforePublish();

      // Generate timestamp for backup version
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const backupVersionName = `Working Version Backup ${timestamp}`;

      // First-time setup for Working Version and QA Version
      try {
        // Save a timestamped backup first
        await this.saveVersionToCloud(backupVersionName, `Initial backup created when setting up Working Version on ${new Date().toLocaleString()}`);

        // Save as Working Version
        const workingResult = await this.saveVersionToCloud('Working Version', 'Main working version');

        // Then save as QA Version
        const qaResult = await this.saveVersionToCloud('QA Version', 'Quality assurance version');

        if (workingResult && qaResult) {
          this.loadedVersion = 'Working Version';
          this.hasUnsavedChanges = false;
          this.showSnackbar('Successfully set up Working and QA Versions', 'success');

          // Refresh versions list and set dropdown to Working Version
          await this.loadTaxonomyVersions();
          this.loadVersion = 'Working Version';
        }
      } catch (error) {
        console.error('Failed to set working versions:', error);
        this.showSnackbar('Failed to set working versions: ' + error.message, 'error');
      }
    },
    
    // Sync QA Version from Working Version (for safe testing without backup spam)
    async syncQaVersion() {
      if (!this.selectedTaxonomy) {
        this.showSnackbar('No taxonomy selected', 'error');
        return;
      }
      
      // Confirm with user
      const confirmed = confirm(
        'This will overwrite QA Version with the current Working Version.\n\n' +
        'Use QA Version for testing to avoid creating backups.\n\n' +
        'Continue?'
      );
      if (!confirmed) return;
      
      try {
        // Store current state
        const currentLoadedVersion = this.loadedVersion;
        const hadUnsavedChanges = this.hasUnsavedChanges;
        
        // Load Working Version data (without changing UI state permanently)
        const baseFilename = this.selectedTaxonomy.replace('.json', '');
        const workingData = await this.tryRemoteLoad(
          baseFilename,
          `${baseFilename}_modified_Working Version.json`,
          `${baseFilename}_attributes_Working Version.json`,
          'Working Version'
        );
        
        if (!workingData.loaded) {
          this.showSnackbar('Working Version not found. Create it first using "Set Working Version".', 'error');
          return;
        }
        
        // Temporarily set the data to Working Version's data
        const originalTaxonomyData = this.taxonomyData;
        const originalAttributeData = this.attributeData;
        
        // Note: tryRemoteLoad returns modifiedData/attributesData, not taxonomyData/attributeData
        this.taxonomyData = workingData.data.modifiedData;
        this.attributeData = workingData.data.attributesData;
        
        // Save as QA Version (no backup created)
        await this.saveVersionToCloud('QA Version', `Synced from Working Version on ${new Date().toLocaleString()}`);
        
        // Also sync shared attributes: manor_shared_attributes → manor_shared_attributes_QA
        await this.syncSharedAttributesToQA();
        
        // Restore original data if we weren't on Working Version
        if (currentLoadedVersion !== 'Working Version') {
          this.taxonomyData = originalTaxonomyData;
          this.attributeData = originalAttributeData;
        }
        
        // Restore unsaved changes state
        this.hasUnsavedChanges = hadUnsavedChanges;
        
        this.showSnackbar('QA Version synced with Working Version', 'success');
        
        // Refresh versions list
        await this.loadTaxonomyVersions();
        
      } catch (error) {
        console.error('Failed to sync QA Version:', error);
        this.showSnackbar('Failed to sync QA Version: ' + error.message, 'error');
      }
    },
    
    // Sync shared attributes from Working Version to QA (manor_shared_attributes → manor_shared_attributes_QA)
    async syncSharedAttributesToQA() {
      try {
        // Call API to copy shared attributes to QA version
        const response = await fetch(`${this.FUNCTION_URL}?action=syncSharedAttributesToQA`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceVersion: 'Working Version',
            targetVersion: 'QA Version'
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.warn('Failed to sync shared attributes to QA:', errorText);
          // Don't throw - the taxonomy sync already succeeded
        } else {
          console.log('Shared attributes synced to QA successfully');
        }
      } catch (error) {
        console.warn('Failed to sync shared attributes to QA:', error.message);
        // Don't throw - the taxonomy sync already succeeded
      }
    },
    
    // Helper method to save a version to the cloud
    // skipConflictCheck: true for backup versions, false for main saves
    async saveVersionToCloud(versionName, description, skipConflictCheck = false) {
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      
      // ✅ FIX: Explicitly determine the editorType based on the filename
      const editorType = baseFilename === 'frontend' ? 'frontend' : 'backend';
      
      // Determine if this is the currently loaded version (needs conflict check)
      const isCurrentVersion = versionName === this.loadedVersion;
      const shouldCheckConflict = isCurrentVersion && !skipConflictCheck;
  
      const pathModified = `allium-ai-manor/taxonomies/${baseFilename}_modified_${versionName}.json`;
      const pathAttributes = `allium-ai-manor/taxonomies/${baseFilename}_attributes_${versionName}.json`;
  
      const metadata = {
          version: versionName,
          description: description || `Version ${versionName} created on ${new Date().toLocaleString()}`,
          createdAt: new Date().toISOString()
      };
  
      if (baseFilename === 'frontend' || this.selectedTaxonomy?.startsWith('frontend')) {
          const payloadRules = {
              path: pathModified,
              data: this.frontendRules,
              metadata: metadata,
              editorType: editorType,
              // Include expectedUpdatedAt for conflict checking on current version saves
              ...(shouldCheckConflict && { expectedUpdatedAt: this.loadedTimestamps.frontend })
          };
          // ✅ FIX: Add editorType to the fetch URL
          const resRules = await fetch(`${this.FUNCTION_URL}?action=save&editorType=${editorType}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payloadRules)
          });
          
          // Handle 409 Conflict from server
          if (resRules.status === 409) {
            const conflictData = await resRules.json();
            const error = new Error('Conflict detected by server');
            error.isConflict = true;
            error.conflictData = conflictData;
            error.dataType = 'frontend';
            throw error;
          }
  
          if (!resRules.ok) {
              throw new Error(`Save failed for frontend rules with status ${resRules.status}`);
          }
          const result = await resRules.json();
          
          // Update loaded timestamp with server's value
          if (shouldCheckConflict && result.updatedAt) {
            this.loadedTimestamps.frontend = result.updatedAt;
          }
          return true;
      }
  
      // Backend logic
      const payloadMod = {
          path: pathModified,
          data: this.taxonomyData,
          metadata: metadata,
          editorType: editorType,
          ...(shouldCheckConflict && { expectedUpdatedAt: this.loadedTimestamps.taxonomy })
      };
      const payloadAtt = {
          path: pathAttributes,
          data: this.attributeData,
          metadata: metadata,
          editorType: editorType,
          ...(shouldCheckConflict && { expectedUpdatedAt: this.loadedTimestamps.attributes })
      };
  
      const [resMod, resAtt] = await Promise.all([
          // ✅ FIX: Add editorType to the fetch URL
          fetch(`${this.FUNCTION_URL}?action=save&editorType=${editorType}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payloadMod)
          }),
          // ✅ FIX: Add editorType to the fetch URL
          fetch(`${this.FUNCTION_URL}?action=save&editorType=${editorType}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payloadAtt)
          })
      ]);
      
      // Check for 409 Conflicts from server
      if (resMod.status === 409) {
        const conflictData = await resMod.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        error.dataType = 'taxonomy';
        throw error;
      }
      if (resAtt.status === 409) {
        const conflictData = await resAtt.json();
        const error = new Error('Conflict detected by server');
        error.isConflict = true;
        error.conflictData = conflictData;
        error.dataType = 'attributes';
        throw error;
      }
  
      if (!resMod.ok || !resAtt.ok) {
          throw new Error(`Save failed with statuses ${resMod.status}, ${resAtt.status}`);
      }
      const [modResult, attResult] = await Promise.all([resMod.json(), resAtt.json()]);
      
      // Update loaded timestamps with server values
      if (shouldCheckConflict) {
        if (modResult.updatedAt) this.loadedTimestamps.taxonomy = modResult.updatedAt;
        if (attResult.updatedAt) this.loadedTimestamps.attributes = attResult.updatedAt;
      }
      
      // Also save GPC mappings if they have unsaved changes (for backend taxonomies)
      if (this.gpcMappings && this.gpcMappings.length > 0 && this.unsavedChanges?.gpcMappings) {
        try {
          const gpcPayload = {
            filename: baseFilename,
            version: versionName,
            mappings: this.gpcMappings,
            metadata: {
              description: `GPC mappings for ${baseFilename}`,
              lastUpdated: new Date().toISOString(),
              taxonomyVersion: versionName
            },
            ...(shouldCheckConflict && { expectedUpdatedAt: this.loadedTimestamps.gpcMappings })
          };
          
          const gpcResponse = await fetch(`${this.FUNCTION_URL}?action=saveGpcMappings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(gpcPayload)
          });
          
          if (gpcResponse.status === 409) {
            const conflictData = await gpcResponse.json();
            const error = new Error('Conflict detected by server');
            error.isConflict = true;
            error.conflictData = conflictData;
            error.dataType = 'gpcMappings';
            throw error;
          }
          
          if (gpcResponse.ok) {
            const gpcResult = await gpcResponse.json();
            console.log(`GPC mappings saved to version ${versionName}`);
            if (shouldCheckConflict && gpcResult.updatedAt) {
              this.loadedTimestamps.gpcMappings = gpcResult.updatedAt;
            }
          }
        } catch (gpcError) {
          // Re-throw conflict errors
          if (gpcError.isConflict) throw gpcError;
          console.warn('Failed to save GPC mappings:', gpcError.message);
        }
      }
      
      return true;
  },
  

    showValueContextMenu(event, attrId, value, valueIndex = null) {
      // Don't show context menu in backend taxonomy editor view
      // NOTE: This check might not be needed anymore, but we'll leave it for safety.
      if (this.currentView === 'backendTaxonomyEditor') {
        return;
      }
      
      event.preventDefault();
      this.valueContextMenu.x = event.clientX;
      this.valueContextMenu.y = event.clientY;
      
      // CRITICAL FIX: The `attrId` passed from the template is now the REAL attribute ID.
      this.valueContextMenu.attrId = attrId;
      this.valueContextMenu.value = value;
      this.valueContextMenu.valueIndex = valueIndex;
      this.valueContextMenu.show = true;

      setTimeout(() => {
        document.addEventListener('click', this.hideValueContextMenu, { once: true });
      }, 0);
    },

    hideValueContextMenu() {
      this.valueContextMenu.show = false;
    },

    renameValueFromContextMenu() {
      const { attrId, value: oldValue } = this.valueContextMenu;
    
      if (!attrId || oldValue === null || oldValue === undefined) {
        return; // Not enough info to proceed
      }
    
      const newValue = prompt("Enter new value:", oldValue);
      if (!newValue || newValue.trim() === '' || newValue === oldValue) {
        return; // No change needed
      }
    
      // --- Update the UI state first (`editableFilters`) ---
      if (this.editableFilters[attrId]) {
        const valuesArray = this.editableFilters[attrId].values;
        const valueIndex = valuesArray.indexOf(oldValue);
        if (valueIndex !== -1) {
          // Use splice to trigger Vue's reactivity
          valuesArray.splice(valueIndex, 1, newValue);
        }
      }
    
      // --- Then, update the master data (`attributeData`) ---
      const attr = this.getAttributeById(attrId);
      if (attr && attr.values) {
        let valueMetadata = {};
        let valueKeyToUpdate = null;
    
        // Find the original value object to preserve its metadata
        for (const valueKey in attr.values) {
          if (attr.values[valueKey].value_en === oldValue) {
            valueKeyToUpdate = valueKey;
            valueMetadata = { ...attr.values[valueKey] }; // shallow copy metadata
            break;
          }
        }
    
        if (valueKeyToUpdate) {
          // Update the English value while keeping all other metadata
          attr.values[valueKeyToUpdate].value_en = newValue;
          
          this.hasUnsavedChanges = true;
          this.showSnackbar(`Value renamed from "${oldValue}" to "${newValue}"`, 'success');
        }
      }
    },

    addNotesFromContextMenu() {
      if (this.valueContextMenu.attrId && this.valueContextMenu.value) {
        // Open the existing notes dialog with the current value
        this.openValueNotesDialog(this.valueContextMenu.attrId, this.valueContextMenu.value);
      }
    },
    removeAttributeIdFromAllTopLevels(taxonomyData, attrId, keysToSkip) {
      if (!taxonomyData || typeof taxonomyData !== 'object') return;

      // Handle top-level objects
      for (const topLevelKey of Object.keys(taxonomyData)) {
        if (keysToSkip && keysToSkip.includes(topLevelKey)) continue;
        const topLevelObj = taxonomyData[topLevelKey];
        this.removeAttributeIdEverywhere(topLevelObj, attrId, keysToSkip);
      }
    },
    normalizeAttributeIds(node) {
      if (!node || typeof node !== 'object') {
        return;
      }
    
      // Standardize the 'filters' array to strings
      if (Array.isArray(node.filters)) {
        node.filters = node.filters.map(id => String(id));
      }
    
      // Standardize the 'attribute_ids' array to strings
      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids = node.attribute_ids.map(id => String(id));
      }
    
      // Recurse into subcategories (new schema)
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach(subNode => this.normalizeAttributeIds(subNode));
      }
    
      // Recurse into other object keys (old schema)
      Object.keys(node).forEach(key => {
        if (!this.keysToSkip.includes(key) && key !== 'subcategories') {
          const childNode = node[key];
          if (childNode && typeof childNode === 'object') {
            this.normalizeAttributeIds(childNode);
          }
        }
      });
    },
    normalizeDataBeforePublish() {
        // Convert all attribute_ids and filters arrays to strings
  for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
    const nodeData = this.taxonomyData[topLevelKey];
    this.ensureStringIds(nodeData);
  }

  // Make sure all attributes have consistent structure
  for (const attrId in this.attributeData) {
    const attr = this.attributeData[attrId];
    attr.attribute_id = String(attrId); // Ensure this is a string
  }
      // Make sure all nodes have their attribute_ids and filters in sync
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        // Pass this.attributeData as the second argument
        this.normalizeFiltersRecursively(nodeData, this.attributeData); 
      }

      // Clean up deleted attributes
      for (const nodeData of Object.values(this.taxonomyData)) {
        this.cleanupDeletedAttributes(nodeData);
      }

      // Make sure all attributes have consistent structure
      for (const attrId in this.attributeData) {
        const attr = this.attributeData[attrId];

        // Ensure display_name is an object
        if (typeof attr.display_name === 'string') {
          attr.display_name = { en: attr.display_name };
        } else if (!attr.display_name) {
          attr.display_name = { en: '' };
        }

        // Normalize values
        if (!attr.values) attr.values = {};
        if (Array.isArray(attr.values)) {
          const obj = {};
          attr.values.forEach((v, i) => {
            obj[String(i).padStart(8, '0')] = v;
          });
          attr.values = obj;
        }

        // Ensure translations
        if (!attr.translations) {
          attr.translations = {
            value_de: [],
            value_fr: [],
            value_it: []
          };
        }
      }
    },
    ensureStringIds(node) {
      if (!node || typeof node !== 'object') return;
    
      // Convert arrays to string IDs
      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids = node.attribute_ids.map(id => String(id));
      }
      if (Array.isArray(node.filters)) {
        node.filters = node.filters.map(id => String(id));
      }
    
      // Recurse into children
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach(sub => this.ensureStringIds(sub));
      }
    
      // Recurse into other object children
      Object.keys(node).forEach(key => {
        if (!this.keysToSkip.includes(key) && 
            key !== 'filters' && 
            key !== 'attribute_ids' && 
            key !== 'subcategories' && 
            key !== 'sources') {
          const child = node[key];
          if (child && typeof child === 'object') {
            this.ensureStringIds(child);
          }
        }
      });
    },
    cleanupDeletedAttributes(node) {
      if (!node || typeof node !== 'object') return;

      // Clean up attribute_ids and filters arrays
      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids = node.attribute_ids.filter(id => !!this.attributeData[id]);
      }

      if (Array.isArray(node.filters)) {
        node.filters = node.filters.filter(id => !!this.attributeData[id]);
      }

      // Process subcategories
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach(sub => this.cleanupDeletedAttributes(sub));
      }

      // Process other object properties
      for (const key of Object.keys(node)) {
        if (!this.keysToSkip.includes(key) &&
          key !== 'filters' &&
          key !== 'attribute_ids' &&
          key !== 'subcategories' &&
          key !== 'sources') {
          const child = node[key];
          if (child && typeof child === 'object') {
            this.cleanupDeletedAttributes(child);
          }
        }
      }
    },
    isAttributeNameUnique(name) {
      return !Object.values(this.attributeData).some(attr => attr.name === name);
    },
    validateVersionName(versionName) {
      if (!versionName) return { valid: true }; // Empty version names are ok (will use date)

      // Check for reserved names and variations
      const lowerName = versionName.toLowerCase();
      const reservedNames = ['production', 'prod', 'test', 'testing', 'staging', 'working version', 'qa version'];

      // Exact match with reserved names
      if (reservedNames.includes(lowerName) || reservedNames.includes(versionName)) {
        return {
          valid: false,
          message: `"${versionName}" is a reserved name. Use appropriate buttons instead.`
        };
      }

      // Check for variations using patterns
      if (lowerName.startsWith('prod') || lowerName.startsWith('test') ||
        lowerName.startsWith('stag') || lowerName.startsWith('work') ||
        lowerName.startsWith('qa')) {
        return {
          valid: false,
          message: `Names starting with "${lowerName.substring(0, 4)}" are reserved. Choose a different name.`
        };
      }

      return { valid: true };
    },
    recheckCategories() {
      const attrPathMap = {};
    
      // This helper function will be called recursively to walk the entire category tree.
      const collectAttributeCategories = (node, currentPath) => {
        // Exit if the node is not a valid object
        if (!node || typeof node !== 'object') return;
    
        // Use the node's name for the path, or a fallback for unnamed nodes
        const nodeName = node.name || 'Unnamed';
        const path = currentPath ? `${currentPath}/${nodeName}` : nodeName;
    
        // Get the array of attribute IDs, whether it's stored in 'filters' or 'attribute_ids'
        const attributeIdsOnNode = node.filters || node.attribute_ids;
    
        // If attributes are found on this node, add their path to our map
        if (Array.isArray(attributeIdsOnNode)) {
          attributeIdsOnNode.forEach(attrId => {
            const attrIdStr = String(attrId);
            if (!attrPathMap[attrIdStr]) {
              attrPathMap[attrIdStr] = new Set();
            }
            attrPathMap[attrIdStr].add(path);
          });
        }
    
        // --- START OF THE CRITICAL FIX ---
        // This section makes the traversal logic robust.
    
        // 1. Recurse into the 'subcategories' array (for the new schema)
        if (Array.isArray(node.subcategories)) {
          node.subcategories.forEach(subNode => {
            collectAttributeCategories(subNode, path);
          });
        }
    
        // 2. Recurse into other object keys (for the old schema or mixed schemas)
        // This ensures we don't miss any nested categories.
        Object.keys(node).forEach(key => {
          // Use the component's 'keysToSkip' list to avoid recursing into metadata
          if (
            !this.keysToSkip.includes(key) &&
            key !== 'subcategories' // Already handled above
          ) {
            const childNode = node[key];
            // Ensure the child is an object before recursing
            if (childNode && typeof childNode === 'object' && !Array.isArray(childNode)) {
              collectAttributeCategories(childNode, path);
            }
          }
        });
        // --- END OF THE CRITICAL FIX ---
      };
    
      // Start the process for each top-level category (e.g., 'Fashion')
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        // The initial path is the top-level key itself
        collectAttributeCategories(nodeData, '');
      }
    
      // Now, update the main attributeData object with the collected paths
      for (const attrId of Object.keys(this.attributeData)) {
        const attrObj = this.getAttributeById(attrId);
        if (attrObj) {
          // If we found paths for this attribute, set them. Otherwise, set an empty array.
          attrObj.categories = attrPathMap[attrId] ? Array.from(attrPathMap[attrId]) : [];
        }
      }
    
      this.showSnackbar('Categories have been re-calculated for all attributes.', 'success');
      // It's good practice to force a re-render to ensure the UI updates.
      this.$forceUpdate();
    },
    getAttributeById(id) {
      if (id === null || id === undefined) return null;
    
      // Try direct lookup first (most common case)
      if (this.attributeData[id]) {
        return this.attributeData[id];
      }
      // Try as string
      const stringId = String(id);
      if (this.attributeData[stringId]) {
        return this.attributeData[stringId];
      }
      // Try as number
      const numId = parseInt(id, 10);
      if (this.attributeData[numId]) {
        return this.attributeData[numId];
      }
      
      return null; // Return null if not found by any means
    },


    toggleExpandCategories(item) {
      const attrId = item.attribute_id;
      const newExpandedState = { ...this.expandedCategories };
      newExpandedState[attrId] = !newExpandedState[attrId];
      this.expandedCategories = newExpandedState;
    },

    shouldDisplayCategory(item, index) {
      // Show if expanded OR if it's one of the first 7 items.
      return this.expandedCategories[item.attribute_id] || index < 7;
    },

    openPublishDialog() {
      if (this.hasUnsavedChanges) {
        this.unsavedChangesDialog = true;
        return;
      }
      this._openPublishDialogInternal();
    },
    handleUnsavedChoice(action) {
      this.unsavedChangesDialog = false;
      
      if (action === 'save-first') {
        // Save, then open publish dialog
        this.saveChanges().then(() => {
          this.showSnackbar('Changes saved. Ready to publish.', 'success');
          this._openPublishDialogInternal();
        }).catch(error => {
          this.showSnackbar('Failed to save: ' + error.message, 'error');
        });
      } else if (action === 'publish-anyway') {
        // Proceed without saving
        this._openPublishDialogInternal();
      }
      // If action === 'cancel', just close the dialog
    },
    _openPublishDialogInternal() {
      this.publishType = 'current';
      this.publishTarget = null;
      
      const hasBackend = this.backendTaxonomyData && Object.keys(this.backendTaxonomyData).length > 0;
      const hasFrontend = this.frontendRules && Array.isArray(this.frontendRules.categories);
      
      console.log('Publishing dialog data check:', {
        currentView: this.currentView,
        hasBackendData: hasBackend,
        hasFrontendRules: hasFrontend,
        selectedTaxonomy: this.selectedTaxonomy,
        hasUnsavedChanges: this.hasUnsavedChanges
      });
      
      if (!hasFrontend && this.selectedTaxonomy !== 'frontend.json') {
        this.loadFrontendRulesInBackground();
      }
      
      this.publishDialog = true;
    },
    
    async publishTo(target) {
      if (!target || !['production', 'test'].includes(target.toLowerCase())) {
          this.showSnackbar('Invalid publish target', 'error');
          return;
      }
  
      try {
          // Normalize data before publishing
          this.normalizeDataBeforePublish();
  
          // Create timestamped backup first
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const backupName = `${target.charAt(0).toUpperCase() + target.slice(1)} Backup ${timestamp}`;
          const itemsToPublish = [];
  
          const isFrontendTaxonomy = this.selectedTaxonomy?.startsWith('frontend');
          
          if (this.publishType === 'current' || this.publishType === 'both') {
              // Publish current taxonomy
              if (isFrontendTaxonomy) {
                  itemsToPublish.push({
                      type: 'frontend',
                      data: this.frontendRules,
                      baseName: this.selectedTaxonomy.replace('.json', '') // Use actual frontend name (frontend or frontend_v2)
                  });
              } else {
                  itemsToPublish.push({
                      type: 'backend',
                      taxonomyData: this.taxonomyData,
                      attributeData: this.attributeData,
                      baseName: this.selectedTaxonomy.replace('.json', '')
                  });
              }
          }
  
          if (this.publishType === 'both' && !isFrontendTaxonomy) {
              // Also publish frontend if we're in backend view - use linked frontend
              if (this.frontendRules) {
                  itemsToPublish.push({
                      type: 'frontend',
                      data: this.frontendRules,
                      baseName: this.getLinkedFrontendFilename() // Use linked frontend (frontend or frontend_v2)
                  });
              }
          }
  
          if (this.publishType === 'backend' && isFrontendTaxonomy) {
              // Publishing backend while in frontend view
              const backendName = this.selectedBackendTaxonomy || 'simplified_taxonomy_deduplicated';
              itemsToPublish.push({
                  type: 'backend',
                  taxonomyData: this.backendTaxonomyData,
                  attributeData: this.attributeData,
                  baseName: backendName.replace('.json', '')
              });
          }
          // Process all items to publish
          for (const item of itemsToPublish) {
              if (item.type === 'frontend') {
                  const path = `allium-ai-manor/taxonomies/${item.baseName}_modified_${target}.json`;
                  // This part is already correct in your original code
                  const response = await fetch(`${this.FUNCTION_URL}?action=publish&editorType=frontend`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                          path,
                          data: item.data,
                          target,
                          type: 'frontend',
                          metadata: {
                              sourceWorkingVersion: this.loadedVersion,
                              publishedAt: new Date().toISOString()
                          }
                      })
                  });
  
                  if (!response.ok) {
                      throw new Error(`Failed to publish frontend: ${await response.text()}`);
                  }
  
              } else if (item.type === 'backend') {
                  // The backup call now works correctly because `saveVersionToCloud` is fixed.
                  await this.saveVersionToCloud(
                      `${item.baseName}_${backupName}`,
                      `Backup before ${target} publish`
                  );
  
                  // Publish taxonomy
                  const modPath = `allium-ai-manor/taxonomies/${item.baseName}_modified_${target}.json`;
                  // ✅ **THE FIX IS HERE**
                  const modResponse = await fetch(`${this.FUNCTION_URL}?action=publish&editorType=backend`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                          path: modPath,
                          data: item.taxonomyData,
                          target,
                          type: 'backend-taxonomy'
                      })
                  });
  
                  if (!modResponse.ok) {
                      throw new Error(`Failed to publish taxonomy: ${await modResponse.text()}`);
                  }
  
                  // Publish attributes
                  const attPath = `allium-ai-manor/taxonomies/${item.baseName}_attributes_${target}.json`;
                  // ✅ **THE FIX IS HERE**
                  const attResponse = await fetch(`${this.FUNCTION_URL}?action=publish&editorType=backend`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                          path: attPath,
                          data: item.attributeData,
                          target,
                          type: 'backend-attributes'
                      })
                  });
  
                  if (!attResponse.ok) {
                      throw new Error(`Failed to publish attributes: ${await attResponse.text()}`);
                  }
              }
          }

          // TODO: Move to a better place
          const sizeMapperFilename = this.selectedBackendTaxonomy || 'simplified_taxonomy_deduplicated';
          await publishSizeMapperData(this.loadVersion, sizeMapperFilename, target);

          // TODO: Move to a better place
          const styleGuideFilename = this.selectedBackendTaxonomy || 'simplified_taxonomy_deduplicated';
          await publishStyleGuide(this.loadVersion, styleGuideFilename, target);
  
          this.showSnackbar(`Successfully published to ${target}`, 'success');
          this.publishDialog = false;
          await this.loadTaxonomyVersions();
  
      } catch (error) {
          console.error('Publishing error:', error);
          this.showSnackbar(`Failed to publish: ${error.message}`, 'error');
      }
  },
  async publishGpcMappings(publishData) {
    try {
      this.gpcDataLoading = true;
      
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      const currentVersion = this.loadedVersion || 'Working Version';
      
      // Validate publish data
      if (!publishData.target || !['production', 'test'].includes(publishData.target)) {
        throw new Error('Invalid publish target');
      }
      
      // The endpoint expects 'targetVersion' not 'target'
      const response = await fetch(`${this.FUNCTION_URL}?action=publishGpcMappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: baseFilename,
          mappings: this.gpcMappings,
          targetVersion: publishData.target,  // Backend expects 'targetVersion'
          metadata: {
            sourceVersion: currentVersion,
            publishedBy: this.$store?.state?.user?.email || 'unknown',
            createBackup: publishData.createBackup
          }
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to publish: ${response.status}`);
      }
      
      const result = await response.json();
      
      this.showSnackbar(
        `Successfully published ${this.gpcMappings.length} mappings to ${publishData.target}`,
        'success'
      );
      
      // Refresh mappings if we're viewing the target version
      if (publishData.target === this.loadedVersion) {
        await this.loadGpcMappings(baseFilename, publishData.target);
      }
      
    } catch (error) {
      console.error('Failed to publish GPC mappings:', error);
      this.showSnackbar(`Failed to publish: ${error.message}`, 'error');
    } finally {
      this.gpcDataLoading = false;
    }
  },
  
  async saveGpcMappingsToVersion(filename, version, data) {
    // Helper method to save GPC mappings to a specific version
    const payload = {
      filename: filename,
      version: version,
      mappings: data.mappings || this.gpcMappings,
      metadata: {
        description: data.description || `GPC mappings for ${filename}`,
        sourceVersion: data.sourceVersion,
        lastUpdated: new Date().toISOString(),
        mappingsCount: (data.mappings || this.gpcMappings).length
      }
    };
  
    const response = await fetch(`${this.FUNCTION_URL}?action=saveGpcMappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  
    if (!response.ok) {
      throw new Error(`Save failed with status ${response.status}`);
    }
    
    return response.json();
  },
  
  async getGpcMappingVersions() {
    // Fetch available versions of GPC mappings
    if (!this.selectedTaxonomy) return { working: null, production: null, test: null, backups: [] };
    
    const baseFilename = this.selectedTaxonomy.replace('.json', '');
    
    try {
      const response = await fetch(
        `${this.FUNCTION_URL}?action=listGpcVersions&filename=${baseFilename}`
      );
      
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to fetch GPC versions:', error);
    }
    
    return { working: null, production: null, test: null, backups: [] };
  },
  
  async compareGpcVersions(version1, version2) {
    // Compare two GPC mapping versions
    const baseFilename = this.selectedTaxonomy.replace('.json', '');
    
    try {
      const response = await fetch(
        `${this.FUNCTION_URL}?action=compareGpcVersions&filename=${baseFilename}&v1=${version1}&v2=${version2}`
      );
      
      if (response.ok) {
        const comparison = await response.json();
        return comparison;
      }
    } catch (error) {
      console.error('Failed to compare GPC versions:', error);
    }
    
    return null;
  },
  
  // Update your existing saveGpcMappings method to handle Working Version backups
  async saveGpcMappings() {
    if (!this.selectedTaxonomy) {
      this.showSnackbar('No taxonomy selected', 'error');
      return;
    }
  
    const baseFilename = this.selectedTaxonomy.replace('.json', '');
    const version = this.loadedVersion || 'Working Version';
  
    // If saving Working Version, create a backup first
    if (version === 'Working Version') {
      try {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const backupVersion = `Working Version Backup ${timestamp}`;
        
        await this.saveGpcMappingsToVersion(baseFilename, backupVersion, {
          description: `Automatic backup of Working Version created on ${new Date().toLocaleString()}`,
          sourceVersion: 'Working Version',
          mappings: this.gpcMappings
        });
        
        console.log(`Created GPC mappings backup: ${backupVersion}`);
      } catch (error) {
        console.warn('Failed to create backup, continuing with save:', error);
      }
    }
  
    const payload = {
      filename: baseFilename,
      version,
      mappings: this.gpcMappings,
      metadata: {
        description: `GPC mappings for ${baseFilename}`,
        lastUpdated: new Date().toISOString(),
        taxonomyVersion: this.loadedVersion
      }
    };
  
    try {
      const response = await fetch(`${this.FUNCTION_URL}?action=saveGpcMappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
  
      if (response.ok) {
        this.showSnackbar('GPC mappings saved successfully', 'success');
        this.hasUnsavedChanges = false;
      } else {
        throw new Error(`Save failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to save GPC mappings:', error);
      this.showSnackbar('Failed to save GPC mappings', 'error');
    }
  },
  
    getDisplayName(attr) {
      if (!attr) {
        return '';
      }
      
      let displayName = '';
      if (attr.display_name && typeof attr.display_name === 'object') {
        displayName = attr.display_name.en || attr.name || '';
      } else if (typeof attr.display_name === 'string') {
        displayName = attr.display_name;
      } else {
        displayName = attr.name || '';
      }
      
      // FIXED: Trim whitespace and newlines
      return displayName.trim();
    },
    
    // Get the internal name (not display name) for an attribute
    getAttributeInternalName(attrId) {
      const attr = this.getAttributeById(attrId);
      if (!attr) return null;
      
      // Return the internal 'name' field if it differs from display name
      const internalName = attr.name || null;
      const displayName = this.getDisplayName(attr);
      
      // Only return if internal name exists and is different from display name
      if (internalName && internalName !== displayName) {
        return internalName;
      }
      return null;
    },
    
    openNameDialog() {
      console.log('Button clicked, opening dialog');
      this.newAttributeName = '';
      this.nameDialog = true;
      console.log('nameDialog set to', this.nameDialog);
    },
    getValuesArray(attr) {
      if (!attr) {
        console.warn('getValuesArray called with null/undefined attr');
        return [];
      }
    
      try {
        // Handle boolean attributes - don't show values in attribute editor
        if (attr.data_type === 'boolean') {
          return [];
        }
        
        // Handle array-of-strings case
        if (Array.isArray(attr.values)) {
          return attr.values.map((v, i) => ({
            value_en: (typeof v === 'string') ? v : (v.value_en || ''),
            value_sort_index: i
          }));
        }
    
        // Handle object case - use direct for...in loop
        const result = [];
        if (attr.values && typeof attr.values === 'object') {
          for (const key in attr.values) {
            if (attr.values.hasOwnProperty(key)) {
              const vo = attr.values[key];
              if (vo && typeof vo === 'object') {
                result.push({
                  value_en: vo.value_en || '',
                  value_de: vo.value_de || '',
                  value_fr: vo.value_fr || '',
                  value_it: vo.value_it || '',
                  value_sort_index: vo.value_sort_index || 0
                });
              }
            }
          }
          // Sort by value_sort_index
          result.sort((a, b) => a.value_sort_index - b.value_sort_index);
        }
        return result;
      } catch (e) {
        console.error('Error in getValuesArray:', e, 'attr:', attr);
        return [];
      }
    },
    nextValueId(attr) {
      // Get IDs from historicalValueIds (default to empty array if undefined)
      const historicalIds = attr.historicalValueIds || [];
      // Get current value IDs from attr.values (default to empty object if undefined)
      const currentIds = Object.keys(attr.values || {});
      // Combine and deduplicate all IDs
      const allIds = [...new Set([...historicalIds, ...currentIds])];

      // If no IDs exist, start with '70000001'
      if (allIds.length === 0) {
        return '70000001';
      }

      // Find the highest ID and increment it
      const maxId = Math.max(...allIds.map(id => parseInt(id, 10)));
      const nextNum = maxId + 1;
      return String(nextNum).padStart(8, '0');
    },
    downloadTaxonomy() {
      // 1) Ensure there's a selected taxonomy
      if (!this.selectedTaxonomy) {
        this.showSnackbar('No taxonomy selected', 'warning');
        return;
      }

      // 2) Remove ".json" from the filename
      const baseFilename = this.selectedTaxonomy.replace('.json', '');

      // 3) Use loadVersion or fallback to current date
      const suffix = this.loadVersion.trim()
        ? this.loadVersion.trim()
        : new Date().toISOString().split('T')[0];

      // 4) Build the two filenames
      const fileNameMod = `${baseFilename}_modified_${suffix}.json`;
      const fileNameAtt = `${baseFilename}_attributes_${suffix}.json`;

      // 5) Convert in-memory objects to pretty JSON
      const modJson = JSON.stringify(this.taxonomyData, null, 2);
      const attJson = JSON.stringify(this.attributeData, null, 2);

      // 6) Download the modified taxonomy file
      const modBlob = new Blob([modJson], { type: 'application/json' });
      const modUrl = URL.createObjectURL(modBlob);
      const modLink = document.createElement('a');
      modLink.href = modUrl;
      modLink.download = fileNameMod;
      document.body.appendChild(modLink);
      modLink.click();
      document.body.removeChild(modLink);
      URL.revokeObjectURL(modUrl);

      // 7) Slight delay ensures each click event fully processes:
      setTimeout(() => {
        // Download the attributes file
        const attBlob = new Blob([attJson], { type: 'application/json' });
        const attUrl = URL.createObjectURL(attBlob);
        const attLink = document.createElement('a');
        attLink.href = attUrl;
        attLink.download = fileNameAtt;
        document.body.appendChild(attLink);
        attLink.click();
        document.body.removeChild(attLink);
        URL.revokeObjectURL(attUrl);
      }, 300);

      // Optional
      this.showSnackbar('Taxonomy + Attributes downloaded', 'success');
    },
    // Recursively flatten categories:
    flattenCategoryTree(node, pathSoFar, outputArr) {
      const name = node.name || 'Unnamed';
      // Build up a local path array
      const localPath = [...pathSoFar, name];

      // CategoryPath is the entire path joined by "|"
      const pathString = localPath.join('|');

      // Extract up to 5 levels
      const [level1, level2, level3, level4, level5] = localPath;

      // Push one row to the output array
      outputArr.push({
        CategoryPath: pathString,
        CategoryLevel1: level1 || '',
        CategoryLevel2: level2 || '',
        CategoryLevel3: level3 || '',
        CategoryLevel4: level4 || '',
        CategoryLevel5: level5 || ''
      });

      // If this node has subcategories, recurse into each one
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach((sub) => {
          this.flattenCategoryTree(sub, localPath, outputArr);
        });
      } else {
        // Also handle object children that might be categories
        // ignoring filters, sources, etc. if you want:
        const keys = Object.keys(node).filter((k) => {
          return (
            !this.keysToSkip.includes(k) &&
            k !== 'filters' &&
            k !== 'subcategories' &&
            k !== 'sources'
          );
        });
        for (const k of keys) {
          const child = node[k];
          if (child && typeof child === 'object') {
            this.flattenCategoryTree(child, localPath, outputArr);
          }
        }
      }
    },
    exportCategoriesToCSV() {
      // 1) Flatten the category tree for all top-level keys
      const flattened = [];
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        if (!nodeData) continue;
        // Pass an empty array as "pathSoFar" to start
        this.flattenCategoryTree(nodeData, [], flattened);
      }

      // 2) Build CSV lines
      //    We'll have columns: CategoryPath, Category Level 1..5
      const headers = [
        'CategoryPath',
        'Category Level 1',
        'Category Level 2',
        'Category Level 3',
        'Category Level 4',
        'Category Level 5'
      ];
      const escapeForCSV = (val) => {
        if (val == null) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      };

      // Start with the header row
      let csvContent = headers.join(',') + '\n';

      // For each flattened item, output a row
      flattened.forEach((item) => {
        const row = [
          item.CategoryPath,
          item.CategoryLevel1,
          item.CategoryLevel2,
          item.CategoryLevel3,
          item.CategoryLevel4,
          item.CategoryLevel5
        ].map(escapeForCSV).join(',');
        csvContent += row + '\n';
      });

      // 3) Trigger CSV download in the browser
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'categories_export.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showSnackbar('Categories CSV exported successfully!', 'success');
    },
    addAttributeValue(item) {
      const txt = (this.newAttributeValue[item.attribute_id] || '').trim();
      if (!txt) return;

      for (const [_, value] of Object.entries(item.values)) {
        if ([value.value_en, value.value_de, value.value_fr, value.value_it].includes(txt)) {
          let addNewValue = confirm(
            "This text already exists as a possible value for the attribute, would you like to add it anyways?"
          );
          if (!addNewValue) return;
        }
      }

      // Generate value ID using the existing helper function
      const valId = this.nextValueId(item);

      // Direct assignment with minimal structure - similar to original code
      if (!item.values) item.values = {};

      // Create a simple value object without excessive nesting
      item.values[valId] = {
        value_en: txt,
        value_de: '',
        value_fr: '',
        value_it: '',
        value_sort_index: Object.keys(item.values).length,
        notes: '',
        sources: []
      };

      // Add to historical IDs if needed
      if (!item.historicalValueIds) {
        item.historicalValueIds = [];
      }
      item.historicalValueIds.push(valId);

      // Simple assignment for valuesArray
      item.valuesArray = this.getValuesArray(item);

      // Clear input
      this.newAttributeValue[item.attribute_id] = '';
    },

    removeAttributeValue(item, index) {
      // Find and delete the value from item.values
      const valObj = item.valuesArray[index];
      const id = Object.keys(item.values).find(k => item.values[k] === valObj);
      if (id) {
        delete item.values[id];
      }

      // Reindex the remaining values
      const remainingValues = Object.values(item.values);
      remainingValues.sort((a, b) => a.value_sort_index - b.value_sort_index);
      remainingValues.forEach((vo, newIndex) => {
        vo.value_sort_index = newIndex;
      });

      // Update valuesArray with the reindexed values
      item.valuesArray = remainingValues;
    },
    onSearch() {
      console.log('Search triggered:', this.searchQuery);
    },
    nodeDataSources(node) {
      // This is the most reliable method. It looks up the original data
      // object for the given node's ID and gets the sources from there.
      const originalData = this.nodeIdMap[node.id];
      return originalData?.sources?.category ?? [];
    },
    getSourceChipStyle(src) {
      const color = this.sourceColors[src] || '#AAA';
      return {
        backgroundColor: color,
        color: '#fff'
      };
    },
    getFilterNameSourcesForSelectedNode(attrId) {
      if (!attrId || !this.selectedNodeId) {
        return [];
      }
      
      const stringAttrId = String(attrId);
    
      // First, check for sources defined on the node itself (this is advanced usage).
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      // The structure here is complex, so we check it defensively.
      // Note: We look up by displayName here because that's how the `sources` object is structured in the data model.
      const displayName = this.editableFilters[stringAttrId]?.displayName;
      if (displayName && nodeData?.sources?.filters?.[displayName]?.filter_sources) {
        return nodeData.sources.filters[displayName].filter_sources;
      }
      
      // If not found on the node, get sources from the master attribute data.
      const attrObj = this.getAttributeById(stringAttrId);
      if (attrObj) {
        // Return `filter_sources` or the top-level `sources` array.
        return attrObj.filter_sources || attrObj.sources || [];
      }
      
      return [];
    },
    getValueChipStyle(filterName, val) {
      try {
        // Use a more defensive approach
        const sources = this.getFilterValueSourcesForSelectedNode(filterName, val);

        if (!sources || sources.length === 0) {
          return {
            backgroundColor: '#f5f5f5',
            color: '#000'
          };
        }

        const firstSource = sources[0];
        const sourceColors = this.sourceColors;
        const color = sourceColors[firstSource] || '#AAA';

        return {
          backgroundColor: color,
          color: '#fff'
        };
      } catch (e) {
        console.error('Error in getValueChipStyle:', e);
        return {
          backgroundColor: '#f5f5f5',
          color: '#000'
        };
      }
    },
    getFilterValueSourcesForSelectedNode(filterName, filterValue) {
      if (!this.selectedNodeId) return [];

      try {
        // Create a local non-reactive reference
        const nodeId = this.selectedNodeId;
        const nodeMap = this.nodeIdMap;
        const nodeData = nodeMap[nodeId];

        // Check node sources first (using direct property access to avoid reactivity)
        if (nodeData &&
          nodeData.sources &&
          nodeData.sources.filters &&
          nodeData.sources.filters[filterName] &&
          nodeData.sources.filters[filterName].values &&
          nodeData.sources.filters[filterName].values[filterValue]) {
          // Make a copy to break reactivity
          return Array.from(nodeData.sources.filters[filterName].values[filterValue]);
        }

        // Look in attribute data if not found in node
        // Find attribute ID without using methods that might trigger reactivity
        let attrId = null;
        for (const id in this.attributeData) {
          const attr = this.attributeData[id];
          const displayName = attr.display_name && typeof attr.display_name === 'object'
            ? attr.display_name.en
            : (typeof attr.display_name === 'string' ? attr.display_name : attr.name);
          if (displayName === filterName) {
            attrId = parseInt(id, 10);
            break;
          }
        }

        if (attrId !== null) {
          const attr = this.attributeData[attrId];
          if (attr && attr.values) {
            // Use a direct loop without Object methods
            for (const valueId in attr.values) {
              if (attr.values[valueId].value_en === filterValue) {
                return attr.values[valueId].sources ? Array.from(attr.values[valueId].sources) : [];
              }
            }
          }
        }

        return [];
      } catch (e) {
        console.error('Error in getFilterValueSourcesForSelectedNode:', e);
        return [];
      }
    },
    getAttributeById(id) {
      // Try direct lookup first
      if (this.attributeData[id]) {
        return this.attributeData[id];
      }
      // Try as string
      if (this.attributeData[String(id)]) {
        return this.attributeData[String(id)];
      }
      // Try as number (for keys that might be numeric)
      if (this.attributeData[parseInt(id, 10)]) {
        return this.attributeData[parseInt(id, 10)];
      }
      return null;
    },
    getAttributeEditorValueChipStyle(attrId, val) {
      try {
        const attrObj = this.attributeData[attrId];
        if (!attrObj || !attrObj.values) {
          return {
            backgroundColor: '#f5f5f5',
            color: '#000'
          };
        }

        let sources = null;
        // Use direct property access and for...in loop
        for (const key in attrObj.values) {
          const valObj = attrObj.values[key];
          if (valObj.value_en === val) {
            sources = valObj.sources;
            break;
          }
        }

        if (!sources || sources.length === 0) {
          return {
            backgroundColor: '#f5f5f5',
            color: '#000'
          };
        }

        const color = this.sourceColors[sources[0]] || '#AAA';
        return {
          backgroundColor: color,
          color: '#fff'
        };
      } catch (e) {
        console.error('Error in getAttributeEditorValueChipStyle:', e);
        return {
          backgroundColor: '#f5f5f5',
          color: '#000'
        };
      }
    },
    hasValueNote(attrId, val) {
      const attr = this.attributeData[attrId];
      if (!attr) return false;
      if (!attr.value_notes) return false;
      const note = attr.value_notes[val];
      return note && note.trim().length > 0;
    },
    async loadTaxonomyList() {
      // For frontend editor view, load from database API (includes V2)
      if (this.currentView === 'frontendTaxonomyEditor') {
        try {
          const response = await fetch(`${this.FUNCTION_URL}?action=listFrontendTaxonomies`);
          if (response.ok) {
            const data = await response.json();
            // Convert to simple filename array format that rest of code expects
            this.taxonomies = data.taxonomies.map(t => t.filename);
            console.log('[loadTaxonomyList] Frontend taxonomies from DB:', this.taxonomies);
            
            let targetTaxonomyFile = 'frontend.json';
            if (!this.taxonomies.includes(targetTaxonomyFile)) {
              this.taxonomies.unshift(targetTaxonomyFile);
            }
            
            if (targetTaxonomyFile && this.selectedTaxonomy !== targetTaxonomyFile) {
              this._isInternalLoad = true;
              this.selectedTaxonomy = targetTaxonomyFile;
              this.$nextTick(() => {
                this._isInternalLoad = false;
              });
            }
            return;
          }
        } catch (error) {
          console.warn('[loadTaxonomyList] Failed to load frontend taxonomies from API, falling back to static file:', error.message);
        }
      }
      
      // For backend editor views (or fallback), load from static index.json
      const fetchUrl = this.getFetchUrl('taxonomies/index.json');
      fetch(fetchUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          this.taxonomies = data;

          let targetTaxonomyFile = null;

          if (this.currentView === 'frontendTaxonomyEditor') {
            targetTaxonomyFile = 'frontend.json';
            if (!data.includes(targetTaxonomyFile)) {
              this.taxonomies.push(targetTaxonomyFile); // Ensure frontend.json is in the list
            }
          } else { // For backendTaxonomyEditor and other views (Attribute Editor, GPC Mapping etc.)
            // Always default to the first available backend taxonomy.
            if (this.selectedBackendTaxonomy && data.includes(this.selectedBackendTaxonomy)) {
                targetTaxonomyFile = this.selectedBackendTaxonomy;
            } else if (data.length > 0) {
                targetTaxonomyFile = data[0]; // Default to the very first backend taxonomy
            }
          }

          if (targetTaxonomyFile && this.selectedTaxonomy !== targetTaxonomyFile) {
            // Set _isInternalLoad to prevent selectedTaxonomy watcher from clearing state prematurely
            this._isInternalLoad = true;
            this.selectedTaxonomy = targetTaxonomyFile;
            this.$nextTick(() => {
                this._isInternalLoad = false;
                // Once selectedTaxonomy is set, its watcher will handle loading the default version
            });
          } else if (!targetTaxonomyFile) {
            this.showSnackbar('No taxonomies found in index.json. Please upload one.', 'warning');
            this.taxonomyData = {}; // Clear any stale data
            this.attributeData = {};
            this.loadedVersion = null;
            this.hasUnsavedChanges = false;
            this.saveVersion = '';
          }
          // If selectedTaxonomy is already targetTaxonomyFile, its watcher won't fire.
          // In that case, ensure loading is triggered if not already loaded (e.g., initial page load).
          if (targetTaxonomyFile && this.selectedTaxonomy === targetTaxonomyFile && !this.loadedVersion) {
              console.log("Taxonomy already selected, triggering manual load of default version.");
              this.loadTaxonomyVersions().then(versions => {
                  let defaultVersion = versions.find(v => v === 'Working Version') ||
                                       versions.find(v => v.toLowerCase() === 'production') ||
                                       versions[0];
                  this.loadTaxonomyFromCloud(defaultVersion);
              });
          }
        })
        .catch((error) => {
          console.error('Error fetching taxonomy list:', error);
          this.showSnackbar('Failed to load taxonomy list', 'error');
          this.taxonomies = [];
          this.taxonomyData = {};
          this.attributeData = {};
          this.loadedVersion = null;
          this.hasUnsavedChanges = false;
          this.saveVersion = '';
        });
    },
        
    getFetchUrl(filename) {
      // Adjust for your environment if needed
      return process.env.NODE_ENV === 'production'
        ? `/${filename}`
        : `/${filename}`;
    },
    async loadTaxonomyVersions() {
      if (!this.selectedTaxonomy) return Promise.resolve([]);
      const justFilename = this.selectedTaxonomy.replace('.json', '');
      
      // ✅ ADD: Include editorType parameter
      const editorType = this.selectedTaxonomy?.startsWith('frontend') ? 'frontend' : 'backend';
      const url = `${this.FUNCTION_URL}?action=listVersions&filename=${justFilename}&editorType=${editorType}`;
  
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch versions');
        const data = await res.json();
        let allVersions = data.versions || [];
  
        // Your existing ordering logic stays exactly the same...
        const hasProduction = allVersions.some(v => v.toLowerCase() === 'production');
        const hasTest = allVersions.some(v => v.toLowerCase() === 'test');
        const hasWorking = allVersions.some(v => v === 'Working Version');
        const hasQA = allVersions.some(v => v === 'QA Version');
  
        let orderedVersions = [];
        if (hasWorking) orderedVersions.push('Working Version');
        if (hasProduction) orderedVersions.push(allVersions.find(v => v.toLowerCase() === 'production'));
        if (hasQA) orderedVersions.push('QA Version');
        if (hasTest) orderedVersions.push(allVersions.find(v => v.toLowerCase() === 'test'));
  
        const otherVersions = allVersions.filter(v =>
          v.toLowerCase() !== 'production' &&
          v.toLowerCase() !== 'test' &&
          v !== 'Working Version' &&
          v !== 'QA Version'
        );
  
        orderedVersions = [...orderedVersions, ...otherVersions];
        this.versions = orderedVersions;
        return this.versions;
  
      } catch (err) {
        console.error('Error fetching versions:', err);
        this.showSnackbar('Failed to list versions', 'error');
        this.versions = [];
        return [];
      }
    },  
    onVersionSelected() {
      this.loadTaxonomyFromCloud();
    },
    onLoadVersionChange(selected) {
      console.log('User selected loadVersion:', selected);

      if (selected === null || selected === undefined) {
        // User cleared the selection, reset loadVersion but don't clear loaded taxonomy immediately
        this.loadVersion = null;
        return;
      }

      // We explicitly set _isInternalLoad to true here because this change originates from the UI dropdown,
      // and we want the loadVersion watcher to handle the actual loading without triggering recursive calls
      // from loadedVersion updates.
      this._isInternalLoad = true;
      this.loadVersion = selected;
      this.$nextTick(() => {
        this._isInternalLoad = false; // Reset the flag after the property update
      });

      // The actual loading will now be triggered by the `loadVersion` watcher.
      // This helps separate concerns: UI selection updates loadVersion, watcher reacts to it.
    },
    getDisplayVersion(versionSlug) {
      if (versionSlug.toLowerCase() === 'production') return 'Production';
      if (versionSlug.toLowerCase() === 'test') return 'Test';
      
      // Check the reverse map for special formatted names like backups
      const foundDisplayKey = Object.keys(this._versionDisplayMap).find(
        key => this._versionDisplayMap[key] === versionSlug
      );
      
      return foundDisplayKey || versionSlug; // Fallback to the slug itself
    },

    async loadTaxonomyFromCloud(versionToLoad) {
      let finalVersionToLoad = versionToLoad;
    
      // If no version is passed, determine the default
      if (!finalVersionToLoad) {
        if (this.loadVersion) {
          finalVersionToLoad = this._versionDisplayMap[this.loadVersion] || this.loadVersion;
        } else {
          const versionsAvailable = await this.loadTaxonomyVersions();
          finalVersionToLoad = versionsAvailable.find(v => v === 'Working Version') ||
                               versionsAvailable.find(v => v.toLowerCase() === 'production') ||
                               versionsAvailable[0];
        }
      }
    
      if (!this.selectedTaxonomy || !finalVersionToLoad) {
        console.warn('Load aborted: Missing selected taxonomy or a version to load.');
        this.taxonomyData = {};
        this.attributeData = {};
        this.loadedVersion = null;
        return;
      }
      
      const justFilename = this.selectedTaxonomy.replace('.json', '');
      const trimmedVersion = finalVersionToLoad.trim();
      const normalizedSlug = ['production', 'test'].includes(trimmedVersion.toLowerCase())
        ? trimmedVersion.toLowerCase()
        : trimmedVersion;
        
      const modFile = `${justFilename}_modified_${normalizedSlug}.json`;
      const attFile = `${justFilename}_attributes_${normalizedSlug}.json`;
    
      try {
        // --- START TRANSACTION ---
        // Step 1: Fetch data into a temporary result object.
        // This function no longer modifies the component state directly.
        const result = await this.tryRemoteLoad(justFilename, modFile, attFile, normalizedSlug);
    
        // Step 2: Check if the load was successful and not already handled (like creating a new file).
        if (result.loaded && result.data) {
          // Step 3: COMMIT THE NEW STATE. All data is available, so we can safely update.
          this._isInternalLoad = true; // Prevent watcher loops
    
          this.setModifiedTaxonomyData(result.data.modifiedData, result.data.attributesData);
          
          this.loadedVersion = finalVersionToLoad;
          this.currentVersionDescription = result.data.metadata.description || 'No description provided.';
          
          // Sync the UI dropdown to the version that was just successfully loaded
          const displayVersion = this.getDisplayVersion(finalVersionToLoad);
          this.loadVersion = displayVersion;
          
          // Pre-populate the "Save as" field for non-special versions
          if (!finalVersionToLoad.includes('Backup') && !['production', 'test'].includes(finalVersionToLoad.toLowerCase())) {
            this.saveVersion = finalVersionToLoad;
          } else {
            this.saveVersion = '';
          }
          
          this.showSnackbar(`Loaded taxonomy (${this.getDisplayVersion(finalVersionToLoad)}) from cloud`, 'success');
          this.hasUnsavedChanges = false;
          this.$nextTick(() => { this._isInternalLoad = false; });

          // Load size mapper data from API (TODO: Put this in a better spot)
          this.onSizeMapperDataChange(await getSizeMapperData(justFilename, finalVersionToLoad));
          await this.loadStyleRules();
          
          // Register session and check for other active users
          this.registerSession();
          this.fetchActiveSessions();
          
        } else if (!result.loaded) {
          // The load failed critically. Throw an error to be caught by the catch block.
          throw new Error(`Failed to load required files for version "${finalVersionToLoad}".`);
        }
        // If result.loaded is true but result.data is null, it means tryRemoteLoad handled it
        // (e.g., by initializing a new taxonomy), so we do nothing here.
    
      } catch (error) {
        // Step 4: AGGRESSIVELY RESET STATE on any failure in the transaction.
        // This prevents the "zombie state" where old data remains.
        console.error('CRITICAL LOAD FAILURE:', error);
        this.showSnackbar(`Failed to load "${this.getDisplayVersion(finalVersionToLoad)}". Editor has been reset.`, 'error', 4000);
    
        this.taxonomyData = {};
        this.attributeData = {};
        this.loadedVersion = null;
        this.currentVersionDescription = '';
        this.hasUnsavedChanges = false;
        this.saveVersion = '';
        this.loadVersion = null; // Also clear the dropdown
      }
    },
    
    /**
     * Fetches taxonomy data from the cloud but does NOT commit it to the component state.
     * Its only job is to return the data or a failure status.
     * @returns {Promise<{loaded: boolean, handled: boolean, data: object|null}>}
     */
    async tryRemoteLoad(baseName, modName, attName, versionSlug) {
      // Determine editor type based on baseName
      const isFrontend = baseName.startsWith('frontend');
      const editorType = isFrontend ? 'frontend' : 'backend';
      
      const modUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${modName}&editorType=${editorType}`;
  
      try {
        // Frontend files don't have separate attribute files - they load backend attributes later
        if (isFrontend) {
          const modResponse = await fetch(modUrl);
          
          if (modResponse.status === 404) {
            console.log(`Frontend file "${modName}" not found. Initializing new empty frontend.`);
            this.initializeEmptyTaxonomy(baseName, versionSlug);
            return { loaded: true, handled: true, data: null };
          }
          
          if (!modResponse.ok) {
            throw new Error(`Failed to load frontend: ${modResponse.statusText}`);
          }
          
          const modJson = await modResponse.json();
          
          // Store frontend loaded timestamp for conflict detection
          if (modJson.updatedAt) {
            this.loadedTimestamps.frontend = modJson.updatedAt;
          }
          
          return {
            loaded: true,
            handled: false,
            data: {
              modifiedData: modJson.data,
              attributesData: {}, // Frontend doesn't have its own attributes
              metadata: modJson.metadata || {}
            }
          };
        }
        
        // Backend files load both modified and attributes
        const attUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${attName}&editorType=${editorType}`;
        
        const [modResponse, attResponse] = await Promise.all([
          fetch(modUrl),
          fetch(attUrl)
        ]);
  
        // Handle case where files don't exist (e.g., creating a new taxonomy)
        if (modResponse.status === 404 || attResponse.status === 404) {
          console.log(`One or more files for "${versionSlug}" not found. Initializing new empty taxonomy.`);
          this.initializeEmptyTaxonomy(baseName, versionSlug);
          return { loaded: true, handled: true, data: null };
        }
  
        // Handle other network errors
        if (!modResponse.ok || !attResponse.ok) {
          throw new Error(`Modified: ${modResponse.statusText}, Attributes: ${attResponse.statusText}`);
        }
  
        const modJson = await modResponse.json();
        const attJson = await attResponse.json();
  
        // Store loaded timestamps for conflict detection
        if (modJson.updatedAt) {
          this.loadedTimestamps.taxonomy = modJson.updatedAt;
        }
        if (attJson.updatedAt) {
          this.loadedTimestamps.attributes = attJson.updatedAt;
        }
  
        return {
          loaded: true,
          handled: false,
          data: {
            modifiedData: modJson.data,
            attributesData: attJson.data,
            metadata: modJson.metadata || {}
          }
        };
      } catch (error) {
        console.error(`Error in tryRemoteLoad for version "${versionSlug}":`, error);
        return { loaded: false, handled: false, data: null };
      }
    },
    
    
    async setModifiedTaxonomyData(modData, attData) {
      this.trackChanges = false; 
  
      // --- FRONTEND RULES LOADING ---
      if (this.selectedTaxonomy?.startsWith('frontend')) {
          console.log(`Loading a frontend definition file: ${this.selectedTaxonomy}`);
          
          // The modData is the frontendRules object.
          const isValidRulesObject = modData && Array.isArray(modData.categories);
  
          if (isValidRulesObject) {
              this.frontendRules = modData;
              // Ensure all required properties exist to prevent future errors.
              if (!this.frontendRules.global_exclusions) this.frontendRules.global_exclusions = [];
              if (!this.frontendRules.backend_source_taxonomy) this.frontendRules.backend_source_taxonomy = 'simplified_taxonomy_deduplicated.json'; // Default
              if (!this.frontendRules.backend_source_version) this.frontendRules.backend_source_version = 'Working Version'; // Default
          } else {
              console.warn("Loaded file is not a valid rules object. Initializing new ruleset.");
              this.frontendRules = {
                  backend_source_taxonomy: 'simplified_taxonomy_deduplicated.json',
                  backend_source_version: 'Working Version',
                  categories: [],
                  global_exclusions: []
              };
          }
          
          // Now, load the backend taxonomy and attributes specified in the rules.
          // This populates `this.backendTaxonomyData` and `this.attributeData`.
          await this.loadBackendSourceForFrontend(this.frontendRules.backend_source_taxonomy, this.frontendRules.backend_source_version);
  
      } else {
          // --- BACKEND TAXONOMY LOADING (No change) ---
          this.taxonomyData = modData || {};
          this.attributeData = attData || {};

                // --- START OF THE FIX ---
      // Normalize all attribute IDs to strings right after loading
      Object.values(this.taxonomyData).forEach(rootNode => {
        this.normalizeAttributeIds(rootNode);
      });
      console.log('%cData Normalization Complete: All attribute IDs in the taxonomy are now strings.', 'color: green; font-weight: bold;');
      // --- END OF THE FIX ---

          const existingIds = new Set();
          Object.values(this.taxonomyData).forEach(rootNode => {
            this.addMissingIds(rootNode, existingIds);
          });

          // Set backendTaxonomyData as well, since it's the source for frontend derivation later.
          this.backendTaxonomyData = this.taxonomyData;
      }
  
      // --- COMMON LOGIC AFTER LOADING ---
      this.selectedNode = null;
      this.selectedNodeId = null;
      
      // For a backend view, we need to populate the topLevelNodes and treeItems.
      // For a frontend view, this is less critical as rebuildFrontendTreeState handles it.
      if (this.currentView === 'backendTaxonomyEditor') {
          this.topLevelNodes = this.getTopLevelNodes(this.taxonomyData);
          if (this.topLevelNodes.length > 0) {
              this.selectedTopLevelNode = this.topLevelNodes[0];
              this.loadTopLevelNode(); // This builds the backend tree
          }
      }
      
      this.gatherAllFilters(); // Re-populates attribute dropdowns
      if (this.currentView === 'frontendTaxonomyEditor') {
        this.rebuildFrontendTreeState();
      }
      
      setTimeout(() => {
          this.trackChanges = true;
          this.hasUnsavedChanges = false;
      }, 500);
    },
    async loadBackendSourceForFrontend(filename, version) {
      console.log(`[LOAD_BACKEND] Loading backend source: ${filename} (${version})`);
      
      const justFilename = filename.replace('.json', '');
      
      // ✅ ADD: Always use backend editorType for loading backend source data
      const editorType = 'backend';
      
      const modUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${justFilename}_modified_${version}.json&editorType=${editorType}`;
      const attUrl = `${this.FUNCTION_URL}?action=load&path=allium-ai-manor/taxonomies/${justFilename}_attributes_${version}.json&editorType=${editorType}`;
      
      console.log(`[LOAD_BACKEND] Fetching URLs:`, { modUrl, attUrl });
  
      try {
          const [modResponse, attResponse] = await Promise.all([
              fetch(modUrl),
              fetch(attUrl)
          ]);
  
          console.log(`[LOAD_BACKEND] Response status:`, { 
            mod: modResponse.status, 
            att: attResponse.status 
          });
  
          if (!modResponse.ok || !attResponse.ok) {
              throw new Error(`Failed to load backend source data: Modified: ${modResponse.status} ${modResponse.statusText}, Attributes: ${attResponse.status} ${attResponse.statusText}`);
          }
  
          const modJson = await modResponse.json();
          const attJson = await attResponse.json();
          
          console.log(`[LOAD_BACKEND] Loaded data:`, {
            modKeys: Object.keys(modJson.data || {}),
            attCount: Object.keys(attJson.data || {}).length
          });
  
          this.backendTaxonomyData = modJson.data;
          this.attributeData = attJson.data;
  
          this.showSnackbar(`Backend source "${filename} (${version})" loaded for frontend derivation.`, 'info');
          console.log(`[LOAD_BACKEND] Successfully loaded backend source`);
      } catch (error) {
          console.error('[LOAD_BACKEND] Error loading backend source for frontend:', error);
          this.showSnackbar(`Failed to load backend source: ${error.message}`, 'error');
          this.backendTaxonomyData = {};
          this.attributeData = {};
      }
    },

    getTopLevelNodes(data) {
      console.log('🔍 getTopLevelNodes called with data:', data);
      if (!data || typeof data !== 'object') {
        console.log('❌ getTopLevelNodes: data is null or not an object');
        return [];
      }
      const keys = Object.keys(data).filter((key) => !this.keysToSkip.includes(key));
      console.log('📋 getTopLevelNodes returning keys:', keys);
      console.log('🚫 keysToSkip:', this.keysToSkip);
      return keys;
    },
    loadTopLevelNode() {
      // Save scroll position
      const container = this.$el.querySelector('.tree-scroll-container');
      const scrollLeft = container ? container.scrollLeft : 0;
      const scrollTop = container ? container.scrollTop : 0;

      // Save expansion state
      if (this.expandedNodes.size === 0 && this.treeItems && this.treeItems.length > 0) {
        this.collectExpansions(this.treeItems[0]);
      }

      // Original code
      if (this.selectedTopLevelNode && this.taxonomyData[this.selectedTopLevelNode]) {
        const data = this.taxonomyData[this.selectedTopLevelNode];
        this.nodeIdMap = {};
        this.nodeIdsByType = {};
        this.treeItems = this.convertToTreeItems(data, this.selectedTopLevelNode);
        this.buildIdToNodeMap(data, this.selectedTopLevelNode);
      } else {
        this.treeItems = [];
        this.nodeIdMap = {};
        this.nodeIdsByType = {};
      }

      // Restore scroll position after DOM update
      this.$nextTick(() => {
        if (container) {
          container.scrollLeft = scrollLeft;
          container.scrollTop = scrollTop;
        }
      });
    },

  openRuleBuilderDialog(parentId = null, existingRule = null) {
    this.ruleBuilderDialog.parentId = parentId;
    this.editingFrontendRule = existingRule; 
    this.ruleBuilderDialog.show = true;
    
    // Make sure the dialog has access to backend taxonomy data for showing inherited attributes
    this.$nextTick(() => {
      // The dialog will receive backendTaxonomyData as a prop
    });
  },
  
  handleSaveRule(rule) {
    const parentId = this.ruleBuilderDialog.parentId;
    const isEditing = !!this.editingFrontendRule;
  
    if (isEditing) {
      // Find and replace the existing rule
      const findAndReplace = (rulesArray, ruleId, newRuleData) => {
        for (let i = 0; i < rulesArray.length; i++) {
          if (rulesArray[i].id === ruleId) {
            // Preserve children from old rule if new one doesn't have them
            newRuleData.children = rulesArray[i].children || [];
            rulesArray.splice(i, 1, newRuleData);
            return true;
          }
          if (rulesArray[i].children && findAndReplace(rulesArray[i].children, ruleId, newRuleData)) {
            return true;
          }
        }
        return false;
      };
      findAndReplace(this.frontendRules.categories, this.editingFrontendRule.id, rule);
      this.showSnackbar(`Rule "${rule.name}" updated.`, 'success');
    } else {
      // Creating a new rule
      if (parentId) {
        const parentRule = this.findRuleById(this.frontendRules.categories, parentId);
        if (parentRule) {
          if (!parentRule.children) parentRule.children = [];
          parentRule.children.push(rule);
        } else {
          this.showSnackbar("Error: could not find parent rule to attach child.", "error");
          return;
        }
      } else {
        this.frontendRules.categories.push(rule);
      }
      this.showSnackbar(`Created new rule "${rule.name}".`, 'success');
    }
  
    // Reset state and trigger a full rebuild of the frontend tree
    this.editingFrontendRule = null;
    this.ruleBuilderDialog.parentId = null;
    this.hasUnsavedChanges = true;
    this.$nextTick(() => {
      this.rebuildFrontendTreeState();
    });
  },

  createItemFromData(data, itemId) {
    if (!data || typeof data !== 'object') return null;
  
    const filters = data.attribute_ids || data.filters || [];
    
    let className = '';
    if (this.currentView === 'frontendTaxonomyEditor') {
      if (data.type === 'conditional') {
          className = 'node-derived-composite';
      } else if (data.backend_source_path || data.type === 'linked_root' || data.type === 'linked_category') {
          className = 'node-derived';
      } else if (data.type === 'manual_node' && filters.length > 0) {
          className = 'node-with-attributes';
      }
    } else if (this.currentView === 'backendTaxonomyEditor') {
      if (filters.length > 0) {
          className = 'node-with-attributes';
      }
    }
  
    const item = {
      // FIXED: Use data.id if it exists (from addMissingIds), fallback to itemId
      id: data.id || itemId,
      label: data.name || itemId || 'Unnamed Node',
      children: [],
      filters: filters,
      className: className,
      isDerived: data.isDerived,
      rule_details: data.derivation_rule || null,
      type: data.type,
      backend_source_path: data.backend_source_path,
      sources: data.sources || {} 
    };
  
    // Recursively build children
    const childrenSource = data.subcategories || data.children;
    if (Array.isArray(childrenSource)) {
      childrenSource.forEach((child, idx) => {
        // FIXED: Use child.id if available, fallback to path-based ID
        const childId = child.id || `${item.id}.[${idx}]`;
        const childItem = this.createItemFromData(child, childId);
        if (childItem) item.children.push(childItem);
      });
    }
    return item;
  },
  
    buildIdToNodeMap(data, parentId = '') {
      if (!data || typeof data !== 'object') return;
      
      // --- CRITICAL FIX ---
      // Use the ID from the data object itself for the map key
      const itemId = data.id || parentId || 'root';
      this.nodeIdMap[itemId] = data;
  
      const childrenSource = data.subcategories || data.children;
      if (Array.isArray(childrenSource)) {
          childrenSource.forEach((child, idx) => {
              // Also use the child's own ID for the recursive call here
              const subId = child.id || `${itemId}.[${idx}]`;
              this.buildIdToNodeMap(child, subId);
          });
      }
    },
    collectNodeFilterIds(nodeData) {
      const setOfIds = new Set();
      if (!nodeData) return setOfIds;
      if (Array.isArray(nodeData.filters)) {
        nodeData.filters.forEach((fId) => setOfIds.add(fId));
      }
      return setOfIds;
    },

    onNodeClick(e, node) {
      const iconClicked = e.target.closest('.tree-expand-icon') || e.target.closest('.org-tree-node-btn');
      if (iconClicked) {
        return;
      }
    
      const id = node.id;
      if (!id) {
        this.selectedNode = null;
        this.selectedNodeId = null;
        return;
      }
    
      this.selectedNodeId = id;

      // NEW: When a node is clicked in the frontend editor, find its rule and populate the web category code.
      if (this.currentView === 'frontendTaxonomyEditor') {
        const rule = this.findRuleById(this.frontendRules.categories, id);
        if (rule) {
          // Handle multiple codes format
          if (rule.web_category_codes && Array.isArray(rule.web_category_codes)) {
            this.webCategoryCodesForSelectedNode = rule.web_category_codes;
          }
          // Handle legacy single code format
          else if (rule.web_category_code && typeof rule.web_category_code === 'string') {
            this.webCategoryCodesForSelectedNode = [rule.web_category_code];
          }
          else {
            this.webCategoryCodesForSelectedNode = [];
          }
        } else {
          this.webCategoryCodesForSelectedNode = [];
        }
      }

      const originalData = this.nodeIdMap[id];
      if (!originalData) {
        this.selectedNode = null;
        return;
      }
      
      const filterIds = originalData.attribute_ids || originalData.filters || [];
      
      const newEditableFilters = {};
      filterIds.forEach(attrId => {
        // Ensure we are working with a string ID for consistency
        const stringAttrId = String(attrId);
        const attrObj = this.getAttributeById(stringAttrId);
        
        if (attrObj) {
          newEditableFilters[stringAttrId] = {
            displayName: this.getDisplayName(attrObj),
            values: this.getValuesArray(attrObj).map(vo => vo.value_en)
          };
        } else {
          // Handle case where attribute might have been deleted but reference remains
          newEditableFilters[stringAttrId] = {
            displayName: `(Missing) Attribute #${stringAttrId}`,
            values: []
          };
        }
      });

      // Aggregate and set metadata, ensuring we assign a default
      // value is some attributes are missing metadata for some reason
      const metadata = {};
      filterIds.forEach(filterId => {
        if (originalData.attribute_metadata?.[filterId]) {
          metadata[filterId] = originalData.attribute_metadata[filterId];
        } else {
          metadata[filterId] = DEFAULT_ATTRIBUTE_METADATA();
        }
      });

      // Populate metadata on taxonomyData node if missing
      this.nodeIdMap[id].attribute_metadata = metadata;
    
      this.selectedNode = {
        id: id,
        name: originalData.name || node.label,
        // Note: The selectedNode.filters property is no longer used by the UI state,
        // but we can keep it for debugging or other potential uses.
        filters: newEditableFilters,
        metadata: metadata,
        // NEW: Add properties needed for the new UI feature
        isDerived: originalData.isDerived, 
        backend_source_path: originalData.backend_source_path,
        include_attributes: originalData.include_attributes
      };
      
      // Set the new, correctly keyed UI state
      this.editableFilters = newEditableFilters;
      this.expandedFilters = []; // Reset expansion state for new node
      this.newFilterValues = {}; // Reset "add value" inputs
    },
    
    refreshSelectedNodeFilters() {
      if (!this.selectedNodeId) {
        this.selectedNode = null;
        return;
      }
      
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) {
        this.selectedNode = null;
        return;
      }
    
      const filterIds = nodeData.attribute_ids || nodeData.filters || [];
      
      const newEditableFilters = {};
      filterIds.forEach((attrId) => {
        const stringAttrId = String(attrId);
        const attrObj = this.getAttributeById(stringAttrId);
        
        if (attrObj) {
          newEditableFilters[stringAttrId] = {
            displayName: this.getDisplayName(attrObj),
            values: this.getValuesArray(attrObj).map(vo => vo.value_en)
          };
        } else {
          newEditableFilters[stringAttrId] = {
            displayName: `(Missing) Attribute #${stringAttrId}`,
            values: []
          };
        }
      });
      
      this.editableFilters = newEditableFilters;
      
      // Prune any expanded filters that no longer exist on this node
      this.expandedFilters = this.expandedFilters.filter(attrId => !!this.editableFilters[attrId]);
    },
    onContextMenuAction({ command, node }) {
      console.log(`[CONTEXT ACTION] Command: '${command}', Node ID: ${node.id}`);
      this.selectedNodeId = node.id;
    
      if (this.currentView === 'backendTaxonomyEditor') {
        switch (command) {
          case 'copy':
            navigator.clipboard.writeText(node.label);
            this.showSnackbar(`Copied "${node.label}"`, 'info');
            break;
          case 'add': 
            this.addNode(node.id); 
            break;
          case 'edit': 
            this.startEditingNode(node.id); 
            break;
          case 'delete': 
            this.deleteNode(node.id); 
            break;
          // ADD THIS CASE
          case 'edit_notes':
            this.openCategoryNotesDialog(node.id);
            break;
          case 'copy_backend_path':
            const path = this.getNodePathById(node.id).replace(/\//g, '|');
            this.backendNodeClipboardPath = path;
            navigator.clipboard.writeText(path);
            this.showSnackbar(`Backend path copied: "${path}"`, 'info');
            break;
        }
        return;
      }
      
      if (this.currentView === 'frontendTaxonomyEditor') {
        const isVirtualRoot = node.id === 'frontend_virtual_root' || node.type === 'root';
        const ruleData = this.findRuleById(this.frontendRules.categories, node.id);
        // ADD THIS DEBUG TOO
    console.log('[CONTEXT] Found rule data:', ruleData);
    
        switch (command) {
          case 'edit_rule':
            if (!ruleData) {
              this.showSnackbar('This is a derived node. Its rule cannot be edited directly.', 'warning');
              return;
            }
            // Find the parent to pass its ID to the dialog
            const parentRule = this.findParentRule(this.frontendRules.categories, ruleData.id);
            this.openRuleBuilderDialog(parentRule ? parentRule.id : null, ruleData);
            break;
    
          case 'delete_rule':
            this.deleteFrontendNodeOrRule(node);
            break;
    
          case 'add_child_rule': {
            let parentIdForNewRule = isVirtualRoot ? null : node.id;
            this.openRuleBuilderDialog(parentIdForNewRule);
            break;
          }
    
          case 'add_from_attribute': {
            let parentIdForNewRule = isVirtualRoot ? null : node.id;
            this.openAttributeSearchDialog(parentIdForNewRule);
            break;
          }
        }
      }
    },
    findParentRule(rulesArray, ruleId) {
      for (const rule of rulesArray) {
          if (rule.children && rule.children.some(child => child.id === ruleId)) {
              return rule;
          }
          if (rule.children) {
              const found = this.findParentRule(rule.children, ruleId);
              if (found) return found;
          }
      }
      return null;
  },
  
  deleteFrontendNodeOrRule(nodeToDelete) {
    const nodeId = nodeToDelete.id;
    const nodeName = nodeToDelete.label;
  
    // --- CASE 1: Try to find and delete a rule that directly created this node. ---
    const findAndRemoveRule = (rulesArray, ruleId) => {
      for (let i = 0; i < rulesArray.length; i++) {
        if (rulesArray[i].id === ruleId) {
          rulesArray.splice(i, 1);
          return true;
        }
        if (rulesArray[i].children && findAndRemoveRule(rulesArray[i].children, ruleId)) {
          return true;
        }
      }
      return false;
    };
  
    if (findAndRemoveRule(this.frontendRules.categories, nodeId)) {
      this.showSnackbar(`Rule for "${nodeName}" was deleted.`, 'success');
      this.hasUnsavedChanges = true;
      this.$nextTick(() => this.rebuildFrontendTreeState());
      return;
    }
  
    // --- CASE 2: Node is derived, can't be deleted directly ---
    this.showSnackbar(`Cannot delete "${nodeName}". This node is derived from backend data or conditions and cannot be deleted directly. Edit the parent rule instead.`, 'warning', 6000);
  },  
  
  // ADD THIS HELPER METHOD to find the correct node name to exclude
  findImmediateChildOfLinkedParent(linkedParentNode, deepChildNode) {
      let currentNode = deepChildNode;
      let parent = this.findParentOfNode(this.displayedFrontendTree, currentNode.id);
      while (parent && parent.id !== linkedParentNode.id) {
          currentNode = parent;
          parent = this.findParentOfNode(this.displayedFrontendTree, currentNode.id);
      }
      return currentNode;
    },

    _addRule(rule, parentId) {
      if (parentId) {
        // Find parent and add to its children array
        const findAndAddChild = (rulesArray) => {
          for (const parentRule of rulesArray) {
            if (parentRule.id === parentId) {
              if (!parentRule.children) parentRule.children = [];
              parentRule.children.push(rule);
              return true;
            }
            if (parentRule.children && findAndAddChild(parentRule.children)) {
              return true;
            }
          }
          return false;
        };
        findAndAddChild(this.frontendRules.categories);
      } else {
        // Add to top-level categories
        this.frontendRules.categories.push(rule);
      }
    
      this.hasUnsavedChanges = true;
      // CRITICAL: Trigger the update.
      this.$nextTick(() => this.rebuildFrontendTreeState());
    },
    addLinkedBackendCategoryRule(parentId, backendPath) {
      // If a parent is specified, this is a child link. Otherwise, it's a top-level link.
      const parentRule = parentId ? this.findRuleById(this.frontendRules.categories, parentId) : null;
  
      if (parentId && (!parentRule || parentRule.type === 'linked_root' || parentRule.type === 'linked_category')) {
          this.showSnackbar('Cannot add linked children to an already linked category.', 'error');
          return;
      }
  
      const newRule = {
          id: `linked_${Date.now()}`,
          name: backendPath.split('|').pop(),
          type: 'linked_category', // Child links are always of this type
          backend_path: backendPath,
          exclusions: [],
          include_attributes: true
      };
      
      if (parentRule) {
          if(!parentRule.children) parentRule.children = [];
          parentRule.children.push(newRule);
      } else {
          // This is a top-level link, so its type is 'linked_root'
          newRule.type = 'linked_root';
          this.frontendRules.categories.push(newRule);
      }
  
      this.hasUnsavedChanges = true;
      this.showSnackbar(`Added linked category rule for "${newRule.name}".`, 'success');
      this.$nextTick(() => this.rebuildFrontendTreeState());
    },
    addManualFrontendNodeRule(parentId) {
      console.log(parentId ? `Creating a new CHILD manual rule for parent: ${parentId}` : "Creating a new TOP-LEVEL manual rule.");
    
      // 1. Create the rule object for the new manual node using simplified structure
      const newRule = {
        id: `manual_${Date.now()}`,
        name: 'New Manual Category',
        type: 'manual_node',
        backend_categories: [], // Can be added later via rule editor
        children: []
      };
    
      // 2. Find the parent rule and add the new rule to its 'children' array.
      let parentFound = false;
      if (parentId) {
        const findAndAddChild = (rulesArray) => {
          for (const parentRule of rulesArray) {
            if (parentRule.id === parentId) {
              if (!Array.isArray(parentRule.children)) {
                parentRule.children = [];
              }
              parentRule.children.push(newRule);
              parentFound = true;
              return true;
            }
            if (parentRule.children && findAndAddChild(parentRule.children)) {
              return true;
            }
          }
          return false;
        };
        findAndAddChild(this.frontendRules.categories);
      } else {
        this.frontendRules.categories.push(newRule);
        parentFound = true;
      }
    
      // 3. If we successfully added the rule, trigger the visual update.
      if (parentFound) {
        this.showSnackbar(`Added "${newRule.name}". Save changes to make it permanent.`, 'success');
        this.hasUnsavedChanges = true;
        
        this.$nextTick(() => {
          this.rebuildFrontendTreeState();
        });
      } else {
        this.showSnackbar(`Error: Could not find parent rule with ID ${parentId}.`, 'error');
      }
    },

    findParentOfNode(rootNode, targetId, parentNode = null) {
      if (!rootNode) return null;
      if (rootNode.id === targetId) return parentNode;
      if (Array.isArray(rootNode.children)) {
        for (const child of rootNode.children) {
          const found = this.findParentOfNode(child, targetId, rootNode);
          if (found) return found;
        }
      }
      return null;
    },
    findParentIdOfNode(nodeId) {
      // Find the parent node ID in the taxonomy data structure
      const findParent = (node, targetId, parentId = null) => {
        if (node.id === targetId) {
          return parentId;
        }
        
        if (node.subcategories && Array.isArray(node.subcategories)) {
          for (const child of node.subcategories) {
            const result = findParent(child, targetId, node.id);
            if (result !== null) {
              return result;
            }
          }
        }
        
        return null;
      };
      
      // Search through all top-level nodes
      const topLevelData = this.taxonomyData[this.selectedTopLevelNode];
      if (topLevelData) {
        return findParent(topLevelData, nodeId, null);
      }
      
      return null;
    },
    hasDuplicateName(parentNode, newName, excludeNodeId = null) {
      // Check if a sibling node already has this name (case-insensitive)
      if (!parentNode || !parentNode.subcategories) {
        return false;
      }
      
      const normalizedNewName = newName.trim().toLowerCase();
      
      return parentNode.subcategories.some(child => {
        // Skip the node being edited (when renaming)
        if (excludeNodeId && child.id === excludeNodeId) {
          return false;
        }
        return child.name.toLowerCase() === normalizedNewName;
      });
    },
    saveNodeName() {
      const trimmedName = this.editNodeName.trim();
      
      // Validate name is not empty
      if (!trimmedName) {
        this.showSnackbar('Node name cannot be empty', 'error');
        return;
      }

      if (this.editNodeId) {
        // Existing code for editing a node
        const node = this.findTreeItemById(this.treeItems[0], this.editNodeId);
        if (node) {
          const nodeData = this.nodeIdMap[this.editNodeId];
          
          // Find parent to check for duplicates
          const parentId = this.findParentIdOfNode(this.editNodeId);
          const parentData = parentId ? this.nodeIdMap[parentId] : null;
          
          // Check for duplicate name among siblings
          if (parentData && this.hasDuplicateName(parentData, trimmedName, this.editNodeId)) {
            this.showSnackbar('A node with this name already exists at this level', 'error');
            return;
          }
          
          node.label = trimmedName;
          if (nodeData) {
            nodeData.name = trimmedName;
          }
        }
        this.showSnackbar('Node renamed successfully', 'success');
      } else if (this.pendingParentNodeId) {
        // New node creation
        const parentData = this.nodeIdMap[this.pendingParentNodeId];
        if (!parentData) {
          console.error('Parent node not found');
          this.editNodeDialog = false;
          return;
        }

        // Check for duplicate name among siblings
        if (this.hasDuplicateName(parentData, trimmedName)) {
          this.showSnackbar('A node with this name already exists at this level', 'error');
          return;
        }

        const newTaxonomyNode = {
          id: `be_id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // <-- ADD THIS LINE
          name: trimmedName,
          filters: [],
          subcategories: [],
          derived_from_attribute: null,
        };

        if (!parentData.subcategories) {
          parentData.subcategories = [];
        }

        // Add new node to parent
        parentData.subcategories.push(newTaxonomyNode);

        // Clear pending parent ID
        this.pendingParentNodeId = null;

        // Rebuild tree to reflect changes
        this.loadTopLevelNode();
        this.showSnackbar('New node added', 'success');
      }

      // Reset dialog state
      this.editNodeDialog = false;
      this.editNodeId = this.originalEditNodeId;
    },
    /**
     * --------------------------------------------------------------------
     * This method preserves the current expansion state and scroll position
     * before rebuilding the tree, ensuring the user sees the same expansions
     * and view afterward.
     * --------------------------------------------------------------------
     */
    addNode(parentNodeId) {
      // Store the parent node ID
      this.pendingParentNodeId = parentNodeId;

      // Reset edit dialog to "create" mode
      this.editNodeName = "New Node";
      this.editNodeDialog = true;

      // Save the original node ID behavior for later
      this.originalEditNodeId = this.editNodeId;
      this.editNodeId = null;
    },
    deleteNode(nodeId) {
      // This function preserves the tree's scroll and expansion state,
      // which is the good part of the first (ignored) implementation.
      this.preserveTreePosition(() => {
        // --- Start of New, Robust Deletion Logic ---

        // A recursive function to find the node's parent and remove the node.
        // It works with any kind of node ID, not just path-based ones.
        const findAndRemove = (parent, targetId) => {
          // Check if the target is a direct child of the current parent node.
          if (parent.subcategories && Array.isArray(parent.subcategories)) {
            const index = parent.subcategories.findIndex(child => child && child.id === targetId);
            if (index !== -1) {
              // Found the node. Remove it from the parent's subcategories.
              parent.subcategories.splice(index, 1);
              return true; // Indicate success
            }
          }

          // If not found, recurse into the children of the current parent.
          if (parent.subcategories && Array.isArray(parent.subcategories)) {
            for (const child of parent.subcategories) {
              // If findAndRemove returns true, the node was found and removed in a deeper branch.
              if (findAndRemove(child, targetId)) {
                return true; // Propagate success signal up the call stack.
              }
            }
          }
          return false; // Node not found in this branch.
        };

        let deleted = false;
        // Get the root of the currently displayed category tree from the raw taxonomy data.
        const rootNode = this.taxonomyData[this.selectedTopLevelNode];

        if (rootNode) {
          // Start the search and removal process from the root of the current tree.
          deleted = findAndRemove(rootNode, nodeId);
        }

        if (deleted) {
          this.showSnackbar('Node deleted successfully', 'success');
          this.hasUnsavedChanges = true;

          // If the deleted node was selected, clear the right-pane selection.
          if (this.selectedNodeId === nodeId) {
            this.selectedNode = null;
            this.selectedNodeId = null;
          }

          // Rebuild the visual tree from the modified data. This is now done
          // automatically by `preserveTreePosition` which calls `loadTopLevelNode`.
          // We don't need to call it manually here anymore.

        } else {
          this.showSnackbar('Error: Could not find node in data to delete.', 'error');
        }

        // --- End of New, Robust Deletion Logic ---
      });

      // After the tree is modified inside preserveTreePosition, we must
      // manually trigger a reload of the visual tree data to reflect the change.
      this.$nextTick(() => {
        this.loadTopLevelNode();
      });
    },
    findTreeItemById(rootItem, id) {
      if (!rootItem) return null;
      if (rootItem.id === id) return rootItem;
      if (rootItem.children) {
        for (const child of rootItem.children) {
          const found = this.findTreeItemById(child, id);
          if (found) return found;
        }
      }
      return null;
    },
    removeNodeFromParent(currentData, segments, basePath, level = 0) {
      if (!currentData) return;
      if (level === 0 && segments[0] === basePath) {
        segments.shift();
      }
      if (segments.length === 0) {
        return;
      }
      const head = segments[0];
      if (head.startsWith('[') && head.endsWith(']')) {
        const idxStr = head.slice(1, -1);
        const idx = parseInt(idxStr, 10);
        if (segments.length === 1) {
          if (Array.isArray(currentData.subcategories) && idx >= 0 && idx < currentData.subcategories.length) {
            currentData.subcategories.splice(idx, 1);
          }
        } else {
          if (Array.isArray(currentData.subcategories) && idx >= 0 && idx < currentData.subcategories.length) {
            segments.shift();
            this.removeNodeFromParent(currentData.subcategories[idx], segments, basePath, level + 1);
          }
        }
      } else {
        if (segments.length === 1) {
          if (head in currentData) {
            delete currentData[head];
          }
        } else {
          const childData = currentData[head];
          segments.shift();
          if (childData && typeof childData === 'object') {
            this.removeNodeFromParent(childData, segments, basePath, level + 1);
          }
        }
      }
    },
    startEditingNode(nodeId) {
      const nodeData = this.nodeIdMap[nodeId];
      if (!nodeData) return;
      this.editNodeId = nodeId;
      this.editNodeName = nodeData.name || '';
      this.editNodeDialog = true;
    },
    toggleFilter(attrId) {
      const idx = this.expandedFilters.indexOf(attrId);
      if (idx === -1) {
        this.expandedFilters.push(attrId);
      } else {
        this.expandedFilters.splice(idx, 1);
      }
    },
    addFilter() {
      const name = this.newFilterName.trim();
      if (!name) {
          this.showSnackbar('Attribute name cannot be empty', 'error');
          return;
      }
      if (!this.isAttributeNameUnique(name)) {
          this.showSnackbar(`Attribute name "${name}" already exists`, 'error');
          return;
      }

      // We are adding a new attribute definition, which is always backend-centric.
      // It should ONLY be allowed in the backendTaxonomyEditor view
      // or perhaps when adding to a *manual_node* type rule in frontendRuleBuilder.
      if (this.currentView !== 'backendTaxonomyEditor' && this.selectedNode && !this.selectedNode.isRuleNode) {
          // For frontend preview, we don't allow adding attributes.
          // For other views like attribute editor, "add new attribute" is handled by openNameDialog/createNewAttribute.
          this.showSnackbar('Attributes can only be added to Backend Taxonomy nodes or via Attribute Editor.', 'warning');
          return;
      }

      // Create the new attribute in the global `attributeData`
      // This method also updates `taxonomyData._metadata.historicalAttributeIds`.
      const newAttr = this.createNewAttribute(name);
      if (!newAttr) {
          this.showSnackbar('Failed to create attribute', 'error');
          return;
      }
      const newAttrId = newAttr.attribute_id;

      // Now, assign this newly created attribute to the currently selected node.
      const nodeData = this.nodeIdMap[this.selectedNodeId]; // This is the displayed node object
      if (!nodeData) {
          this.showSnackbar('No node selected to add attribute to.', 'error');
          return;
      }

      if (!Array.isArray(nodeData.filters)) {
          nodeData.filters = [];
      }
      if (!Array.isArray(nodeData.attribute_ids)) {
          nodeData.attribute_ids = [];
      }

      // Add the new attribute's ID to the node's filters/attribute_ids/metadata
      nodeData.filters.push(newAttrId);
      nodeData.attribute_ids.push(newAttrId);
      nodeData.attribute_metadata[newAttrId] = DEFAULT_ATTRIBUTE_METADATA();

      // Update the attribute's categories list (which lives on the attribute data itself)
      if (!newAttr.categories) newAttr.categories = [];
      const nodePath = this.getNodePathById(this.selectedNodeId);
      if (nodePath && !newAttr.categories.includes(nodePath)) {
          newAttr.categories.push(nodePath);
      }

      // If currently in backendTaxonomyEditor, the `taxonomyData` is `backendTaxonomyData`
      // and its changes are directly reflected.
      // If it's a `manual_node` type rule in frontendRuleBuilder, the change needs to be propagated back to the rule object.
      if (this.currentView === 'frontendRuleBuilder' && nodeData.type === 'manual_node' && nodeData.isRuleNode) {
          // Find the actual rule object in `this.frontendRules.categories`
          const findRuleById = (rulesArray, id) => {
              for (const rule of rulesArray) {
                  if (rule.id === id) return rule;
                  if (rule.children && Array.isArray(rule.children)) {
                      const foundChild = findRuleById(rule.children, id);
                      if (foundChild) return foundChild;
                  }
              }
              return null;
          };
          const manualRuleInRules = findRuleById(this.frontendRules.categories, this.selectedNodeId);
          if (manualRuleInRules) {
              // Update the attribute_ids array in the rule definition
              if (!Array.isArray(manualRuleInRules.attribute_ids)) manualRuleInRules.attribute_ids = [];
              manualRuleInRules.attribute_ids.push(newAttrId);
              // The `filters` property on the rule itself is just for internal display sync in this case.
              manualRuleInRules.filters = [...manualRuleInRules.attribute_ids]; // Keep in sync for serialization
          }
      }

      // Directly add the new attribute to editableFilters for immediate display
      // This is more reliable than refreshSelectedNodeFilters for reactivity
      this.editableFilters = {
        ...this.editableFilters,
        [newAttrId]: {
          displayName: this.getDisplayName(newAttr),
          values: []
        }
      };
      // Directly add the new attribute to the selected node's metadata
      this.selectedNode.metadata[newAttrId] = DEFAULT_ATTRIBUTE_METADATA();
      
      // Also update editableFilterNames
      this.editableFilterNames = {
        ...this.editableFilterNames,
        [newAttrId]: this.getDisplayName(newAttr)
      };

      // Mark changes
      this.hasUnsavedChanges = true;
      this.newFilterName = '';
      this.showSnackbar(`Added attribute "${name}" to node and global attribute list.`, 'success');

      // Optional: Update the tree style if node now has attributes
      const treeItem = this.findTreeItemById(this.treeItems[0], this.selectedNodeId);
      if (treeItem) {
          treeItem.filters = nodeData.filters; // Update the filters property on the tree item
          // Apply correct class based on whether it now has attributes
          if (treeItem.filters && treeItem.filters.length > 0) {
               // Only set if not already a derived node class
              if (!treeItem.className.includes('node-derived')) {
                  treeItem.className = 'node-with-attributes';
              }
          } else if (!treeItem.className.includes('node-derived')) {
               treeItem.className = ''; // Remove class if no attributes and not derived
          }
      }
    },
    findAttributeIdByDisplayName(filterName) {
      // Add null/undefined check
      if (!filterName || typeof filterName !== 'string') {
        console.warn('findAttributeIdByDisplayName called with invalid filterName:', filterName);
        return null;
      }
    
      // Extract the display name part from composite strings like "Display Name [Internal Name]"
      const displayNameOnly = filterName.split(' [')[0];
    
      for (const [id, obj] of Object.entries(this.attributeData)) {
        if (this.getDisplayName(obj) === displayNameOnly || obj.name === displayNameOnly) {
          return id;
        }
      }
      return null;
    },
    getNextAvailableAttributeId() {
      // Get IDs from multiple sources to find the true maximum
      const historicalIds = this.taxonomyData._metadata?.historicalAttributeIds || [];
      const currentAttributeIds = Object.keys(this.attributeData || {});
      
      // Combine all known IDs
      const allIds = [...historicalIds, ...currentAttributeIds]
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));
      
      if (allIds.length === 0) return '60001000'; // Starting ID for new taxonomies (in 60001xxx range)
      
      const maxId = Math.max(...allIds);
      return String(maxId + 1);
    },
    removeFilter(attrId) {
      // Update UI states
      delete this.editableFilters[attrId];
      delete this.editableFilterNames[attrId];
      delete this.selectedNode.metadata[attrId];
      const idx = this.expandedFilters.indexOf(attrId);
      if (idx !== -1) {
        this.expandedFilters.splice(idx, 1);
      }
    
      // Get the node's data
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) return;
    
      // Remove from attribute_ids array
      if (Array.isArray(nodeData.attribute_ids)) {
        nodeData.attribute_ids = nodeData.attribute_ids.filter(id => String(id) !== String(attrId));
      }
    
      // Remove from filters array
      if (Array.isArray(nodeData.filters)) {
        nodeData.filters = nodeData.filters.filter(id => String(id) !== String(attrId));
      }

      // Remove from metadata
      delete nodeData.attribute_metadata[attrId];
    
      // Remove the node's path from the attribute's categories
      const attrObj = this.attributeData[attrId];
      if (attrObj && attrObj.categories) {
        const nodePath = this.getNodePathById(this.selectedNodeId);
        const pathIndex = attrObj.categories.indexOf(nodePath);
        if (pathIndex !== -1) {
          attrObj.categories.splice(pathIndex, 1);
        }
      }
    
      // Update selected node UI state
      if (this.selectedNode.filters && this.selectedNode.filters[attrId]) {
        delete this.selectedNode.filters[attrId];
      }
    
      // Refresh the tree
      this.loadTopLevelNode();
    },
    scanAllFilterIds() {
      const setOfIds = new Set();
      for (const topLevelKey of this.getTopLevelNodes(this.taxonomyData)) {
        const nodeData = this.taxonomyData[topLevelKey];
        if (nodeData) {
          this.collectAllFilterIds(nodeData, setOfIds);
        }
      }
      return setOfIds;
    },
    collectAllFilterIds(nodeData, setOfIds = new Set()) {
      if (!nodeData) return setOfIds;
      if (Array.isArray(nodeData.filters)) {
        nodeData.filters.forEach((fId) => setOfIds.add(fId));
      }
      if (Array.isArray(nodeData.subcategories)) {
        nodeData.subcategories.forEach((sub) => {
          if (sub && typeof sub === 'object') {
            this.collectAllFilterIds(sub, setOfIds);
          }
        });
      }
      Object.keys(nodeData).forEach((k) => {
        if (
          !this.keysToSkip.includes(k) &&
          k !== 'filters' &&
          k !== 'name' &&
          k !== 'subcategories' &&
          k !== 'sources'
        ) {
          const child = nodeData[k];
          if (child && typeof child === 'object') {
            this.collectAllFilterIds(child, setOfIds);
          }
        }
      });
      return setOfIds;
    },
    addFilterValue(attrId) {
      const val = (this.newFilterValues[attrId] || '').trim();
      if (!val) return;
    
      this.editableFilters[attrId].values.push(val);
    
      const attrObj = this.attributeData[attrId];
      if (attrObj) {
        const valId = this.nextValueId(attrObj);
        const maxIndex = Math.max(...Object.values(attrObj.values || {}).map(v => v.value_sort_index || 0), -1);
        attrObj.values = attrObj.values || {};
        attrObj.values[valId] = {
          value_en: val,
          value_de: '',
          value_fr: '',
          value_it: '',
          value_sort_index: maxIndex + 1,
          notes: '',
          sources: []
        };
        if (!attrObj.historicalValueIds) attrObj.historicalValueIds = [];
        if (!attrObj.historicalValueIds.includes(valId)) {
          attrObj.historicalValueIds.push(valId);
        }
        if (attrObj.translations) {
          attrObj.translations.value_de = attrObj.translations.value_de || [];
          attrObj.translations.value_fr = attrObj.translations.value_fr || [];
          attrObj.translations.value_it = attrObj.translations.value_it || [];
          attrObj.translations.value_de.push('');
          attrObj.translations.value_fr.push('');
          attrObj.translations.value_it.push('');
        }
      }
    
      this.newFilterValues[attrId] = '';
    },
    removeFilterValue(attrId, idx) {
      const val = this.editableFilters[attrId].values[idx];
      this.editableFilters[attrId].values.splice(idx, 1);
    
      const attrObj = this.attributeData[attrId];
      if (!attrObj || !val) return;
    
      const keyToDelete = Object.keys(attrObj.values).find(key => attrObj.values[key].value_en === val);
      if (keyToDelete) {
        const valueObj = attrObj.values[keyToDelete];
        const sortIndex = valueObj.value_sort_index;
        delete attrObj.values[keyToDelete];
    
        if (attrObj.translations?.value_de && sortIndex < attrObj.translations.value_de.length) {
          attrObj.translations.value_de.splice(sortIndex, 1);
        }
        if (attrObj.translations?.value_fr && sortIndex < attrObj.translations.value_fr.length) {
          attrObj.translations.value_fr.splice(sortIndex, 1);
        }
        if (attrObj.translations?.value_it && sortIndex < attrObj.translations.value_it.length) {
          attrObj.translations.value_it.splice(sortIndex, 1);
        }
      }
    
      if (attrObj.value_sources) delete attrObj.value_sources[val];
      if (attrObj.value_notes) delete attrObj.value_notes[val];
    },
    getNodePathById(nodeId) {
      if (!this.nodeIdMap || !nodeId) return '';

      const segments = nodeId.split('.');
      let currentNode = this.nodeIdMap[segments[0]];
      if (!currentNode) return '';

      let path = [currentNode.name || segments[0]];

      for (let i = 1; i < segments.length; i++) {
        const segment = segments[i];
        if (segment.startsWith('[') && segment.endsWith(']')) {
          const index = parseInt(segment.slice(1, -1), 10);
          if (currentNode.subcategories && currentNode.subcategories[index]) {
            currentNode = currentNode.subcategories[index];
            path.push(currentNode.name || `Unnamed[${index}]`);
          } else {
            return '';
          }
        } else {
          if (currentNode[segment]) {
            currentNode = currentNode[segment];
            path.push(currentNode.name || segment);
          } else {
            return '';
          }
        }
      }
      return path.join('/');
    },
    saveFilterName(attrId) {
      const stringAttrId = String(attrId);
      
      if (!this.editableFilters[stringAttrId]) return;
    
      const newDisplayName = (this.editableFilters[stringAttrId].displayName || '').trim();
      
      const attr = this.getAttributeById(stringAttrId);
      if (!attr) return;
    
      if (!newDisplayName) {
        // If user cleared the name, revert to the original name from master data.
        this.editableFilters[stringAttrId].displayName = this.getDisplayName(attr);
        this.editingFilterName = null;
        return;
      }
    
      // Update the master attribute data's display name.
      if (typeof attr.display_name !== 'object' || attr.display_name === null) {
        attr.display_name = { en: '' }; // Ensure it's a valid object
      }
      attr.display_name.en = newDisplayName;
      
      // Since the name was changed, we must regather all filters for the dropdown.
      this.gatherAllFilters();
      
      this.editingFilterName = null; // Exit editing mode
      this.hasUnsavedChanges = true;
    },
    startEditingValue(filterName, index) {
      // Don't allow editing in backend taxonomy editor view
      if (this.currentView === 'backendTaxonomyEditor') {
        return;
      }
      this.editingValue = { filter: filterName, index };
    },
    stopEditingValue() {
      this.editingValue = { filter: null, index: null };
    },
    openValueNotesDialog(attrId, value_en) {
      const attr = this.attributeData[attrId];
      if (!attr || !attr.values) {
        console.error(`Attribute #${attrId} or its values are not defined`);
        return;
      }

      // Find the value object key where value_en matches
      const valueKey = Object.keys(attr.values).find(
        (key) => attr.values[key].value_en === value_en
      );
      if (!valueKey) {
        console.error(`Value "${value_en}" not found in attribute #${attrId}`);
        return;
      }

      const valueObj = attr.values[valueKey];
      this.valueNotesDialog.attrId = attrId;
      this.valueNotesDialog.value = value_en;
      this.valueNotesDialog.valueKey = valueKey; // Store the key (e.g., "70000001")
      this.valueNotesDialog.tempNote = valueObj.notes || ""; // Load existing note
      this.valueNotesDialog.show = true;
    },
    saveValueNotes() {
      const { attrId, valueKey, tempNote } = this.valueNotesDialog;
      if (!attrId || !valueKey) {
        console.error("Invalid attribute ID or value key");
        return;
      }

      const attr = this.attributeData[attrId];
      if (attr && attr.values && attr.values[valueKey]) {
        attr.values[valueKey].notes = tempNote; // Update the notes field
      } else {
        console.error(`Cannot save note: value #${valueKey} not found in attribute #${attrId}`);
      }
      this.valueNotesDialog.show = false; // Close the dialog
    },
    async saveChanges() {
      // Prevent rapid double-clicks from triggering multiple saves
      if (this._saveInProgress) {
        console.log('Save already in progress, ignoring duplicate request');
        return;
      }
      this._saveInProgress = true;
      
      try {
        console.log('saveChanges called.');
    
      // 1. Sync displayed filters back into attributeData (for backend editor)
      // This logic now uses the attribute ID as the key, preventing data corruption.
      if (this.currentView === 'backendTaxonomyEditor' && this.selectedNodeId) {
        for (const [attrId, filterData] of Object.entries(this.editableFilters)) {
          const stringAttrId = String(attrId);
          const attr = this.attributeData[stringAttrId];
          
          if (attr) {
            const existingValuesMap = new Map();
            if (attr.values) {
              Object.entries(attr.values).forEach(([valueKey, valueObj]) => {
                existingValuesMap.set(valueObj.value_en, { key: valueKey, obj: valueObj });
              });
            }
    
            const newValuesObject = {};
            const newValuesArray = filterData.values || [];
            
            newValuesArray.forEach(val_en => {
              if (existingValuesMap.has(val_en)) {
                // Preserve existing value object if the value still exists
                const { key, obj } = existingValuesMap.get(val_en);
                newValuesObject[key] = obj;
              } else {
                // This is a new value, create a new entry for it
                const newValId = this.nextValueId(attr);
                const maxIndex = Object.keys(newValuesObject).length;
                newValuesObject[newValId] = {
                  value_en: val_en,
                  value_de: '', value_fr: '', value_it: '',
                  value_sort_index: maxIndex,
                  notes: '', sources: []
                };
                if (!attr.historicalValueIds) attr.historicalValueIds = [];
                attr.historicalValueIds.push(newValId);
              }
            });
    
            attr.values = newValuesObject;
            // Optionally, you can resync the `valuesArray` for immediate UI consistency if needed, though it will be recalculated on next load.
            attr.valuesArray = this.getValuesArray(attr);
          }
        }
      }

      // Basic validation
      if (!this.selectedTaxonomy) {
          this.showSnackbar('No taxonomy selected', 'warning');
          return;
      }

      // Normalize data before saving (applies to whatever `taxonomyData` and `attributeData` currently represent)
      this.normalizeDataBeforePublish();

      // 2. Determine target filename and version
      const baseFilename = this.selectedTaxonomy.replace('.json', '');
      let versionToSave = this.saveVersion.trim(); // User-entered save version
      let description = this.versionDescription || `Changes saved on ${new Date().toLocaleString()}`;

      // ============================================================================
      // CONFLICT DETECTION - Only check data types that have unsaved changes
      // ============================================================================
      const isFrontend = this.selectedTaxonomy?.startsWith('frontend');
      const dataTypesToCheck = [];
      
      if (isFrontend) {
        if (this.unsavedChanges?.frontend) {
          dataTypesToCheck.push('frontend');
        }
      } else {
        if (this.unsavedChanges?.taxonomy) {
          dataTypesToCheck.push('taxonomy');
        }
        if (this.unsavedChanges?.attributes) {
          dataTypesToCheck.push('attributes');
        }
        if (this.unsavedChanges?.gpcMappings) {
          dataTypesToCheck.push('gpcMappings');
        }
      }
      
      for (const dataType of dataTypesToCheck) {
        const hasConflict = await this.checkForConflict(dataType);
        if (hasConflict) {
          // Conflict dialog will be shown, abort save
          return;
        }
      }
      // ============================================================================

      if (!['test', 'production', 'QA Version'].includes(versionToSave)) {
        // TODO: Move to better spot
        await saveSizeMapperData(baseFilename, versionToSave, this.sizeMapperData);
        await saveStyleGuideRules(versionToSave, baseFilename, this.styleRules);
      }

      // Special handling for Working Version and QA Version
      if (this.loadedVersion === 'Working Version' || this.loadedVersion === 'QA Version') {
          try {
              // Create timestamped backup only for Working Version
              // Note: Backups skip conflict check (skipConflictCheck=true) since they're new documents
              if (this.loadedVersion === 'Working Version') {
                  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                  const backupVersionName = `Working Version Backup ${timestamp}`;
                  await this.saveVersionToCloud(backupVersionName, `Automatic backup of Working Version created on ${new Date().toLocaleString()}`, true);
                  console.log(`Created automatic backup: ${backupVersionName}`);
              }

              // Then update the version itself (either Working or QA)
              // This call DOES do conflict checking (skipConflictCheck=false, the default)
              await this.saveVersionToCloud(versionToSave, this.versionDescription || `${versionToSave} updated on ${new Date().toLocaleString()}`);

              // Clear all unsaved changes flags
              await this.clearUnsavedChanges('all');
              
              // Note: saveVersionToCloud already updates loadedTimestamps from server response
              // This is a fallback in case server doesn't return timestamps (e.g., for new versions)
              
              // Update session
              this.registerSession();
              
              this.showSnackbar(`${versionToSave} updated successfully` +
                  (this.loadedVersion === 'Working Version' ? ' with backup' : ''), 'success');

              await this.loadTaxonomyVersions(); // Refresh versions list
              this.loadVersion = versionToSave; // Ensure dropdown reflects the saved version
              return; // Exit after handling special versions
          } catch (error) {
              // Handle server-side conflict detection (409 response)
              if (error.isConflict) {
                console.log('Server detected conflict during save:', error.conflictData);
                this.conflictDialog.dataType = error.dataType || 'taxonomy';
                this.conflictDialog.serverUpdatedAt = error.conflictData?.currentUpdatedAt || null;
                this.conflictDialog.loadedAt = error.conflictData?.expectedUpdatedAt || null;
                this.conflictDialog.show = true;
                return;
              }
              console.error(`Failed to update ${this.loadedVersion}:`, error);
              this.showSnackbar(`Failed to update ${this.loadedVersion}: ${error.message}`, 'error');
              return;
          }
      }

      // Handle custom version save if specified (or default to date)
      if (versionToSave) {
          // Check if trying to save as one of our main version names
          if (['production', 'test', 'Working Version', 'QA Version'].includes(versionToSave)) {
              if (versionToSave.toLowerCase() === 'production' || versionToSave.toLowerCase() === 'test') {
                  this.showSnackbar('Cannot save directly to "production" or "test". Use "Publish Taxonomy" instead.', 'error');
                  return;
              } else if (versionToSave === 'Working Version') {
                  this.showSnackbar('To update Working Version, load it first then use Save Changes', 'info');
                  return;
              } else if (versionToSave === 'QA Version') {
                  this.showSnackbar('To update QA Version, use Set Working Version or load it first', 'info');
                  return;
              }
          }
          const validation = this.validateVersionName(versionToSave);
          if (!validation.valid) {
              this.showSnackbar(validation.message, 'error');
              return;
          }
      } else {
          // If no custom version name, use current date. This applies to new saves only.
          versionToSave = new Date().toISOString().split('T')[0];
      }

      // 3. Build metadata for the save
      const metadata = {
        version: versionToSave,
        description: description,
        createdAt: new Date().toISOString()
      };

      // 4. Save based on selected taxonomy type
      if (this.selectedTaxonomy?.startsWith('frontend')) {
        // For the frontend, we only save the RULES file.
        const rulesPath = `allium-ai-manor/taxonomies/${baseFilename}_modified_${versionToSave}.json`;
        const payload = {
          path: rulesPath,
          data: this.frontendRules, // CRITICAL: Save the rules object
          metadata: metadata
        };
    
        const response = await fetch(`${this.FUNCTION_URL}?action=save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
    
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Save failed for frontend rules: ${errorText}`);
        }
        await response.json(); // Wait for the save to complete
    
      } else {
        // For the backend, we save BOTH the taxonomy structure and the attributes file.
        const modPath = `allium-ai-manor/taxonomies/${baseFilename}_modified_${versionToSave}.json`;
        const attPath = `allium-ai-manor/taxonomies/${baseFilename}_attributes_${versionToSave}.json`;
    
        const payloadMod = { path: modPath, data: this.taxonomyData, metadata: metadata };
        const payloadAtt = { path: attPath, data: this.attributeData, metadata: metadata };
    
        const [resMod, resAtt] = await Promise.all([
          fetch(`${this.FUNCTION_URL}?action=save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payloadMod)
          }),
          fetch(`${this.FUNCTION_URL}?action=save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payloadAtt)
          })
        ]);
    
        if (!resMod.ok || !resAtt.ok) {
          throw new Error(`Save failed with statuses Mod:${resMod.status}, Att:${resAtt.status}`);
        }
        await Promise.all([resMod.json(), resAtt.json()]); // Wait for both saves
        
        // Also save GPC mappings if they have unsaved changes
        if (this.gpcMappings && this.gpcMappings.length > 0 && this.unsavedChanges?.gpcMappings) {
          try {
            const gpcPayload = {
              filename: baseFilename,
              version: versionToSave,
              mappings: this.gpcMappings,
              metadata: {
                description: `GPC mappings for ${baseFilename}`,
                lastUpdated: new Date().toISOString(),
                taxonomyVersion: versionToSave
              }
            };
            
            const gpcResponse = await fetch(`${this.FUNCTION_URL}?action=saveGpcMappings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(gpcPayload)
            });
            
            if (!gpcResponse.ok) {
              console.warn('GPC mappings save failed, but taxonomy saved successfully');
            } else {
              console.log('GPC mappings saved alongside taxonomy');
            }
          } catch (gpcError) {
            console.warn('Failed to save GPC mappings:', gpcError.message);
            // Don't fail the whole save for GPC mappings
          }
        }
      }
    
      // 5. Update UI state after successful save
      this.hasUnsavedChanges = false;
      this.unsavedChanges = { taxonomy: false, attributes: false, gpcMappings: false, frontend: false };
      this.loadedVersion = versionToSave;
      this.saveVersion = ''; // Clear the save input
      this.versionDescription = '';
      
      // Update loaded timestamps after successful save
      const now = new Date().toISOString();
      if (this.selectedTaxonomy?.startsWith('frontend')) {
        this.loadedTimestamps.frontend = now;
      } else {
        this.loadedTimestamps.taxonomy = now;
        this.loadedTimestamps.attributes = now;
        // Only update GPC timestamp if we actually saved GPC mappings
        // (This is a fallback - ideally the server response provides the timestamp)
      }
      
      // Update session to reflect saved state
      this.registerSession();
      
      // Refresh versions list to show the new version
      await this.loadTaxonomyVersions();
      this.loadVersion = versionToSave;
      
      this.showSnackbar(`Saved as version "${versionToSave}"`, 'success');
      
      return true; // Indicate success
      } finally {
        this._saveInProgress = false;
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
      setTimeout(() => {
        this.snackbar.show = false;
      }, 2000);
    },
    
    /**
     * Format a timestamp for display in conflict dialogs
     */
    formatTimestamp(isoString) {
      if (!isoString) return 'Unknown';
      try {
        const date = new Date(isoString);
        return date.toLocaleString();
      } catch (e) {
        return isoString;
      }
    },
    
    // Enhanced filteredAllFilterNames
    filteredAllFilterNames() {
      const query = (this.addExistingFilterName || '').toLowerCase().trim();
      if (!query) return this.allFilterNames;

      // First look for filter names containing the query
      const nameMatches = this.allFilterNames.filter(name =>
        name.toLowerCase().includes(query)
      );

      // Then look for values that match the query
      const valueMatches = new Set();

      // Try exact match first
      if (this.valueToFilterMap[query]) {
        this.valueToFilterMap[query].forEach(filterName => {
          valueMatches.add(filterName);
        });
      }

      // Then try partial matches
      for (const [valueLower, filterNames] of Object.entries(this.valueToFilterMap)) {
        if (valueLower.includes(query)) {
          filterNames.forEach(filterName => {
            valueMatches.add(filterName);
          });
        }
      }

      // Combine both sets of matches (names and values), removing duplicates
      const allMatches = new Set([...nameMatches]);
      valueMatches.forEach(match => allMatches.add(match));

      return Array.from(allMatches).sort();
    },
    // Enhanced gatherAllFilters with proper data structure
    gatherAllFilters() {
      const filterItems = [];
      
      // Sort attributes by display name for a clean dropdown list
      const sortedAttrs = Object.entries(this.attributeData).sort(([, a], [, b]) => {
        return (this.getDisplayName(a) || '').localeCompare(this.getDisplayName(b) || '');
      });
    
      for (const [attrId, attObj] of sortedAttrs) {
        const displayName = this.getDisplayName(attObj) || `(Unnamed)`;
        const internalName = attObj.name || null;
        let finalDisplayName = displayName;
        
        // Always show internal name if it differs from display name
        if (internalName && internalName !== displayName) {
          finalDisplayName = `${displayName} [${internalName}]`;
        }
    
        filterItems.push({
          title: finalDisplayName, // This is what the user sees
          value: String(attrId)    // This is the value `v-model` will receive
        });
      }
    
      this.allFilterNames = filterItems; // `allFilterNames` is now an array of objects
    },
    collectFiltersRecursively(node) {
      if (Array.isArray(node.attribute_ids)) {
        node.attribute_ids.forEach((attrId) => {
          const attObj = this.attributeData[attrId];
          if (attObj) {
            const fName = attObj.display_name;
            if (!this.allFiltersMap[fName]) {
              this.allFiltersMap[fName] = new Set();
            }
            getValuesArray(attObj).forEach((valObj) => this.allFiltersMap[fName].add(valObj.value_en));
          }
        });
      }
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach((sub) => this.collectFiltersRecursively(sub));
      } else {
        const keys = Object.keys(node).filter(
          (k) =>
            !this.keysToSkip.includes(k) &&
            k !== 'filters' &&
            k !== 'name' &&
            k !== 'subcategories' &&
            k !== 'sources'
        );
        keys.forEach((k) => {
          const child = node[k];
          if (child && typeof child === 'object') {
            this.collectFiltersRecursively(child);
          }
        });
      }
    },
    onExistingFilterChange(selectedAttrId) {
      // This event now directly receives the attribute ID from the v-autocomplete
      if (!selectedAttrId) return;
    
      // Since we have the ID, we call the add logic directly.
      this.addExistingFilter(selectedAttrId);
      
      // Clear the autocomplete after selection for a better UX
      this.$nextTick(() => {
        this.addExistingFilterName = null;
      });
    },
    
    addExistingFilter(attrIdToAdd) {
      const stringAttrId = String(attrIdToAdd);
    
      if (!this.selectedNodeId) {
        this.showSnackbar('Please select a node first', 'error');
        return;
      }
    
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) {
        this.showSnackbar('Selected node data not found', 'error');
        return;
      }
      
      if (!Array.isArray(nodeData.attribute_ids)) nodeData.attribute_ids = [];
    
      if (nodeData.attribute_ids.includes(stringAttrId)) {
        this.showSnackbar('Filter already exists on this node', 'warning');
        return;
      }
      
      // Add the ID to the node's data
      nodeData.attribute_ids.push(stringAttrId);
      nodeData.attribute_metadata[stringAttrId] = DEFAULT_ATTRIBUTE_METADATA();
      // Also keep the 'filters' array in sync for legacy compatibility
      if (!Array.isArray(nodeData.filters)) nodeData.filters = [];
      nodeData.filters.push(stringAttrId);
    
      // Update the attribute's own list of categories
      const attrObj = this.getAttributeById(stringAttrId);
      if (attrObj) {
        if (!attrObj.categories) attrObj.categories = [];
        const nodePath = this.getNodePathById(this.selectedNodeId);
        if (nodePath && !attrObj.categories.includes(nodePath)) {
          attrObj.categories.push(nodePath);
        }
        this.showSnackbar(`Added filter "${this.getDisplayName(attrObj)}" to node`, 'success');
        
        // Directly update selected node's metadata
        this.selectedNode.metadata[attrIdToAdd] = DEFAULT_ATTRIBUTE_METADATA();

        // Directly update editableFilters for immediate display
        this.editableFilters = {
          ...this.editableFilters,
          [stringAttrId]: {
            displayName: this.getDisplayName(attrObj),
            values: this.getValuesArray(attrObj).map(vo => vo.value_en)
          }
        };
        
        this.editableFilterNames = {
          ...this.editableFilterNames,
          [stringAttrId]: this.getDisplayName(attrObj)
        };
      }
    
      this.hasUnsavedChanges = true;
    },
    startDrag(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startWidth = this.leftPaneWidth;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const delta = e.clientX - this.startX;
      this.leftPaneWidth = Math.max(300, this.startWidth + delta);
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    createAttributeWithName() {
      const name = this.newAttributeName.trim();
      if (!name) {
        this.showSnackbar('Attribute name cannot be empty', 'error');
        return;
      }
      if (!this.isAttributeNameUnique(name)) {
        this.showSnackbar(`Attribute name "${name}" already exists`, 'error');
        return;
      }
      this.createNewAttribute(name);
      this.nameDialog = false;
    },
    createNewAttribute(customName) {
      // Generate a unique ID for the new attribute
      const newId = this.getNextAvailableAttributeId();
      if (!newId) {
        this.showSnackbar('Failed to generate a unique attribute ID', 'error');
        return null;
      }

      // Determine the attribute name (use customName if provided, otherwise generate one)
      const attributeName = (typeof customName === 'string' && customName.trim())
        ? customName.trim()
        : `Attribute ${newId}`;

      // Directly assign the new attribute to attributeData
      this.attributeData[newId] = {
        name: attributeName,
        display_name: { en: attributeName, de: '', fr: '', it: '' },
        description: '',
        data_type: 'text',
        always_required: false,
        is_image_attribute: false,
        is_closed_list: false,
        filter_attribute: false,
        critical_attribute: false,
        secondary_allowed: false,
        customer_facing: true,
        notes: '',
        sort_order: 'Alphabetical',
        attribute_level: 'Product',
        attribute_unit: '',
        justCreated: true,
        attribute_id: newId,
        is_category_type_attribute: false,
        attribute_group: null,
        values: {},
        historicalValueIds: [], // Initialize empty historicalValueIds for new attributes
        translations: {
          value_de: [],
          value_fr: [],
          value_it: []
        },
        sources: []
      };

      // Ensure _metadata exists and add the new ID
      if (!this.taxonomyData._metadata) {
        this.taxonomyData._metadata = {};
      }
      if (!this.taxonomyData._metadata.historicalAttributeIds) {
        this.taxonomyData._metadata.historicalAttributeIds = [];
      }
      this.taxonomyData._metadata.historicalAttributeIds.push(newId);

      this.gatherAllFilters();
      this.showSnackbar(`Created new attribute #${newId}`, 'success');
      return this.attributeData[newId];
    },
    updateItemValues(item) {
      // After drag-drop, update the sort indexes to match new order
      item.valuesArray.forEach((vo, idx) => {
        // Find which key in the values object corresponds to this value object by matching value_en
        // instead of using direct object reference comparison
        const id = Object.keys(item.values).find(k =>
          item.values[k].value_en === vo.value_en
        );

        // Update the sort index to reflect new position
        if (id) {
          item.values[id].value_sort_index = idx;
          // Make sure we mark that we have unsaved changes
          this.hasUnsavedChanges = true;
        }
      });
    },
    openTextEditDialog(attributeObj, fieldName) {
      this.textEditDialog.attributeObj = attributeObj;
      this.textEditDialog.fieldName = fieldName;
      if (fieldName === 'display_name') {
        this.textEditDialog.tempValue = attributeObj.display_name?.en || '';
      } else {
        this.textEditDialog.tempValue = attributeObj[fieldName] || '';
      }
      this.textEditDialog.show = true;
    },
    saveTextEdit() {
      if (!this.textEditDialog.attributeObj || !this.textEditDialog.fieldName) {
        this.textEditDialog.show = false;
        return;
      }
      const field = this.textEditDialog.fieldName;
      const val = this.textEditDialog.tempValue;
      const attrId = this.textEditDialog.attributeObj.attribute_id;
      if (field === 'display_name') {
        let displayName = this.attributeData[attrId].display_name;
        if (typeof displayName === 'string') {
          // Convert string to object with 'en' key
          this.attributeData[attrId].display_name = { en: displayName };
        } else if (!displayName) {
          // Initialize as object if undefined or null
          this.attributeData[attrId].display_name = { en: '' };
        }
        // Now safe to set the 'en' property
        this.attributeData[attrId].display_name.en = val;
      } else {
        this.attributeData[attrId][field] = val;
      }
      this.textEditDialog.show = false;
    },
    // NEW: Only show first 7 values unless expanded
    shouldDisplayValue(item, index) {
      return this.expandedAttributeValues[item.attribute_id] || index < 7;
    },
    // NEW: Toggle expand/collapse for an attribute’s values
    toggleExpandValues(item) {
      const attrId = item.attribute_id;
      this.expandedAttributeValues[attrId] = !this.expandedAttributeValues[attrId];
    },
    // NEW: Toggle full screen mode for Attribute Editor
    toggleAttributeEditorFullscreen() {
      this.isAttributeEditorFullscreen = !this.isAttributeEditorFullscreen;
    },
    deleteAttribute(item) {
      const attrId = item.attribute_id;

      // Remove the property using the delete operator
      delete this.attributeData[attrId];

      // Remove the attribute ID from all taxonomy nodes
      this.removeAttributeIdFromAllTopLevels(this.taxonomyData, attrId, this.keysToSkip);

      // Reload the top-level node to reflect the changes
      this.loadTopLevelNode();

      // If the currently selected node had this attribute, refresh its filter list
      if (this.selectedNodeId) {
        this.refreshSelectedNodeFilters();
      }

      // Update the list of all filters
      this.gatherAllFilters();

      // Display a success message
      this.showSnackbar(`Attribute #${attrId} deleted`, 'success');
    },
    removeAttributeIdEverywhere(obj, attrId, keysToSkip = []) {
      if (!obj || typeof obj !== 'object') return;

      // Handle arrays (like subcategories)
      if (Array.isArray(obj)) {
        for (const element of obj) {
          this.removeAttributeIdEverywhere(element, attrId, keysToSkip);
        }
        return;
      }

      // Clean up both filters and attribute_ids arrays
      if (Array.isArray(obj.filters)) {
        // FIX: A simple, type-safe string comparison is all that's needed.
        obj.filters = obj.filters.filter(id => String(id) !== String(attrId));
      }
      if (Array.isArray(obj.attribute_ids)) {
        // FIX: Same simple comparison here.
        obj.attribute_ids = obj.attribute_ids.filter(id => String(id) !== String(attrId));
      }

      // Process subcategories
      if (Array.isArray(obj.subcategories)) {
        for (const sub of obj.subcategories) {
          this.removeAttributeIdEverywhere(sub, attrId, keysToSkip);
        }
      }

      // Process other objects
      for (const [key, value] of Object.entries(obj)) {
        if (
          keysToSkip.includes(key) ||
          key === 'filters' ||
          key === 'attribute_ids' ||
          key === 'subcategories' ||
          key === 'sources' ||
          key === 'name'
        ) {
          continue;
        }
        this.removeAttributeIdEverywhere(value, attrId, keysToSkip);
      }
    },
    refreshSelectedNodeFilters() {
      console.log('🔥 REFRESH SELECTED NODE FILTERS - DATA CHECK:');
  console.log('🔥 BEFORE refresh - filterIdsByName length:', Object.keys(this.filterIdsByName).length);
      if (!this.selectedNodeId) {
        this.selectedNode = null;
        return;
      }
    
      const nodeData = this.nodeIdMap[this.selectedNodeId];
      if (!nodeData) {
        this.selectedNode = null;
        return;
      }
    
      const filterIds = Array.isArray(nodeData.filters) ? nodeData.filters : 
                       Array.isArray(nodeData.attribute_ids) ? nodeData.attribute_ids : [];
      
      const name = nodeData.name || '(untitled node)';
      const filtersDict = {};
    
      filterIds.forEach((attrId) => {
        // Handle both string and number IDs
        let attrObj = this.attributeData[attrId] || 
                     this.attributeData[String(attrId)] || 
                     this.attributeData[parseInt(attrId, 10)];
        
        if (!attrObj) {
          // FIXED: Use the same structure as onNodeClick
          filtersDict[attrId] = {
            displayName: `Unknown #${attrId}`,
            values: []
          };
        } else {
          const displayName = this.getDisplayName(attrObj);
          const values = this.getValuesArray(attrObj);
          
          // FIXED: Use the same structure as onNodeClick (attrId as key, object as value)
          filtersDict[attrId] = {
            displayName: displayName,
            values: values.map(vo => vo.value_en)
          };
        }
      });
    
      // FIXED: Match the structure used in onNodeClick
      this.selectedNode = { 
        name, 
        filters: filtersDict 
      };
      
      this.editableFilters = JSON.parse(JSON.stringify(filtersDict));
      
      // FIXED: editableFilterNames should map attrId -> displayName
      this.editableFilterNames = Object.fromEntries(
        Object.entries(filtersDict).map(([attrId, data]) => [attrId, data.displayName])
      );
      
      this.expandedFilters = this.expandedFilters.filter(f => !!filtersDict[f]);
      console.log('🔥 AFTER refresh - filterIdsByName length:', Object.keys(this.filterIdsByName).length);
    },

    exportAttributesToCSV() {
      const rows = this.filteredAttributes;
      const escapeCSV = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const cleanDataCell = v => {
        // This function removed any value thats just a string of semicolons (due to missing values)
        if (v && v.replace(/;/g, "").length === 0) {
          return "";
        } else {
          return v;
        }
      }
      const headers = [
        'attribute_id', 'name', 'display_name_en', 'display_name_de', 'display_name_fr', 'display_name_it',
        'description', 'values', 'attribute_group', 'data_type',
        'always_required', 'is_image_attribute', 'is_closed_list', 'filter_attribute',
        'critical_attribute', 'secondary_allowed', 'customer_facing', 'notes',
        'sort_order', 'attribute_level', 'attribute_unit', 'categories',
        'filter_sources', 'value_ids', 'value_sort_index', 'value_sources',
        'value_notes', 'value_translations'
      ];

      let csv = headers.map(escapeCSV).join(',') + '\n';

      rows.forEach(item => {
        const sortedValues = Object.entries(item.values).toSorted((a, b) => {
          return a[1].value_sort_index - b[1].value_sort_index;
        })

        csv += [
          item.attribute_id,
          item.name,
          item.display_name.en,
          item.display_name.de,
          item.display_name.fr,
          item.display_name.it,
          item.description,
          (this.getValuesArray(item) || [])                   // ← fix
            .map(v => v.value_en).join(';'),
          item.attribute_group,
          item.data_type,
          item.always_required ? 'true' : 'false',
          item.is_image_attribute ? 'true' : 'false',
          item.is_closed_list ? 'true' : 'false',
          item.filter_attribute ? 'true' : 'false',
          item.critical_attribute ? 'true' : 'false',
          item.secondary_allowed ? 'true' : 'false',
          item.customer_facing ? 'true' : 'false',
          item.notes,
          item.sort_order,
          item.attribute_level,
          item.attribute_unit,
          (item.categories || []).join(';'),
          (item.filter_sources || []).join(';'),
          (sortedValues.map(([key]) => key) || []).join(';'),
          (sortedValues.map(([_, value]) => value.value_sort_index) || []).join(';'),
          (sortedValues.map(([_, value]) => value.sources) || []).join(';'),
          (sortedValues.map(([_, value]) => value.notes) || []).join(';'),
          (sortedValues.map(([_, value]) => JSON.stringify({
            value_en: value.value_en,
            value_de: value.value_de,
            value_fr: value.value_fr,
            value_it: value.value_it,
          }))).join(';'),
        ].map(cleanDataCell).map(escapeCSV).join(',') + '\n';
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'attribute_editor_export.csv';
      a.click();
      URL.revokeObjectURL(url);
      this.showSnackbar('CSV exported successfully!', 'success');
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
      setTimeout(() => {
        this.snackbar.show = false;
      }, 2000);
    },
    goToAttributeEditorByName(name) {
      this.searchQuery = name;
      this.currentView = 'attributeEditor';
    },
    deleteAttributesByName(name) {
      const toDelete = [];
      for (const [idStr, attrObj] of Object.entries(this.attributeData)) {
        if ((attrObj.name || '(unnamed)') === name) {
          toDelete.push(parseInt(idStr, 10));
        }
      }
      for (const attrId of toDelete) {
        if (this.attributeData[attrId]) {
          this.deleteAttribute({ attribute_id: attrId });
        }
      }
    },
    onFilterDragStart(event, attrId) {
      event.dataTransfer.setData('filterId', attrId);
      this.isDragging = true;
    },
onFilterDrop(event, node) {
  const filterId = event.dataTransfer.getData('filterId');
  if (!filterId) return;

  const nodeData = this.nodeIdMap[node.id];
  if (!nodeData) return;

  if (!Array.isArray(nodeData.filters)) nodeData.filters = [];
  if (!Array.isArray(nodeData.attribute_ids)) nodeData.attribute_ids = [];

  // --- START OF THE FIX ---
  // Always work with strings for comparison and mutation
  const stringFilterId = String(filterId);

  // Check for duplicates using the string ID
  if (nodeData.filters.includes(stringFilterId)) {
  // --- END OF THE FIX ---
    this.showSnackbar('Filter already exists on this node', 'warning');
  } else {
    // --- START OF THE FIX ---
    // Push the STRING version of the ID to both arrays
    nodeData.filters.push(stringFilterId);
    nodeData.attribute_ids.push(stringFilterId);
    // --- END OF THE FIX ---

    const attrObj = this.getAttributeById(stringFilterId);
    if (attrObj) {
      if (!attrObj.categories) attrObj.categories = [];
      const nodePath = this.getNodePathById(node.id);
      if (nodePath && !attrObj.categories.includes(nodePath)) {
        attrObj.categories.push(nodePath);
      }
    }
    if (this.selectedNodeId === node.id) {
      this.refreshSelectedNodeFilters();
    }
    this.showSnackbar('Filter added to node', 'success');
  }
},
    applyExpansionState(node) {
      // Add console logging to debug
      if (this.expandedNodes.size === 0) {
        // If we have an empty set, default everything to collapsed if collapsable is true
        node.expand = this.collapsable ? false : true;
      } else {
        node.expand = this.expandedNodes.has(node.id);
      }
      if (node.children && node.children.length) {
        node.children = node.children.map((c) => this.applyExpansionState(c));
      }
      return node;
    },
    collectExpansions(node) {
      if (node.expand) {
        this.expandedNodes.add(node.id);
      }
      if (node.children) {
        node.children.forEach((child) => this.collectExpansions(child));
      }
    },
    openTranslationsDialog(item) {
      const attrId = item.attribute_id;
      const attr = this.attributeData[attrId];
      if (!attr) return;

      // Deep clone the data using JSON methods
      this.translationsDialog.item = JSON.parse(JSON.stringify({
        attribute_id: attrId,
        display_name: {
          en: this.getDisplayName(attr),
          de: attr.display_name?.de || '',
          fr: attr.display_name?.fr || '',
          it: attr.display_name?.it || ''
        },
        valuesArray: this.getValuesArray(attr)
      }));
      this.translationsDialog.show = true;
    },
    closeTranslationsDialog() {
      if (this.translationsDialog.item) {
        const attrId = this.translationsDialog.item.attribute_id;
        const attr = this.attributeData[attrId];
        if (attr) {
          // Save display_name translations
          attr.display_name = { ...this.translationsDialog.item.display_name };

          // Save value translations to BOTH valuesArray AND the underlying values object
          this.translationsDialog.item.valuesArray.forEach((vo, index) => {
            const originalValue = attr.valuesArray[index];
            if (originalValue) {
              // Update valuesArray entry
              originalValue.value_de = vo.value_de;
              originalValue.value_fr = vo.value_fr;
              originalValue.value_it = vo.value_it;

              // Most importantly, update the actual values object that gets persisted
              // Find the matching key in the values object
              const valueKey = Object.keys(attr.values).find(k =>
                attr.values[k].value_en === originalValue.value_en
              );

              if (valueKey) {
                attr.values[valueKey].value_de = vo.value_de;
                attr.values[valueKey].value_fr = vo.value_fr;
                attr.values[valueKey].value_it = vo.value_it;
              }
            }
          });

          // Flag that we have unsaved changes that need to be saved
          this.hasUnsavedChanges = true;

          // Show a success message
          this.showSnackbar('Translations updated! Remember to save changes.', 'success');
        }
      }
      this.translationsDialog.show = false;
    },

    onSizeMapperDataChange(newData) {
      this.sizeMapperData = newData;
    }
  },
   watch: {
    filterIdsByName: {
      handler(newVal, oldVal) {
        console.log('🔥 filterIdsByName CHANGED!');
        console.log('🔥 Old length:', Object.keys(oldVal || {}).length);
        console.log('🔥 New length:', Object.keys(newVal || {}).length);
        console.log('🔥 Stack trace:', new Error().stack);
      },
      deep: true
    },
    
    allFilterNames: {
      handler(newVal, oldVal) {
        console.log('🔥 allFilterNames CHANGED!');
        console.log('🔥 Old length:', (oldVal || []).length);
        console.log('🔥 New length:', (newVal || []).length);
      }
    },
    // Watch for changes in the active view (sidebar navigation)
    // Note: Unsaved changes check is handled in handleViewChangeRequest() BEFORE the view changes
    // This watcher handles data reloading and view-specific initialization AFTER the change
currentView(newView, oldView) {
  if (newView === oldView) return;
  
  const oldGroup = this.getViewGroup(oldView);
  const newGroup = this.getViewGroup(newView);
  const isSwitchingGroups = oldGroup !== newGroup;
  
  console.log(`[View Switch Watcher] ${oldView} (${oldGroup}) → ${newView} (${newGroup}), switching groups: ${isSwitchingGroups}`);
  
  // Update session with new active section for multi-user awareness
  this.registerSession();
  
  // V2 SUPPORT: Show shared attributes warning when entering attribute views
  if (this.isEditingSharedAttributes() && !this.sharedAttributesWarningDismissed && this.v2EndpointsEnabled) {
    this.showSharedAttributesWarning = true;
  } else {
    this.showSharedAttributesWarning = false;
  }

  // --- Save Context Before Switching ---
  if (oldView === 'backendTaxonomyEditor' || ['attributeEditor', 'attributeOverview', 'languageVariants', 'gpcMapping'].includes(oldView)) {
    this.selectedBackendTaxonomy = this.selectedTaxonomy;
    console.log(`Saved backend taxonomy: ${this.selectedBackendTaxonomy}`);
  }

  // --- Determine target version ---
  const currentVersion = this.loadedVersion;
  const isSpecialVersion = ['Working Version', 'QA Version', 'Production'].includes(currentVersion);
  const targetVersion = isSpecialVersion ? currentVersion : 'Working Version';
  
  console.log(`[View Switch] Target version: ${targetVersion}`);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2: Handle taxonomy file switching (frontend vs backend)
  // ═══════════════════════════════════════════════════════════════════════════
  if (newView === 'frontendTaxonomyEditor') {
    const linkedFrontend = this.getLinkedFrontendFilename() + '.json';
    
    if (this.selectedTaxonomy !== linkedFrontend) {
      console.log(`[View Switch] Switching to linked frontend: ${linkedFrontend}`);
      this._pendingVersionForTaxonomySwitch = targetVersion;
      this.selectedTaxonomy = linkedFrontend;
      return; // selectedTaxonomy watcher will handle loading
    }
  } else if (['backendTaxonomyEditor', 'attributeEditor', 'attributeOverview', 'languageVariants', 'gpcMapping'].includes(newView)) {
    const targetBackend = this.selectedBackendTaxonomy || 'simplified_taxonomy_deduplicated.json';
    
    if (this.selectedTaxonomy !== targetBackend || this.selectedTaxonomy?.startsWith('frontend')) {
      console.log(`[View Switch] Switching to backend: ${targetBackend}`);
      this._pendingVersionForTaxonomySwitch = targetVersion;
      this.selectedTaxonomy = targetBackend;
      return; // selectedTaxonomy watcher will handle loading
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3: Reload data for the NEW view group (when switching groups)
  // This ensures we have latest data and fresh timestamps for conflict detection
  // ═══════════════════════════════════════════════════════════════════════════
  if (isSwitchingGroups) {
    this.reloadDataForViewGroup(newGroup, targetVersion);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 4: View-specific initialization
  // ═══════════════════════════════════════════════════════════════════════════
  if (newView === 'gpcMapping') {
    if (this.selectedTaxonomy && this.loadedVersion && !this.selectedTaxonomy?.startsWith('frontend')) {
      this.initializeGpcMapping();
    }
  }
  
  // Load style rules when switching to style rules view
  if (newView === 'styleRules') {
    this.loadStyleRules();
    this.loadGPCBricksAndBrands();
  }
},

    // Watch for changes in the selected taxonomy filename (e.g., 'main.json', 'frontend.json')
    selectedTaxonomy: {
      handler(newTaxonomy, oldTaxonomy) {
        if (!newTaxonomy || newTaxonomy === oldTaxonomy) {
          return;
        }
        
        // Check for unsaved changes before switching taxonomies
        if (this.hasUnsavedChanges && !this._isInternalLoad && oldTaxonomy) {
          const confirmed = confirm(
            'You have unsaved changes that will be lost when switching taxonomies. Continue?'
          );
          if (!confirmed) {
            // Revert to the previous taxonomy
            this.$nextTick(() => {
              this._isInternalLoad = true;
              this.selectedTaxonomy = oldTaxonomy;
              this.$nextTick(() => {
                this._isInternalLoad = false;
              });
            });
            return;
          }
        }
        
        console.log(`[WATCHER] selectedTaxonomy changed to '${newTaxonomy}'. Initiating load.`);
  
        // 1. Reset version state for the new taxonomy.
        this.versions = [];
        this.loadVersion = null;
        this.loadedVersion = null;
  
        // Check if there's a pending version from view switching
        const pendingVersion = this._pendingVersionForTaxonomySwitch;
        this._pendingVersionForTaxonomySwitch = null; // Clear it
  
        // 2. Load available versions for the new file.
        this.loadTaxonomyVersions().then(versions => {
          // 3. Determine the version to load
          // Use pending version if set (from view switching), otherwise default to Working Version
          let targetVersion;
          if (pendingVersion && versions.includes(pendingVersion)) {
            targetVersion = pendingVersion;
            console.log(`[WATCHER] Using pending version from view switch: ${targetVersion}`);
          } else {
            targetVersion = versions.find(v => v === 'Working Version') ||
                            versions.find(v => v.toLowerCase() === 'production') ||
                            versions[0];
          }
          
          // 4. Trigger the actual load from the cloud.
          this.loadTaxonomyFromCloud(targetVersion).then(() => {
            // 5. Reload style rules (they're taxonomy-specific)
            this.loadStyleRules();
            this.loadGpcMappings(
              newTaxonomy.replace('.json', ''),
              targetVersion
            );
          });
        }).catch(error => {
          // This case handles when a file like `frontend.json` doesn't exist yet.
          console.warn(`Could not load versions for ${newTaxonomy}, it may be new. Attempting to initialize...`);
          // `loadTaxonomyFromCloud` will see the 404 and correctly call `initializeEmptyTaxonomy`.
          const fallbackVersion = pendingVersion || 'Working Version';
          this.loadTaxonomyFromCloud(fallbackVersion);
        });
      }
    },
    selectedTopLevelNode(newNode, oldNode) {
      if (newNode !== oldNode && this.currentView === 'backendTaxonomyEditor') {
        this.loadTopLevelNode();
      }
    },
    
    // NOTE: taxonomyData, attributeData, frontendRules watchers are defined later in this file
    // They handle unsaved change tracking and additional logic like backend category generation

    // Watch for the actual loaded version to react to changes, including automatic loads
    loadedVersion(newVersion, oldVersion) {
      if (newVersion && newVersion !== oldVersion) {
          console.log(`Taxonomy version loaded: '${newVersion}'`);
          
          // If we are currently in the GPC Mapping view, re-initialize GPC data
          // NOTE: We do this even during _isInternalLoad because GPC mappings need to match the loaded version
          if (this.currentView === 'gpcMapping') {
              this.initializeGpcMapping();
          }
          
          // Skip context saving during internal loads to prevent watcher loops
          if (this._isInternalLoad) {
              console.log(`loadedVersion watcher: skipping context save due to _isInternalLoad flag`);
              return;
          }
          
          // If it's a backend taxonomy, save it as the last active backend context
          if (this.selectedTaxonomy !== 'frontend.json') {
              this.selectedBackendTaxonomy = this.selectedTaxonomy;
              this.loadedBackendVersion = newVersion;
              console.log(`Updated last backend context to: ${this.selectedBackendTaxonomy}, ${this.loadedBackendVersion}`);
          }
      }
    },


    // Watch for changes that indicate unsaved work
    hasUnsavedChanges(newValue) {
      // This watcher logic seems robust enough for its purpose, keep as is.
      if (newValue &&
          this.trackChanges &&
          this.loadedVersion &&
          this.loadVersion !== this.loadedVersion) {

        console.log('Real changes detected, syncing dropdown to loaded version:', this.loadedVersion);

        let displayVersion = this.loadedVersion;
        if (this.loadedVersion === 'production') displayVersion = 'Production';
        if (this.loadedVersion === 'test') displayVersion = 'Test';

        if (this.loadedVersion.includes('Backup ')) {
          for (const [displayVer, actualVer] of Object.entries(this._versionDisplayMap || {})) {
            if (actualVer === this.loadedVersion) {
              displayVersion = displayVer;
              break;
            }
          }
        }
        this.loadVersion = displayVersion;
      }
    },

    // Watch for taxonomy data changes (for auto-generating backend categories)
    // NOTE: This is the ONLY taxonomyData watcher - it handles both unsaved tracking AND backend category generation
    taxonomyData: {
      deep: true,
      handler(newData) {
        if (this.trackChanges && this.loadedVersion !== null) {
          this.hasUnsavedChanges = true;
          // Only mark taxonomy as changed if we're in a taxonomy-editing view
          // (not when in attribute editor where changes should only affect attributes)
          if (this.currentView === 'backendTaxonomyEditor' || this.currentView === 'frontendTaxonomyEditor') {
            this.unsavedChanges.taxonomy = true;
          }

          // Regenerate backend categories when taxonomy structure changes IF we are editing a backend taxonomy
          if (this.selectedTaxonomy !== 'frontend.json') { // This implicitly means it's a backend taxonomy
            clearTimeout(this.taxonomyChangeTimeout);
            this.taxonomyChangeTimeout = setTimeout(() => {
              if (Object.keys(newData).length > 0) {
                // Pass parameters explicitly
                this.generateBackendCategoriesFromTaxonomy(this.selectedTaxonomy.replace('.json', ''), this.loadedVersion);
              }
            }, 1000); // Wait 1 second after last change
          }
        }
      }
    },

    // Watch for attribute data changes
    // NOTE: This is the ONLY attributeData watcher for unsaved tracking
    attributeData: {
      deep: true,
      handler() {
        if (this.trackChanges && this.loadedVersion !== null) {
          this.hasUnsavedChanges = true;
          this.unsavedChanges.attributes = true;
        }
      }
    },

    // Watch for frontend rules changes
    frontendRules: {
      deep: true,
      handler() {
        if (this.trackChanges && this.currentView === 'frontendTaxonomyEditor') {
          this.hasUnsavedChanges = true;
          this.unsavedChanges.frontend = true;
        }
      }
    },

    // Watch for GPC mappings changes
    gpcMappings: {
      deep: true,
      handler() {
        if (this.trackChanges && this.loadedVersion !== null) {
          this.hasUnsavedChanges = true;
          this.unsavedChanges.gpcMappings = true;
          this.updateBrickMappedCategories();
        }
      }
    },

    // Watch for backend categories changes
    backendCategoriesData: {
      handler(newCategories) {
        if (newCategories && newCategories.length > 0) {
          console.log(`Backend categories updated: ${newCategories.length} categories available`);
          // Force computed property to recalculate
          this.$nextTick(() => {
            console.log('🔄 Forcing topLevelNodesWithBrickIds recalculation...');
            // Access the computed property to force recalculation
            const nodes = this.topLevelNodesWithBrickIds;
            console.log('📊 Forced recalc result:', nodes.length, 'nodes');
          });
        }
      }
    },

    // NEW: Watch for topLevelNodes changes and load GPC data
    topLevelNodes: {
      handler(newNodes, oldNodes) {
        console.log('🔍 topLevelNodes watcher fired:', {
          newNodesLength: newNodes?.length || 0,
          oldNodesLength: oldNodes?.length || 0,
          gpcBricksDataLength: this.gpcBricksData.length,
          gpcDataLoading: this.gpcDataLoading
        });
        console.log('📋 New nodes:', newNodes);
        
        // If we have nodes but no GPC bricks data, load it
        if (newNodes && newNodes.length > 0 && this.gpcBricksData.length === 0 && !this.gpcDataLoading) {
          console.log('🔄 Auto-loading GPC bricks data for brick ID mapping...');
          this.ensureGpcBricksLoaded();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    // Generate unique session ID for multi-user tracking
    this.sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    console.log('[TaxonomyEditor] Session ID:', this.sessionId);
    
    // On mount, load the list of available taxonomies.
    // The `selectedTaxonomy` watcher will handle loading the default taxonomy
    // for the initial `currentView` ('backendTaxonomyEditor').
    this.loadTaxonomyList();
    this.loadPlaceholderWebCategoryCodes();
    
    // V2 SUPPORT: Check system health and load taxonomy families
    await this.checkSystemHealth();
    if (this.v2EndpointsEnabled) {
      await this.loadTaxonomyFamilies();
    }
    
    // Load frontend rules in background if starting in backend view
    if (this.currentView !== 'frontendTaxonomyEditor') {
      this.loadFrontendRulesInBackground();
    }

    // Make debug toggle accessible from browser console
    window.toggleTaxonomyDebug = this.toggleDebugMode;
    console.log('[TaxonomyEditor] Debug: Run "toggleTaxonomyDebug()" in console to show/hide debug buttons');
    console.log('[TaxonomyEditor] V2 endpoints enabled:', this.v2EndpointsEnabled);
    
    // Register page unload handler to clean up session
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  
  // Clean up timeouts and session when component is destroyed
  beforeUnmount() {
    if (this.taxonomyChangeTimeout) {
      clearTimeout(this.taxonomyChangeTimeout);
    }
    // Clean up session on unmount
    this.removeSession();
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }
};